// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * OperationStarted
 *
 * Response of a streaming start endpoint (202).
 */
export const zOperationStarted = z.object({
    operation_id: z.string(),
    session_uuid: z.string().nullish().default(null),
    task_uuid: z.string().nullish().default(null)
});

/**
 * AskUserData
 */
export const zAskUserData = z.object({
    options: z.unknown().optional().default(null),
    question: z.string().optional().default(''),
    questions: z.array(z.unknown()).optional()
});

/**
 * AskUserEvent
 */
export const zAskUserEvent = z.object({
    data: zAskUserData,
    event: z.literal('ask_user')
});

/**
 * AttachmentStateEvent
 *
 * Best-effort live notice of a file's new state on its chat's progress channel (``progress.py``).
 *
 * Additive and lossy: clients keep the highest ``state_version`` per file and recover through the status
 * endpoint, which stays authoritative.
 */
export const zAttachmentStateEvent = z.object({
    attempt: z.int().nullish().default(null),
    fileId: z.string(),
    lifecycle: z.enum([
        'uploading',
        'pending_start',
        'queued',
        'preparing',
        'ready',
        'failed',
        'rejected',
        'cancelled',
        'legacy_unverified'
    ]),
    reasonCode: z.string().nullish().default(null),
    stateVersion: z.int(),
    type: z.literal('attachment_state').optional().default('attachment_state')
});

/**
 * ChatTitleUpdatedData
 */
export const zChatTitleUpdatedData = z.object({
    session_uuid: z.string(),
    title: z.string()
});

/**
 * ChatTitleUpdatedEvent
 */
export const zChatTitleUpdatedEvent = z.object({
    data: zChatTitleUpdatedData,
    event: z.literal('chat_title_updated')
});

/**
 * ContentDeltaData
 */
export const zContentDeltaData = z.object({
    delta: z.string()
});

/**
 * ContentDeltaEvent
 */
export const zContentDeltaEvent = z.object({
    data: zContentDeltaData,
    event: z.literal('content_delta')
});

/**
 * ContextInfoData
 */
export const zContextInfoData = z.object({
    budget: z.int().nullish().default(null),
    context_window: z.int().nullish().default(null),
    groups_pruned: z.int().nullish().default(null),
    groups_total: z.int().nullish().default(null),
    groups_trimmed: z.int().nullish().default(null),
    iteration: z.int().nullish().default(null),
    max_iterations: z.int().nullish().default(null),
    max_output_tokens: z.int().nullish().default(null),
    messages_after_trim: z.int().nullish().default(null),
    messages_loaded: z.int().nullish().default(null),
    model: z.string().nullish().default(null),
    percent_used: z.union([
        z.int(),
        z.number()
    ]).nullish().default(null),
    prompt_tokens: z.int().nullish().default(null),
    retry_after_seconds: z.number().nullish().default(null),
    status: z.literal('waiting_for_capacity').nullish().default(null),
    summarized: z.boolean().nullish().default(null),
    tool_tokens: z.int().nullish().default(null)
});

/**
 * ContextInfoEvent
 */
export const zContextInfoEvent = z.object({
    data: zContextInfoData,
    event: z.literal('context_info')
});

/**
 * DraftUpdatedData
 *
 * Build with AI (MHS-733): the model proposed or revised an entity draft; the UI shows it as a card.
 *
 * Not terminal. Clients that don't render cards ignore it (and aren't offered the tool that emits it).
 */
export const zDraftUpdatedData = z.object({
    draft: z.record(z.string(), z.unknown()).optional(),
    draft_id: z.string(),
    entity_type: z.string(),
    missing_fields: z.array(z.string()).optional(),
    questions: z.array(z.unknown()).optional(),
    target_uuid: z.string().nullish().default(null)
});

/**
 * DraftUpdatedEvent
 */
export const zDraftUpdatedEvent = z.object({
    data: zDraftUpdatedData,
    event: z.literal('draft_updated')
});

/**
 * FlowEndData
 */
export const zFlowEndData = z.object({
    completed_at: z.string().nullish().default(null),
    flow_run_uuid: z.string().nullish().default(null),
    output: z.string().nullish().default(null),
    status: z.string().nullish().default(null),
    total_tokens: z.int().nullish().default(null)
});

/**
 * FlowEndEvent
 */
export const zFlowEndEvent = z.object({
    data: zFlowEndData,
    event: z.literal('flow_end')
});

/**
 * FlowFailedData
 */
export const zFlowFailedData = z.object({
    completed_at: z.string().nullish().default(null),
    error: z.string().nullish().default(null),
    total_tokens: z.int().nullish().default(null)
});

/**
 * FlowFailedEvent
 */
export const zFlowFailedEvent = z.object({
    data: zFlowFailedData,
    event: z.literal('flow_failed')
});

/**
 * FlowStartData
 */
export const zFlowStartData = z.object({
    flow_name: z.string().nullish().default(null),
    run_uuid: z.string().nullish().default(null),
    started_at: z.string().nullish().default(null),
    total_steps: z.int().nullish().default(null)
});

/**
 * FlowStartEvent
 */
export const zFlowStartEvent = z.object({
    data: zFlowStartData,
    event: z.literal('flow_start')
});

/**
 * HeartbeatData
 */
export const zHeartbeatData = z.object({
    status: z.string().nullish().default(null)
});

/**
 * HeartbeatEvent
 */
export const zHeartbeatEvent = z.object({
    data: zHeartbeatData,
    event: z.literal('heartbeat')
});

/**
 * MessageEndData
 */
export const zMessageEndData = z.object({
    cancelled: z.boolean().nullish().default(null),
    costUsd: z.number().nullish().default(null),
    max_iterations: z.int().nullish().default(null),
    stop_reason: z.enum(['max_iterations', 'rate_limited']).nullish().default(null),
    tokensUsed: z.int().nullish().default(null)
});

/**
 * MessageEndEvent
 */
export const zMessageEndEvent = z.object({
    data: zMessageEndData,
    event: z.literal('message_end')
});

/**
 * MessageStartData
 */
export const zMessageStartData = z.record(z.string(), z.unknown());

/**
 * MessageStartEvent
 */
export const zMessageStartEvent = z.object({
    data: zMessageStartData,
    event: z.literal('message_start')
});

/**
 * MicroAppBindingData
 *
 * Live data for a single dashboard binding.
 */
export const zMicroAppBindingData = z.object({
    bindingKey: z.string(),
    cached: z.boolean().optional().default(false),
    error: z.string().nullish(),
    errorCode: z.string().nullish(),
    errorSource: z.string().nullish(),
    executedAt: z.iso.datetime().nullish(),
    value: z.unknown().nullish()
});

/**
 * OperationCompletedData
 *
 * Terminal. Payload is the operation's final data (often empty).
 */
export const zOperationCompletedData = z.object({
    cancelled: z.boolean().nullish().default(null)
});

/**
 * OperationCompletedEvent
 */
export const zOperationCompletedEvent = z.object({
    data: zOperationCompletedData,
    event: z.literal('operation_completed')
});

/**
 * OperationFailedData
 *
 * Terminal.
 */
export const zOperationFailedData = z.object({
    error: z.string()
});

/**
 * OperationFailedEvent
 */
export const zOperationFailedEvent = z.object({
    data: zOperationFailedData,
    event: z.literal('operation_failed')
});

/**
 * OperationPausedData
 */
export const zOperationPausedData = z.object({
    reason: z.string().nullish().default(null)
});

/**
 * OperationPausedEvent
 */
export const zOperationPausedEvent = z.object({
    data: zOperationPausedData,
    event: z.literal('operation_paused')
});

/**
 * OperationResumedData
 */
export const zOperationResumedData = z.record(z.string(), z.unknown());

/**
 * OperationResumedEvent
 */
export const zOperationResumedEvent = z.object({
    data: zOperationResumedData,
    event: z.literal('operation_resumed')
});

/**
 * OperationStartedData
 */
export const zOperationStartedData = z.object({
    entity_id: z.string().nullish().default(null),
    operation_id: z.string(),
    operation_type: z.string(),
    title: z.string().nullish().default(null)
});

/**
 * OperationStartedEvent
 */
export const zOperationStartedEvent = z.object({
    data: zOperationStartedData,
    event: z.literal('operation_started')
});

/**
 * OperationStatusChangedData
 */
export const zOperationStatusChangedData = z.object({
    entity_id: z.string().nullish().default(null),
    operation_id: z.string(),
    operation_type: z.string(),
    status: z.string(),
    title: z.string()
});

/**
 * OperationStatusChangedEvent
 */
export const zOperationStatusChangedEvent = z.object({
    data: zOperationStatusChangedData,
    event: z.literal('operation_status_changed')
});

/**
 * ResearchCountsData
 */
export const zResearchCountsData = z.object({
    pages: z.int().optional().default(0),
    queries: z.int().optional().default(0),
    sources: z.int().optional().default(0)
});

/**
 * ResearchLatestData
 */
export const zResearchLatestData = z.object({
    kind: z.enum([
        'query',
        'source',
        'thought'
    ]).optional().default('query'),
    text: z.string().optional().default(''),
    url: z.string().nullish().default(null)
});

/**
 * ResearchPageData
 */
export const zResearchPageData = z.object({
    title: z.string().optional().default(''),
    url: z.string().nullish().default(null)
});

/**
 * ResearchTopicData
 */
export const zResearchTopicData = z.object({
    id: z.string(),
    status: z.enum([
        'pending',
        'active',
        'done',
        'skipped'
    ]).optional().default('pending'),
    title: z.string()
});

/**
 * ResearchProgressData
 *
 * Progress of a running research tool call (MHS-796/797/798). Not terminal; coarse (~20-80 per run).
 */
export const zResearchProgressData = z.object({
    counts: zResearchCountsData.optional(),
    latest: zResearchLatestData.nullish().default(null),
    pages: z.array(zResearchPageData).optional(),
    round: z.int().optional().default(1),
    searches: z.array(z.string()).optional(),
    stage: z.enum([
        'planning',
        'gathering',
        'reading',
        'analyzing',
        'writing'
    ]).optional().default('planning'),
    tool_call_id: z.string(),
    topics: z.array(zResearchTopicData).optional()
});

/**
 * ResearchProgressEvent
 */
export const zResearchProgressEvent = z.object({
    data: zResearchProgressData,
    event: z.literal('research_progress')
});

/**
 * RouteDecisionData
 *
 * Agents only: the routing decision for a first message (shape owned by agent routing).
 */
export const zRouteDecisionData = z.object({
    sessionUuid: z.string().nullish().default(null)
});

/**
 * RouteDecisionEvent
 */
export const zRouteDecisionEvent = z.object({
    data: zRouteDecisionData,
    event: z.literal('route_decision')
});

/**
 * StepContentData
 */
export const zStepContentData = z.object({
    content: z.string().optional().default(''),
    step_number: z.int().nullish().default(null)
});

/**
 * StepContentEvent
 */
export const zStepContentEvent = z.object({
    data: zStepContentData,
    event: z.literal('step_content')
});

/**
 * StepEndData
 */
export const zStepEndData = z.object({
    output: z.string().nullish().default(null),
    status: z.string().nullish().default(null),
    step_number: z.int().nullish().default(null),
    tokens_used: z.int().nullish().default(null)
});

/**
 * StepEndEvent
 */
export const zStepEndEvent = z.object({
    data: zStepEndData,
    event: z.literal('step_end')
});

/**
 * StepFailedData
 */
export const zStepFailedData = z.object({
    error: z.string().nullish().default(null),
    step_number: z.int().nullish().default(null)
});

/**
 * StepFailedEvent
 */
export const zStepFailedEvent = z.object({
    data: zStepFailedData,
    event: z.literal('step_failed')
});

/**
 * StepPausedData
 *
 * The run is waiting for a human review of this step; resume it with POST /flows/{uuid}/runs/{run}/resume. Or,
 * with ``action_request_uuid``, for its owner to approve a write action the step called (MHS-972): decided with
 * POST /action-requests/{uuid}/decision, which resumes it.
 */
export const zStepPausedData = z.object({
    action_request_uuid: z.string().nullish().default(null),
    message: z.string().nullish().default(null),
    run_uuid: z.string().nullish().default(null),
    step_number: z.int().nullish().default(null)
});

/**
 * StepPausedEvent
 */
export const zStepPausedEvent = z.object({
    data: zStepPausedData,
    event: z.literal('step_paused')
});

/**
 * StepStartData
 */
export const zStepStartData = z.object({
    agent_name: z.string().nullish().default(null),
    agent_uuid: z.string().nullish().default(null),
    step_number: z.int().nullish().default(null),
    step_type: z.string().nullish().default(null)
});

/**
 * StepStartEvent
 */
export const zStepStartEvent = z.object({
    data: zStepStartData,
    event: z.literal('step_start')
});

/**
 * StreamErrorData
 */
export const zStreamErrorData = z.object({
    code: z.string().nullish().default(null),
    message: z.string()
});

/**
 * StreamErrorEvent
 */
export const zStreamErrorEvent = z.object({
    data: zStreamErrorData,
    event: z.literal('error')
});

/**
 * ToolConfirmationData
 *
 * A tool that runs only after the user confirms was proposed (MHS-795). Not terminal: the turn ends normally.
 *
 * The proposal is also saved on the reply (``message_metadata.tool_confirmation``, status ``pending``), so a reload
 * shows it from the chat. Only clients that send ``client_features: ["tool_confirmation"]`` are offered such tools.
 */
export const zToolConfirmationData = z.object({
    arguments: z.record(z.string(), z.unknown()).optional(),
    confirmation_id: z.string(),
    details: z.record(z.string(), z.unknown()).nullish().default(null),
    message_id: z.string().nullish().default(null),
    tool: z.string()
});

/**
 * ToolConfirmationEvent
 */
export const zToolConfirmationEvent = z.object({
    data: zToolConfirmationData,
    event: z.literal('tool_confirmation')
});

/**
 * ToolEndData
 */
export const zToolEndData = z.object({
    is_error: z.boolean().nullish().default(null),
    metadata: z.record(z.string(), z.unknown()).nullish().default(null),
    name: z.string(),
    tool_call_id: z.string()
});

/**
 * ToolEndEvent
 */
export const zToolEndEvent = z.object({
    data: zToolEndData,
    event: z.literal('tool_end')
});

/**
 * ToolStartData
 */
export const zToolStartData = z.object({
    arguments: z.unknown().optional().default(null),
    name: z.string(),
    tool_call_id: z.string()
});

/**
 * ToolStartEvent
 */
export const zToolStartEvent = z.object({
    data: zToolStartData,
    event: z.literal('tool_start')
});

/**
 * One SSE frame from an operation stream: `event:` and parsed `data:`.
 */
export const zOperationStreamEvent = z.discriminatedUnion('event', [
    zOperationStartedEvent,
    zOperationCompletedEvent,
    zOperationFailedEvent,
    zOperationPausedEvent,
    zOperationResumedEvent,
    zMessageStartEvent,
    zContentDeltaEvent,
    zToolStartEvent,
    zToolEndEvent,
    zAskUserEvent,
    zContextInfoEvent,
    zRouteDecisionEvent,
    zDraftUpdatedEvent,
    zToolConfirmationEvent,
    zResearchProgressEvent,
    zMessageEndEvent,
    zStreamErrorEvent,
    zFlowStartEvent,
    zStepStartEvent,
    zStepContentEvent,
    zStepEndEvent,
    zStepFailedEvent,
    zStepPausedEvent,
    zFlowEndEvent,
    zFlowFailedEvent
]);

/**
 * UnreadCountData
 */
export const zUnreadCountData = z.object({
    count: z.int()
});

/**
 * UnreadCountEvent
 */
export const zUnreadCountEvent = z.object({
    data: zUnreadCountData,
    event: z.literal('unread_count')
});

/**
 * NotificationActor
 *
 * Who (or what) caused the notification.
 */
export const zNotificationActor = z.object({
    avatarUrl: z.string().nullish(),
    kind: z.enum([
        'user',
        'agent',
        'system'
    ]),
    name: z.string(),
    uuid: z.string().nullish()
});

/**
 * NotificationSubject
 *
 * The thing the notification is about (a chat, a workflow run, a data source...).
 */
export const zNotificationSubject = z.object({
    kind: z.string(),
    name: z.string(),
    uuid: z.string().nullish()
});

/**
 * NotificationPushedData
 *
 * A new notification (camelCase keys, as the REST list returns them).
 */
export const zNotificationPushedData = z.object({
    actor: zNotificationActor.nullable(),
    context: z.array(z.string()).nullable(),
    createdAt: z.iso.datetime(),
    excerpt: z.string().nullable(),
    message: z.string(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    readAt: z.iso.datetime().nullish().default(null),
    showDesktop: z.boolean().nullish().default(null),
    subject: zNotificationSubject.nullable(),
    title: z.string(),
    type: z.string(),
    uuid: z.string()
});

/**
 * NotificationPushedEvent
 */
export const zNotificationPushedEvent = z.object({
    data: zNotificationPushedData,
    event: z.literal('notification')
});

/**
 * One SSE frame from the user event stream: `event:` and parsed `data:`.
 */
export const zUserStreamEvent = z.discriminatedUnion('event', [
    zNotificationPushedEvent,
    zUnreadCountEvent,
    zOperationStatusChangedEvent,
    zChatTitleUpdatedEvent,
    zHeartbeatEvent
]);

/**
 * RoutedAgent
 */
export const zRoutedAgent = z.object({
    color: z.string(),
    icon: z.string(),
    name: z.string(),
    uuid: z.string()
});

/**
 * AgentHandoffInitiator
 *
 * Who moved a chat to another agent mid-conversation (MHS-628).
 */
export const zAgentHandoffInitiator = z.enum([
    'llm',
    'user',
    'router'
]);

/**
 * AgentHandoffEvent
 *
 * A handoff as the chat shows it: the ``handoff`` stream event, the switch endpoint's answer
 * and the divider row's ``messageMetadata.handoff``.
 */
export const zAgentHandoffEvent = z.object({
    createdAt: z.iso.datetime(),
    fromAgent: zRoutedAgent.nullish(),
    initiator: zAgentHandoffInitiator,
    reason: z.string(),
    sessionUuid: z.string(),
    toAgent: zRoutedAgent,
    uuid: z.string()
});

/**
 * HealthResponse
 */
export const zHealthResponse = z.object({
    release: z.string().optional().default(''),
    status: z.string(),
    version: z.string()
});

/**
 * BudgetStatusResponse
 *
 * Budget status for the current user.
 */
export const zBudgetStatusResponse = z.object({
    currentSpendUsd: z.number(),
    monthlyLimitUsd: z.number(),
    percentUsed: z.number(),
    resetsAt: z.string()
});

/**
 * PlatformCapabilities
 *
 * Backend-driven capability flags surfaced to the frontend.
 *
 * Single source of truth for features whose availability is gated by backend
 * configuration (not frontend build/env flags), so the UI can hide or disable
 * controls that would otherwise fail server-side.
 */
export const zPlatformCapabilities = z.object({
    agentMemoryEnabled: z.boolean().optional().default(false),
    automatedSyncEnabled: z.boolean().optional().default(false),
    azureAdminEnabled: z.boolean().optional().default(false),
    chatFilePipelineEnabled: z.boolean().optional().default(false),
    fabricEnabled: z.boolean().optional().default(false),
    googleDocsEnabled: z.boolean().optional().default(false),
    googleDriveEnabled: z.boolean().optional().default(false),
    googleSheetsEnabled: z.boolean().optional().default(false),
    googleSlidesEnabled: z.boolean().optional().default(false),
    knowledgeExtractionEnabled: z.boolean().optional().default(false),
    knowledgeGraphEnabled: z.boolean().optional().default(false),
    oauthClientManagementEnabled: z.boolean().optional().default(false),
    rallyEnabled: z.boolean().optional().default(false),
    researchEnabled: z.boolean().optional().default(false),
    sharepointEnabled: z.boolean().optional().default(false),
    transcriptionEnabled: z.boolean().optional().default(false)
});

/**
 * TeamSummary
 *
 * Summary team schema for lists.
 */
export const zTeamSummary = z.object({
    description: z.string().nullish(),
    isEveryone: z.boolean(),
    memberCount: z.int().optional().default(0),
    name: z.string(),
    uuid: z.string()
});

/**
 * TeamMembershipResponse
 *
 * Team membership with nested team info and user's role.
 */
export const zTeamMembershipResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    team: zTeamSummary
});

/**
 * UserResponse
 *
 * User response schema.
 */
export const zUserResponse = z.object({
    avatarUrl: z.string().nullish(),
    createdAt: z.iso.datetime(),
    deactivationReason: z.string().nullish(),
    email: z.string(),
    isActive: z.boolean(),
    lastLoginAt: z.iso.datetime().nullish(),
    name: z.string(),
    role: z.string(),
    settings: z.record(z.string(), z.unknown()).optional(),
    uuid: z.string()
});

/**
 * CurrentUserResponse
 *
 * Response schema for current authenticated user.
 */
export const zCurrentUserResponse = z.object({
    budget: zBudgetStatusResponse.nullish(),
    capabilities: zPlatformCapabilities,
    teams: z.array(zTeamMembershipResponse),
    user: zUserResponse
});

/**
 * DataResponse[CurrentUserResponse]
 */
export const zDataResponseCurrentUserResponse = z.object({
    data: zCurrentUserResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UserSettingsUpdate
 *
 * Schema for updating user settings.
 */
export const zUserSettingsUpdate = z.object({
    agentMemory: z.boolean().nullish(),
    customInstructions: z.string().max(2000).nullish(),
    defaultModelId: z.string().max(50).nullish(),
    groupChatBench: z.array(z.string()).nullish(),
    kgLearnFromChats: z.boolean().nullish(),
    notificationsEnabled: z.boolean().nullish(),
    responseLength: z.string().regex(/^(brief|moderate|detailed)$/).nullish(),
    responseTone: z.string().regex(/^(professional|casual|friendly|concise|direct)$/).nullish(),
    showContextInfo: z.boolean().nullish(),
    theme: z.string().regex(/^(light|dark|system)$/).nullish()
});

/**
 * DataResponse[UserResponse]
 */
export const zDataResponseUserResponse = z.object({
    data: zUserResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ValidationError
 */
export const zValidationError = z.object({
    loc: z.array(z.union([z.string(), z.int()])),
    msg: z.string(),
    type: z.string()
});

/**
 * HTTPValidationError
 */
export const zHttpValidationError = z.object({
    detail: z.array(zValidationError).optional()
});

/**
 * ReasoningEffort
 *
 * Reasoning effort levels a reasoning model may accept (MHS-669).
 *
 * The vocabulary shared by OpenAI Responses (``reasoning.effort``) and Anthropic
 * Messages (``output_config.effort``); each model's accepted subset is curated in
 * ``ai_models.reasoning_config``.
 */
export const zReasoningEffort = z.enum([
    'none',
    'minimal',
    'low',
    'medium',
    'high',
    'xhigh',
    'max'
]);

/**
 * ReasoningConfig
 *
 * Reasoning efforts a model accepts; ``default_effort`` only labels the UI's "Default" option.
 */
export const zReasoningConfig = z.object({
    defaultEffort: zReasoningEffort.nullish(),
    supportedEfforts: z.array(zReasoningEffort).min(1)
});

/**
 * AIModelSummary
 *
 * Summary AI model schema for lists.
 */
export const zAiModelSummary = z.object({
    apiProtocol: z.string().optional().default('openai_responses'),
    capabilities: z.array(z.string()).optional(),
    contextWindow: z.int(),
    costTier: z.string().nullish(),
    description: z.string().nullish(),
    displayName: z.string().nullish(),
    hosting: z.string().optional().default('azure_openai_eastus2'),
    id: z.string(),
    isDeprecated: z.boolean().optional().default(false),
    isEnabled: z.boolean(),
    isReasoning: z.boolean().optional().default(false),
    maxTokens: z.int(),
    modelType: z.string().optional().default('chat'),
    name: z.string(),
    provider: z.string(),
    reasoningConfig: zReasoningConfig.nullish(),
    replacementModelId: z.string().nullish(),
    sunsetDate: z.iso.date().nullish(),
    supportsCodeInterpreter: z.boolean().optional().default(false),
    supportsFunctionCalling: z.boolean().optional().default(true),
    supportsStreaming: z.boolean().optional().default(true),
    supportsVision: z.boolean().optional().default(false)
});

/**
 * PaginationMeta
 *
 * Pagination metadata.
 */
export const zPaginationMeta = z.object({
    page: z.int(),
    pageSize: z.int(),
    total: z.int(),
    totalPages: z.int()
});

/**
 * PaginatedResponse[AIModelSummary]
 */
export const zPaginatedResponseAiModelSummary = z.object({
    data: z.array(zAiModelSummary),
    meta: zPaginationMeta
});

/**
 * ModelTierDefaultResponse
 *
 * A single tier-to-model mapping.
 */
export const zModelTierDefaultResponse = z.object({
    description: z.string().nullish(),
    label: z.string(),
    modelId: z.string().nullish(),
    modelName: z.string().nullish(),
    tier: z.string(),
    userFacing: z.boolean().optional().default(false)
});

/**
 * ModelTierDefaultsResponse
 *
 * All tier defaults returned together.
 */
export const zModelTierDefaultsResponse = z.object({
    defaults: z.array(zModelTierDefaultResponse).optional()
});

/**
 * DataResponse[ModelTierDefaultsResponse]
 */
export const zDataResponseModelTierDefaultsResponse = z.object({
    data: zModelTierDefaultsResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ChatSessionCreate
 *
 * Schema for creating a chat session.
 */
export const zChatSessionCreate = z.object({
    agentUuid: z.string().nullish(),
    brandUuid: z.string().nullish(),
    modelId: z.string().min(1).max(50),
    projectUuid: z.string().nullish(),
    reasoningEffort: z.string().regex(/^(none|minimal|low|medium|high|xhigh|max)$/).nullish(),
    source: z.enum(['user', 'build']).optional().default('user'),
    spaceUuid: z.string().nullish(),
    teamUuid: z.string().nullish(),
    title: z.string().max(255).nullish()
});

/**
 * LinkedEntity
 *
 * An entity whose ``source_session_id`` points at the chat.
 */
export const zLinkedEntity = z.object({
    createdAt: z.iso.datetime(),
    entityType: z.enum([
        'agent',
        'data_source',
        'skill',
        'brand',
        'micro_app',
        'workflow'
    ]),
    name: z.string(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * AttachmentCapabilities
 *
 * What may be read right now. ``requiresValidation``: reads return ``preparing`` until it completes.
 */
export const zAttachmentCapabilities = z.object({
    original: z.boolean().optional().default(false),
    preview: z.boolean().optional().default(false),
    requiresValidation: z.boolean().optional().default(false),
    sandbox: z.boolean().optional().default(false),
    text: z.boolean().optional().default(false),
    visual: z.boolean().optional().default(false)
});

/**
 * AttachmentResponse
 *
 * Response schema for a chat attachment.
 */
export const zAttachmentResponse = z.object({
    capabilities: zAttachmentCapabilities.nullish(),
    changeSummary: z.string().nullish(),
    createdAt: z.iso.datetime(),
    fileVersion: z.int().nullish(),
    filename: z.string(),
    isRevision: z.boolean().optional().default(false),
    lifecycle: z.string().nullish(),
    lineageUuid: z.string().nullish(),
    mimeType: z.string(),
    origin: z.string().optional().default('upload'),
    size: z.int(),
    stateVersion: z.int().nullish(),
    uuid: z.string(),
    version: z.int().nullish()
});

/**
 * MessageResponse
 *
 * Message response schema.
 */
export const zSrcSchemasChatMessageResponse = z.object({
    attachmentCount: z.int().nullish(),
    attachments: z.array(zAttachmentResponse).optional(),
    attachmentsNextCursor: z.string().nullish(),
    content: z.string(),
    createdAt: z.iso.datetime(),
    messageMetadata: z.record(z.string(), z.unknown()).optional(),
    role: z.string(),
    senderName: z.string().nullish(),
    senderUuid: z.string().nullish(),
    tokensUsed: z.int().nullish(),
    toolCallId: z.string().nullish(),
    toolCalls: z.array(z.record(z.string(), z.unknown())).nullish(),
    uuid: z.string()
});

/**
 * ChatSessionResponse
 *
 * Full chat session response schema.
 */
export const zChatSessionResponse = z.object({
    agentName: z.string().nullish(),
    agentUuid: z.string().nullish(),
    brandName: z.string().nullish(),
    brandUuid: z.string().nullish(),
    createdAt: z.iso.datetime(),
    isStarred: z.boolean(),
    linkedEntities: z.array(zLinkedEntity).optional(),
    messageCount: z.int(),
    messages: z.array(zSrcSchemasChatMessageResponse).optional(),
    modelId: z.string(),
    projectUuid: z.string().nullish(),
    reasoningEffort: z.string().nullish(),
    source: z.string().optional().default('user'),
    spaceUuid: z.string().nullish(),
    teamUuid: z.string().nullish(),
    title: z.string().nullish(),
    updatedAt: z.iso.datetime(),
    userName: z.string().nullish(),
    userUuid: z.string(),
    uuid: z.string()
});

/**
 * DataResponse[ChatSessionResponse]
 */
export const zDataResponseChatSessionResponse = z.object({
    data: zChatSessionResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AttachmentSelectionRefs
 *
 * Bounded references that expand server-side to every file in those batches/messages of the chat.
 */
export const zAttachmentSelectionRefs = z.object({
    batchIds: z.array(z.string()).max(32).optional(),
    messageIds: z.array(z.string()).max(32).optional()
});

/**
 * ToolConfirmationDecision
 *
 * The user's decision on a tool proposal card (MHS-795). The message's content is the button's label.
 */
export const zToolConfirmationDecision = z.object({
    decision: z.enum(['run', 'decline']),
    id: z.string().min(1).max(64),
    overrides: z.record(z.string(), z.unknown()).nullish(),
    remember: z.boolean().optional().default(false)
});

/**
 * SharePointDocumentMeta
 *
 * Metadata for a SharePoint document attached to a message.
 */
export const zSharePointDocumentMeta = z.object({
    content: z.string().max(500000).nullish(),
    filename: z.string().max(500),
    mimeType: z.string().max(255).nullish(),
    size: z.int().nullish(),
    url: z.string().max(2048)
});

/**
 * MessageCreate
 *
 * Schema for creating/sending a message.
 */
export const zMessageCreate = z.object({
    attachmentRefs: zAttachmentSelectionRefs.nullish(),
    attachments: z.array(z.string()).optional(),
    clientFeatures: z.array(z.string()).max(10).nullish(),
    confirmation: zToolConfirmationDecision.nullish(),
    content: z.string().min(1).max(100000),
    continuation: z.boolean().optional().default(false),
    disableTools: z.boolean().optional().default(false),
    documentAttachments: z.array(z.string()).optional(),
    draftCards: z.boolean().optional().default(false),
    imageAttachments: z.array(z.string()).optional(),
    retryOf: z.string().max(64).nullish(),
    sharepointContent: z.string().max(500000).nullish(),
    sharepointDocuments: z.array(zSharePointDocumentMeta).nullish(),
    sharepointSlideImages: z.array(z.string()).nullish(),
    timeZone: z.string().max(64).nullish()
});

/**
 * AgentInvokeRequest
 *
 * Request to invoke an agent.
 */
export const zAgentInvokeRequest = z.object({
    clientFeatures: z.array(z.string()).max(10).nullish(),
    confirmation: zToolConfirmationDecision.nullish(),
    continuation: z.boolean().optional().default(false),
    documentAttachments: z.array(z.string()).nullish(),
    imageAttachments: z.array(z.string()).nullish(),
    maxTokens: z.int().gte(1).nullish(),
    message: z.string().min(1).max(100000),
    projectUuid: z.string().nullish(),
    reasoningEffort: z.string().regex(/^(none|minimal|low|medium|high|xhigh|max)$/).nullish(),
    retryOf: z.string().max(64).nullish(),
    routeDecisionUuid: z.string().nullish(),
    sessionUuid: z.string().nullish(),
    sharepointContent: z.string().max(500000).nullish(),
    sharepointDocuments: z.array(zSharePointDocumentMeta).nullish(),
    sharepointSlideImages: z.array(z.string()).nullish(),
    temperature: z.number().gte(0).lte(2).nullish(),
    timeZone: z.string().max(64).nullish()
});

/**
 * Body_upload_attachments_api_v1_chat_sessions__session_uuid__attachments_post
 */
export const zBodyUploadAttachmentsApiV1ChatSessionsSessionUuidAttachmentsPost = z.object({
    files: z.array(z.string())
});

/**
 * DataResponse[list[AttachmentResponse]]
 */
export const zDataResponseListAttachmentResponse = z.object({
    data: z.array(zAttachmentResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * TooManyFilesDetail
 */
export const zTooManyFilesDetail = z.object({
    count: z.int(),
    error_code: z.literal('too_many_files'),
    max: z.int()
});

/**
 * TooManyFilesError
 *
 * 400 body: more files than one upload accepts.
 */
export const zTooManyFilesError = z.object({
    detail: zTooManyFilesDetail
});

/**
 * FileTooLargeDetail
 */
export const zFileTooLargeDetail = z.object({
    error_code: z.literal('file_too_large'),
    file_kind: z.enum(['image', 'document']),
    filename: z.string().nullish(),
    max_size: z.int(),
    size: z.int()
});

/**
 * FileTooLargeError
 *
 * 413 body: a file is over its kind's size limit.
 */
export const zFileTooLargeError = z.object({
    detail: zFileTooLargeDetail
});

/**
 * UnsupportedFileTypeDetail
 */
export const zUnsupportedFileTypeDetail = z.object({
    allowed_document_types: z.array(z.string()),
    allowed_image_types: z.array(z.string()),
    error_code: z.literal('unsupported_file_type'),
    filename: z.string().nullish(),
    mime_type: z.string()
});

/**
 * UnsupportedFileTypeError
 *
 * 415 body: a file's type isn't accepted as a chat attachment.
 */
export const zUnsupportedFileTypeError = z.object({
    detail: zUnsupportedFileTypeDetail
});

/**
 * _AttachmentPage
 */
export const zAttachmentPage = z.object({
    data: z.array(zAttachmentResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    nextCursor: z.string().nullish(),
    operationId: z.string().nullish(),
    total: z.int().optional().default(0)
});

/**
 * AttachmentStatusItem
 */
export const zAttachmentStatusItem = z.object({
    attempt: z.int().nullish(),
    canCancel: z.boolean().optional().default(false),
    canRetry: z.boolean().optional().default(false),
    capabilities: zAttachmentCapabilities,
    enrichment: z.record(z.string(), z.string()).optional(),
    fileId: z.string(),
    fileVersion: z.int(),
    filename: z.string(),
    lifecycle: z.enum([
        'uploading',
        'pending_start',
        'queued',
        'preparing',
        'ready',
        'failed',
        'rejected',
        'cancelled',
        'legacy_unverified'
    ]),
    mimeType: z.string(),
    provenance: z.string().nullish(),
    reasonCode: z.string().nullish(),
    reconcileStatus: z.string().nullish(),
    retryable: z.boolean().optional().default(false),
    scanStatus: z.string().nullish(),
    size: z.int(),
    stateVersion: z.int()
});

/**
 * AttachmentStatusResponse
 */
export const zAttachmentStatusResponse = z.object({
    counts: z.record(z.string(), z.int()).optional(),
    items: z.array(zAttachmentStatusItem),
    missing: z.array(z.string()).optional(),
    nextCursor: z.string().nullish(),
    total: z.int().optional().default(0)
});

/**
 * DataResponse[AttachmentStatusResponse]
 */
export const zDataResponseAttachmentStatusResponse = z.object({
    data: zAttachmentStatusResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AttachmentKindLimits
 *
 * What one kind of attachment (images or documents) accepts.
 */
export const zAttachmentKindLimits = z.object({
    extensions: z.array(z.string()),
    maxSize: z.int(),
    mimeTypes: z.array(z.string())
});

/**
 * AttachmentLimitsResponse
 *
 * Chat attachment upload limits, so clients can check files before uploading.
 */
export const zAttachmentLimitsResponse = z.object({
    document: zAttachmentKindLimits,
    image: zAttachmentKindLimits,
    maxFiles: z.int()
});

/**
 * DocumentVersionResponse
 *
 * A single version entry within a document lineage.
 */
export const zDocumentVersionResponse = z.object({
    attachmentUuid: z.string(),
    changeSummary: z.string().nullish(),
    createdAt: z.iso.datetime(),
    filename: z.string(),
    messageUuid: z.string().nullish(),
    size: z.int(),
    version: z.int()
});

/**
 * DocumentLineageResponse
 *
 * Response for a document lineage with all its versions.
 */
export const zDocumentLineageResponse = z.object({
    canonicalFilename: z.string(),
    createdAt: z.iso.datetime(),
    currentVersion: z.int(),
    documentType: z.string(),
    updatedAt: z.iso.datetime(),
    uuid: z.string(),
    versions: z.array(zDocumentVersionResponse).optional()
});

/**
 * DataResponse[list[DocumentLineageResponse]]
 */
export const zDataResponseListDocumentLineageResponse = z.object({
    data: z.array(zDocumentLineageResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ShareLinkResponse
 *
 * Response for a share link.
 */
export const zShareLinkResponse = z.object({
    createdAt: z.iso.datetime(),
    isActive: z.boolean(),
    permission: z.string(),
    token: z.string(),
    uuid: z.string()
});

/**
 * ShareResponse
 *
 * Response for a direct user share.
 */
export const zShareResponse = z.object({
    createdAt: z.iso.datetime(),
    permission: z.string(),
    sharedBy: zUserResponse,
    sharedWith: zUserResponse,
    uuid: z.string()
});

/**
 * SessionSharingResponse
 *
 * Combined response for all shares on a session.
 */
export const zSessionSharingResponse = z.object({
    links: z.array(zShareLinkResponse).optional(),
    sharedWithSpace: z.boolean().optional().default(false),
    shares: z.array(zShareResponse).optional(),
    spaceName: z.string().nullish()
});

/**
 * DataResponse[SessionSharingResponse]
 */
export const zDataResponseSessionSharingResponse = z.object({
    data: zSessionSharingResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ShareUserRequest
 *
 * Request to share a session with a specific user.
 */
export const zShareUserRequest = z.object({
    permission: z.string().regex(/^(read|read_write)$/).optional().default('read'),
    userUuid: z.string()
});

/**
 * DataResponse[ShareResponse]
 */
export const zDataResponseShareResponse = z.object({
    data: zShareResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * CreateShareLinkRequest
 *
 * Request to create a shareable link.
 */
export const zCreateShareLinkRequest = z.object({
    permission: z.string().regex(/^(read|read_write)$/).optional().default('read')
});

/**
 * DataResponse[ShareLinkResponse]
 */
export const zDataResponseShareLinkResponse = z.object({
    data: zShareLinkResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SharedSessionInfo
 *
 * Extra sharing info returned alongside a chat session summary.
 */
export const zSharedSessionInfo = z.object({
    permission: z.string(),
    sharedAt: z.iso.datetime(),
    sharedBy: zUserResponse
});

/**
 * SharedSessionSummary
 *
 * A chat session that was shared with the current user.
 */
export const zSharedSessionSummary = z.object({
    agentUuid: z.string().nullish(),
    createdAt: z.iso.datetime(),
    isStarred: z.boolean(),
    messageCount: z.int(),
    modelId: z.string(),
    sharing: zSharedSessionInfo,
    title: z.string().nullish(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * DataResponse[list[SharedSessionSummary]]
 */
export const zDataResponseListSharedSessionSummary = z.object({
    data: z.array(zSharedSessionSummary),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * TagChip
 *
 * Minimal tag info for display in history items.
 */
export const zTagChip = z.object({
    color: z.string().optional().default('#6366f1'),
    name: z.string(),
    uuid: z.string()
});

/**
 * ChatSessionSummary
 *
 * Summary chat session schema for lists.
 */
export const zChatSessionSummary = z.object({
    agentUuid: z.string().nullish(),
    createdAt: z.iso.datetime(),
    isStarred: z.boolean(),
    messageCount: z.int(),
    modelId: z.string(),
    projectUuid: z.string().nullish(),
    source: z.string().optional().default('user'),
    spaceIsPersonal: z.boolean().optional().default(false),
    spaceName: z.string().nullish(),
    spaceUuid: z.string().nullish(),
    tags: z.array(zTagChip).optional(),
    title: z.string().nullish(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * PaginatedResponse[ChatSessionSummary]
 */
export const zPaginatedResponseChatSessionSummary = z.object({
    data: z.array(zChatSessionSummary),
    meta: zPaginationMeta
});

/**
 * ChatSessionUpdate
 *
 * Schema for updating a chat session.
 */
export const zChatSessionUpdate = z.object({
    agentUuid: z.string().nullish(),
    brandUuid: z.string().nullish(),
    isStarred: z.boolean().nullish(),
    modelId: z.string().min(1).max(50).nullish(),
    reasoningEffort: z.string().regex(/^(none|minimal|low|medium|high|xhigh|max)$/).nullish(),
    title: z.string().max(255).nullish()
});

/**
 * DataResponse[list[MessageResponse]]
 */
export const zDataResponseListMessageResponse = z.object({
    data: z.array(zSrcSchemasChatMessageResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * EditWithAiRequest
 *
 * Open the chat to edit an entity in (Edit with AI).
 */
export const zEditWithAiRequest = z.object({
    entityType: z.enum([
        'agent',
        'data_source',
        'skill',
        'brand',
        'micro_app',
        'workflow'
    ]),
    modelId: z.string().min(1).max(50),
    uuid: z.string().min(1).max(64)
});

/**
 * DraftApplied
 *
 * The entity a draft card created or saved.
 */
export const zDraftApplied = z.object({
    entityUuid: z.string().min(1).max(64)
});

/**
 * SpaceSummary
 *
 * Lightweight space schema for list views (incl. sidebar).
 */
export const zSpaceSummary = z.object({
    chatCount: z.int().optional().default(0),
    color: z.string().optional().default('#7C16F5'),
    createdAt: z.iso.datetime(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    icon: z.string().optional().default('box'),
    isPersonal: z.boolean().optional().default(false),
    lastActivityAt: z.iso.datetime().nullish(),
    memberCount: z.int().optional().default(0),
    name: z.string(),
    status: z.string().optional().default('active'),
    teamNames: z.array(z.string()).optional(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * PaginatedResponse[SpaceSummary]
 */
export const zPaginatedResponseSpaceSummary = z.object({
    data: z.array(zSpaceSummary),
    meta: zPaginationMeta
});

/**
 * BrandRef
 *
 * A brand named by another resource (e.g. a space's or agent's default brand).
 */
export const zBrandRef = z.object({
    name: z.string(),
    uuid: z.string()
});

/**
 * SpaceResponse
 *
 * Full space response.
 */
export const zSpaceResponse = z.object({
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    color: z.string().optional().default('#7C16F5'),
    createdAt: z.iso.datetime(),
    currentUserRole: z.string().nullish(),
    defaultBrand: zBrandRef.nullish(),
    description: z.string().nullish(),
    icon: z.string().optional().default('box'),
    isPersonal: z.boolean().optional().default(false),
    name: z.string(),
    settings: z.record(z.string(), z.unknown()).optional(),
    status: z.string().optional().default('active'),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * DataResponse[SpaceResponse]
 */
export const zDataResponseSpaceResponse = z.object({
    data: zSpaceResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * DataResponse[list[SpaceSummary]]
 */
export const zDataResponseListSpaceSummary = z.object({
    data: z.array(zSpaceSummary),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SpacePinAdd
 *
 * Schema for pinning a space into the user's sidebar.
 */
export const zSpacePinAdd = z.object({
    displayOrder: z.int().optional().default(0),
    spaceUuid: z.string()
});

/**
 * MessageResponse
 *
 * Simple message response.
 */
export const zSrcSchemasBaseMessageResponse = z.object({
    message: z.string()
});

/**
 * SpacePinReorder
 *
 * Schema for reordering pinned spaces.
 */
export const zSpacePinReorder = z.object({
    spaceUuids: z.array(z.string())
});

/**
 * SpaceCreate
 *
 * Schema for creating a space.
 */
export const zSpaceCreate = z.object({
    color: z.string().max(7).optional().default('#7C16F5'),
    description: z.string().max(5000).nullish(),
    icon: z.string().max(50).optional().default('box'),
    name: z.string().min(1).max(255),
    settings: z.record(z.string(), z.unknown()).optional(),
    teamRole: z.string().regex(/^(admin|editor|viewer)$/).nullish(),
    teamUuid: z.string().nullish()
});

/**
 * SpaceUpdate
 *
 * Schema for updating a space.
 */
export const zSpaceUpdate = z.object({
    color: z.string().max(7).nullish(),
    defaultBrandUuid: z.string().nullish(),
    description: z.string().max(5000).nullish(),
    icon: z.string().max(50).nullish(),
    name: z.string().min(1).max(255).nullish(),
    settings: z.record(z.string(), z.unknown()).nullish(),
    status: z.string().regex(/^(active|archived)$/).nullish()
});

/**
 * SpaceActivityResponse
 *
 * A single activity entry in the space activity feed.
 */
export const zSpaceActivityResponse = z.object({
    activityType: z.string(),
    createdAt: z.iso.datetime(),
    description: z.string().nullish(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    spaceName: z.string().nullish(),
    spaceUuid: z.string().nullish(),
    title: z.string(),
    userEmail: z.string().nullish(),
    userName: z.string().nullish(),
    uuid: z.string()
});

/**
 * SpaceUserMemberSummary
 *
 * A user member of a space (summary used in hub/detail).
 */
export const zSpaceUserMemberSummary = z.object({
    email: z.string(),
    joinedAt: z.iso.datetime(),
    name: z.string(),
    role: z.string(),
    userUuid: z.string()
});

/**
 * SpaceWorkflowSummary
 *
 * A workflow on a space's page: just its most recent run (by anyone), not its history.
 */
export const zSpaceWorkflowSummary = z.object({
    hasSteps: z.boolean().optional().default(true),
    lastRunAt: z.iso.datetime().nullish(),
    lastRunStatus: z.string().nullish(),
    lastRunUuid: z.string().nullish(),
    name: z.string(),
    uuid: z.string()
});

/**
 * SpaceResourceResponse
 *
 * A resource linked to a space (populated fields depend on resource_type).
 */
export const zSpaceResourceResponse = z.object({
    addedByName: z.string().nullish(),
    agentColor: z.string().nullish(),
    agentIcon: z.string().nullish(),
    agentName: z.string().nullish(),
    agentUuid: z.string().nullish(),
    canChange: z.boolean().optional().default(false),
    canGrant: z.boolean().optional().default(false),
    canRevokeAccess: z.boolean().optional().default(false),
    canUse: z.boolean().optional().default(true),
    chatSessionTitle: z.string().nullish(),
    chatSessionUuid: z.string().nullish(),
    createdAt: z.iso.datetime(),
    dataCollectionName: z.string().nullish(),
    dataCollectionUuid: z.string().nullish(),
    displayOrder: z.int().optional().default(0),
    flow: zSpaceWorkflowSummary.nullish(),
    isMine: z.boolean().optional().default(false),
    isPinned: z.boolean().optional().default(false),
    microAppName: z.string().nullish(),
    microAppUuid: z.string().nullish(),
    quickActionLabel: z.string().nullish(),
    quickActionUuid: z.string().nullish(),
    resourceType: z.string(),
    spaceHasAccess: z.boolean().optional().default(false),
    title: z.string().nullish(),
    url: z.string().nullish(),
    uuid: z.string(),
    visibility: z.string().optional().default('space')
});

/**
 * SpaceQuickActionSummary
 *
 * A quick action on a space (launch agent/flow; optional prompt template).
 */
export const zSpaceQuickActionSummary = z.object({
    actionType: z.string(),
    agentName: z.string().nullish(),
    agentUuid: z.string().nullish(),
    autoSubmit: z.boolean().optional().default(false),
    canUse: z.boolean().optional().default(true),
    color: z.string().optional().default('text-blue-400'),
    description: z.string().nullish(),
    displayOrder: z.int().optional().default(0),
    gradient: z.string().optional().default('from-blue-500/20 to-indigo-500/20'),
    hintText: z.string().nullish(),
    icon: z.string().optional().default('zap'),
    inputPlaceholder: z.string().nullish(),
    isEnabled: z.boolean().optional().default(true),
    label: z.string(),
    promptTemplate: z.string().nullish(),
    targetRoute: z.string().nullish(),
    uuid: z.string()
});

/**
 * SpaceChatSummary
 *
 * Lightweight recent-chat summary for the space hub.
 */
export const zSpaceChatSummary = z.object({
    agentName: z.string().nullish(),
    messageCount: z.int().optional().default(0),
    title: z.string().nullish(),
    updatedAt: z.iso.datetime(),
    userName: z.string().nullish(),
    uuid: z.string()
});

/**
 * SpaceHubStats
 *
 * Aggregate stats shown at the top of the hub.
 */
export const zSpaceHubStats = z.object({
    agentCount: z.int().optional().default(0),
    chatCount: z.int().optional().default(0),
    knowledgeCount: z.int().optional().default(0),
    memberCount: z.int().optional().default(0),
    myChatCount: z.int().optional().default(0),
    quickActionCount: z.int().optional().default(0)
});

/**
 * SpaceHubResponse
 *
 * Full aggregated response for the space hub page.
 */
export const zSpaceHubResponse = z.object({
    activities: z.array(zSpaceActivityResponse).optional(),
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    color: z.string().optional().default('#7C16F5'),
    createdAt: z.iso.datetime(),
    currentUserRole: z.string().nullish(),
    defaultBrand: zBrandRef.nullish(),
    description: z.string().nullish(),
    icon: z.string().optional().default('box'),
    isPersonal: z.boolean().optional().default(false),
    members: z.array(zSpaceUserMemberSummary).optional(),
    name: z.string(),
    pinned: z.array(zSpaceResourceResponse).optional(),
    quickActions: z.array(zSpaceQuickActionSummary).optional(),
    recentChats: z.array(zSpaceChatSummary).optional(),
    resources: z.array(zSpaceResourceResponse).optional(),
    stats: zSpaceHubStats,
    status: z.string().optional().default('active'),
    teamMembers: z.array(z.record(z.string(), z.unknown())).optional(),
    teamNames: z.array(z.string()).optional(),
    unplacedWorkflows: z.array(zSpaceWorkflowSummary).optional(),
    uuid: z.string()
});

/**
 * DataResponse[SpaceHubResponse]
 */
export const zDataResponseSpaceHubResponse = z.object({
    data: zSpaceHubResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SpaceTeamMemberResponse
 *
 * A team granted a role on a space.
 */
export const zSpaceTeamMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    teamName: z.string(),
    teamUuid: z.string()
});

/**
 * SpaceUserMemberResponse
 *
 * A user granted a role on a space.
 */
export const zSpaceUserMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    user: zUserResponse
});

/**
 * SpaceMembersResponse
 *
 * Combined membership state for a space plus the caller's effective role.
 */
export const zSpaceMembersResponse = z.object({
    canManage: z.boolean(),
    currentUserRole: z.string().nullish(),
    teamMembers: z.array(zSpaceTeamMemberResponse).optional(),
    userMembers: z.array(zSpaceUserMemberResponse).optional()
});

/**
 * DataResponse[SpaceMembersResponse]
 */
export const zDataResponseSpaceMembersResponse = z.object({
    data: zSpaceMembersResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * PaginatedResponse[SpaceActivityResponse]
 */
export const zPaginatedResponseSpaceActivityResponse = z.object({
    data: z.array(zSpaceActivityResponse),
    meta: zPaginationMeta
});

/**
 * DataResponse[list[SpaceActivityResponse]]
 */
export const zDataResponseListSpaceActivityResponse = z.object({
    data: z.array(zSpaceActivityResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * CollectionFileSummary
 *
 * Summary file schema for lists.
 */
export const zCollectionFileSummary = z.object({
    createdAt: z.iso.datetime(),
    errorMessage: z.string().nullish(),
    fileSize: z.int().nullish(),
    filename: z.string(),
    mimeType: z.string().nullish(),
    sourceType: z.string().nullish(),
    sourceUrl: z.string().nullish(),
    status: z.string(),
    uuid: z.string()
});

/**
 * DataResponse[list[CollectionFileSummary]]
 */
export const zDataResponseListCollectionFileSummary = z.object({
    data: z.array(zCollectionFileSummary),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AddSpaceUserMemberRequest
 *
 * Add or update a user's role on a space.
 */
export const zAddSpaceUserMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    userUuid: z.string()
});

/**
 * DataResponse[SpaceUserMemberResponse]
 */
export const zDataResponseSpaceUserMemberResponse = z.object({
    data: zSpaceUserMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UpdateSpaceMemberRoleRequest
 *
 * Change an existing member's role.
 */
export const zUpdateSpaceMemberRoleRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/)
});

/**
 * AddSpaceTeamMemberRequest
 *
 * Add or update a team's role on a space.
 */
export const zAddSpaceTeamMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    teamUuid: z.string()
});

/**
 * DataResponse[SpaceTeamMemberResponse]
 */
export const zDataResponseSpaceTeamMemberResponse = z.object({
    data: zSpaceTeamMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * DataResponse[list[SpaceResourceResponse]]
 */
export const zDataResponseListSpaceResourceResponse = z.object({
    data: z.array(zSpaceResourceResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SpaceResourceAdd
 *
 * Schema for adding a resource to a space.
 */
export const zSpaceResourceAdd = z.object({
    agentUuid: z.string().nullish(),
    chatSessionUuid: z.string().nullish(),
    dataCollectionUuid: z.string().nullish(),
    displayOrder: z.int().optional().default(0),
    flowUuid: z.string().nullish(),
    isPinned: z.boolean().optional().default(false),
    microAppUuid: z.string().nullish(),
    quickActionUuid: z.string().nullish(),
    resourceType: z.string().regex(/^(agent|data_collection|chat_session|micro_app|quick_action|flow|link)$/),
    title: z.string().max(500).nullish(),
    url: z.string().max(2048).nullish(),
    visibility: z.string().regex(/^(private|space)$/).optional().default('private')
});

/**
 * DataResponse[SpaceResourceResponse]
 */
export const zDataResponseSpaceResourceResponse = z.object({
    data: zSpaceResourceResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SpaceResourceUpdate
 *
 * Schema for updating a resource's pin state, order or visibility.
 */
export const zSpaceResourceUpdate = z.object({
    displayOrder: z.int().nullish(),
    isPinned: z.boolean().nullish(),
    visibility: z.string().regex(/^(private|space)$/).nullish()
});

/**
 * SpaceResourceReorder
 *
 * Schema for reordering resources.
 */
export const zSpaceResourceReorder = z.object({
    resourceUuids: z.array(z.string())
});

/**
 * SidebarSectionResponse
 */
export const zSidebarSectionResponse = z.object({
    collapsed: z.boolean(),
    kind: z.enum(['general', 'custom']),
    name: z.string(),
    sortMode: z.enum([
        'custom',
        'alpha',
        'recent'
    ]),
    spaceUuids: z.array(z.string()).optional(),
    uuid: z.string()
});

/**
 * SidebarResponse
 */
export const zSidebarResponse = z.object({
    sections: z.array(zSidebarSectionResponse).optional()
});

/**
 * DataResponse[SidebarResponse]
 */
export const zDataResponseSidebarResponse = z.object({
    data: zSidebarResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SidebarSectionCreate
 */
export const zSidebarSectionCreate = z.object({
    name: z.string().min(1).max(80)
});

/**
 * SidebarSectionUpdate
 */
export const zSidebarSectionUpdate = z.object({
    collapsed: z.boolean().nullish(),
    name: z.string().min(1).max(80).nullish(),
    sortMode: z.enum([
        'custom',
        'alpha',
        'recent'
    ]).nullish()
});

/**
 * SidebarSectionReorder
 */
export const zSidebarSectionReorder = z.object({
    sectionUuids: z.array(z.string())
});

/**
 * SidebarSpaceMove
 */
export const zSidebarSpaceMove = z.object({
    position: z.int().gte(0).nullish(),
    sectionUuid: z.string().nullish()
});

/**
 * SpaceAccessImpactPerson
 *
 * A space member who can't fully use an item's target.
 */
export const zSpaceAccessImpactPerson = z.object({
    hasAccess: z.boolean(),
    missing: z.array(z.string()).optional(),
    name: z.string(),
    uuid: z.string()
});

/**
 * SpaceAccessImpactResponse
 *
 * Who in the space can't use an item's target, and why (advisory).
 */
export const zSpaceAccessImpactResponse = z.object({
    missingCapability: z.int(),
    people: z.array(zSpaceAccessImpactPerson).optional(),
    totalMembers: z.int(),
    withoutAccess: z.int()
});

/**
 * DataResponse[SpaceAccessImpactResponse]
 */
export const zDataResponseSpaceAccessImpactResponse = z.object({
    data: zSpaceAccessImpactResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * Body_upload_space_files_api_v1_spaces__space_uuid__files_post
 */
export const zBodyUploadSpaceFilesApiV1SpacesSpaceUuidFilesPost = z.object({
    files: z.array(z.string())
});

/**
 * SpaceDocumentResponse
 *
 * A document shared with a space.
 */
export const zSpaceDocumentResponse = z.object({
    canRemove: z.boolean().optional().default(false),
    createdAt: z.iso.datetime(),
    filename: z.string(),
    mimeType: z.string().nullish(),
    sharedByName: z.string().nullish(),
    size: z.int().nullish(),
    url: z.string(),
    uuid: z.string(),
    version: z.int().nullish()
});

/**
 * DataResponse[list[SpaceDocumentResponse]]
 */
export const zDataResponseListSpaceDocumentResponse = z.object({
    data: z.array(zSpaceDocumentResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * DataResponse[list[SpaceChatSummary]]
 */
export const zDataResponseListSpaceChatSummary = z.object({
    data: z.array(zSpaceChatSummary),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SpaceShareUpdate
 *
 * Share a chat with everyone in its space (or stop).
 */
export const zSpaceShareUpdate = z.object({
    shared: z.boolean()
});

/**
 * SpaceDocumentShare
 *
 * Share one version of a chat document with the space (a snapshot).
 */
export const zSpaceDocumentShare = z.object({
    attachmentUuid: z.string()
});

/**
 * DataResponse[SpaceDocumentResponse]
 */
export const zDataResponseSpaceDocumentResponse = z.object({
    data: zSpaceDocumentResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * DataResponse[list[SpaceQuickActionSummary]]
 */
export const zDataResponseListSpaceQuickActionSummary = z.object({
    data: z.array(zSpaceQuickActionSummary),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SpaceQuickActionCreate
 *
 * Schema for creating a quick action.
 */
export const zSpaceQuickActionCreate = z.object({
    actionType: z.string().regex(/^(agent|flow)$/),
    agentUuid: z.string().nullish(),
    autoSubmit: z.boolean().optional().default(false),
    color: z.string().max(50).optional().default('text-blue-400'),
    config: z.record(z.string(), z.unknown()).optional(),
    description: z.string().nullish(),
    displayOrder: z.int().optional().default(0),
    flowUuid: z.string().nullish(),
    gradient: z.string().max(100).optional().default('from-blue-500/20 to-indigo-500/20'),
    hintText: z.string().max(255).nullish(),
    icon: z.string().max(50).optional().default('zap'),
    inputPlaceholder: z.string().max(255).nullish(),
    isEnabled: z.boolean().optional().default(true),
    label: z.string().min(1).max(255),
    promptTemplate: z.string().nullish(),
    targetRoute: z.string().max(500).nullish()
});

/**
 * DataResponse[SpaceQuickActionSummary]
 */
export const zDataResponseSpaceQuickActionSummary = z.object({
    data: zSpaceQuickActionSummary,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SpaceQuickActionUpdate
 *
 * Schema for updating a quick action (all optional).
 */
export const zSpaceQuickActionUpdate = z.object({
    agentUuid: z.string().nullish(),
    autoSubmit: z.boolean().nullish(),
    color: z.string().max(50).nullish(),
    config: z.record(z.string(), z.unknown()).nullish(),
    description: z.string().nullish(),
    displayOrder: z.int().nullish(),
    flowUuid: z.string().nullish(),
    gradient: z.string().max(100).nullish(),
    hintText: z.string().max(255).nullish(),
    icon: z.string().max(50).nullish(),
    inputPlaceholder: z.string().max(255).nullish(),
    isEnabled: z.boolean().nullish(),
    label: z.string().min(1).max(255).nullish(),
    promptTemplate: z.string().nullish(),
    targetRoute: z.string().max(500).nullish()
});

/**
 * ProjectRef
 *
 * Lightweight reference to a project (uuid + name).
 */
export const zProjectRef = z.object({
    name: z.string(),
    uuid: z.string()
});

/**
 * SlashCommandSchema
 *
 * A slash command defined in an agent's system prompt.
 */
export const zSlashCommandSchema = z.object({
    description: z.string().optional().default(''),
    name: z.string()
});

/**
 * AgentSummary
 *
 * Summary agent schema for lists.
 */
export const zAgentSummary = z.object({
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    color: z.string(),
    createdAt: z.iso.datetime(),
    createdByUuid: z.string(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    icon: z.string(),
    modelId: z.string(),
    name: z.string(),
    ownedByProject: zProjectRef.nullish(),
    sharedInProjects: z.array(zProjectRef).optional(),
    slashCommands: z.array(zSlashCommandSchema).optional(),
    teamUuid: z.string(),
    usageCount: z.int(),
    uuid: z.string()
});

/**
 * DataResponse[list[AgentSummary]]
 */
export const zDataResponseListAgentSummary = z.object({
    data: z.array(zAgentSummary),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AgentPinAdd
 *
 * Star an agent for Home's "Pinned agents".
 */
export const zAgentPinAdd = z.object({
    agentUuid: z.string()
});

/**
 * ActiveOperation
 *
 * A running or paused operation: its Redis status hash, as stored (every value is a string). An operation the
 * list has just failed (a reply whose worker died, or one gone stale) is listed once more as failed (MHS-793).
 */
export const zActiveOperation = z.object({
    entity_id: z.string().nullish(),
    operation_id: z.string().nullish(),
    operation_type: z.string().nullish(),
    started_at: z.string().nullish(),
    status: z.string().nullish(),
    title: z.string().nullish(),
    updated_at: z.string().nullish()
});

/**
 * ActiveOperationsResponse
 */
export const zActiveOperationsResponse = z.object({
    data: z.array(zActiveOperation)
});

/**
 * WikiFileSummary
 */
export const zWikiFileSummary = z.object({
    errorCount: z.int(),
    nodeType: z.string().nullable(),
    path: z.string(),
    revision: z.int(),
    title: z.string().nullable(),
    updatedAt: z.iso.datetime(),
    updatedBy: z.string().nullable(),
    visibility: z.enum([
        'private',
        'members',
        'org'
    ]),
    warningCount: z.int()
});

/**
 * WikiFileList
 */
export const zWikiFileList = z.object({
    canEdit: z.boolean(),
    canPublish: z.boolean().optional().default(false),
    files: z.array(zWikiFileSummary),
    isMember: z.boolean().optional().default(true),
    spaceName: z.string().optional().default('')
});

/**
 * DataResponse[WikiFileList]
 */
export const zDataResponseWikiFileList = z.object({
    data: zWikiFileList,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * WikiBacklink
 *
 * A readable file that links here.
 */
export const zWikiBacklink = z.object({
    edgeTypes: z.array(z.string()),
    path: z.string(),
    spaceUuid: z.string(),
    title: z.string()
});

/**
 * WikiDiagnostic
 *
 * A problem the indexer found in a file (error = not indexed, warning = indexed).
 */
export const zWikiDiagnostic = z.object({
    code: z.string(),
    message: z.string(),
    severity: z.enum(['error', 'warning'])
});

/**
 * WikiLinkTarget
 *
 * Where a ``[[link]]`` in this file points. Only targets the caller may read.
 */
export const zWikiLinkTarget = z.object({
    nodeUuid: z.string(),
    path: z.string(),
    sameSpace: z.boolean(),
    spaceUuid: z.string(),
    text: z.string(),
    title: z.string()
});

/**
 * WikiNodeInfo
 */
export const zWikiNodeInfo = z.object({
    aliases: z.array(z.string()),
    canonicalName: z.string(),
    confirmed: z.boolean(),
    nodeType: z.string(),
    summary: z.string().nullable(),
    uuid: z.string(),
    visibility: z.enum([
        'private',
        'members',
        'org'
    ])
});

/**
 * WikiFileResponse
 */
export const zWikiFileResponse = z.object({
    backlinks: z.array(zWikiBacklink),
    canEdit: z.boolean(),
    canPublish: z.boolean().optional().default(false),
    content: z.string(),
    diagnostics: z.array(zWikiDiagnostic),
    links: z.array(zWikiLinkTarget),
    node: zWikiNodeInfo.nullable(),
    path: z.string(),
    revision: z.int(),
    updatedAt: z.iso.datetime(),
    updatedBy: z.string().nullable(),
    uuid: z.string(),
    visibility: z.enum([
        'private',
        'members',
        'org'
    ])
});

/**
 * DataResponse[WikiFileResponse]
 */
export const zDataResponseWikiFileResponse = z.object({
    data: zWikiFileResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * WikiFileSave
 */
export const zWikiFileSave = z.object({
    baseRevision: z.int().gte(1).nullish(),
    content: z.string().max(524288),
    path: z.string().min(1).max(512)
});

/**
 * AgentMemoryEntry
 */
export const zAgentMemoryEntry = z.object({
    key: z.string(),
    updatedAt: z.iso.datetime(),
    uuid: z.string(),
    value: z.string()
});

/**
 * AgentMemoryGroup
 *
 * One agent's notes about you. ``agentUuid`` null: the default AnthmAI chat.
 */
export const zAgentMemoryGroup = z.object({
    agentName: z.string().nullish(),
    agentUuid: z.string().nullish(),
    entries: z.array(zAgentMemoryEntry)
});

/**
 * AgentMemoryList
 */
export const zAgentMemoryList = z.object({
    groups: z.array(zAgentMemoryGroup)
});

/**
 * DataResponse[AgentMemoryList]
 */
export const zDataResponseAgentMemoryList = z.object({
    data: zAgentMemoryList,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ProposalMatch
 *
 * How an extracted name was linked to an existing page when it wasn't the page's own name.
 */
export const zProposalMatch = z.object({
    mention: z.string(),
    reason: z.enum([
        'same_name',
        'alias',
        'first_name',
        'nickname',
        'first_name_and_surname',
        'initial_and_surname',
        'first_name_and_initial',
        'surname_spelling',
        'first_name_spelling',
        'surname',
        'username',
        'abbreviation',
        'spelling',
        'short_name',
        'shorter_name'
    ])
});

/**
 * ProposalNode
 *
 * The existing page an extracted name matched (only pages the caller can read).
 */
export const zProposalNode = z.object({
    name: z.string(),
    path: z.string(),
    spaceUuid: z.string(),
    type: z.string(),
    uuid: z.string()
});

/**
 * ProposalEntity
 */
export const zProposalEntity = z.object({
    aliases: z.array(z.string()),
    matchedAs: zProposalMatch.nullish(),
    name: z.string(),
    node: zProposalNode.nullable(),
    type: z.string()
});

/**
 * Proposal
 */
export const zProposal = z.object({
    chatSessionUuid: z.string().nullable(),
    chatSpaceUuid: z.string().nullable(),
    confidence: z.number().gte(0).lte(1),
    createdAt: z.iso.datetime(),
    evidence: z.string(),
    importFilename: z.string().nullish(),
    importUuid: z.string().nullish(),
    object: zProposalEntity,
    relation: z.enum([
        'works_on',
        'leads',
        'reports_to',
        'owns',
        'expert_in',
        'depends_on',
        'uses',
        'customer_of'
    ]),
    role: z.string().nullish(),
    status: z.enum([
        'pending',
        'accepted',
        'rejected',
        'expired'
    ]),
    subject: zProposalEntity,
    summary: z.string(),
    uuid: z.string()
});

/**
 * ProposalList
 */
export const zProposalList = z.object({
    pendingCount: z.int(),
    proposals: z.array(zProposal)
});

/**
 * DataResponse[ProposalList]
 */
export const zDataResponseProposalList = z.object({
    data: zProposalList,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ProposalAccept
 *
 * Accept, optionally corrected. Omitted fields keep the proposal's values.
 */
export const zProposalAccept = z.object({
    newPageVisibility: z.enum([
        'private',
        'members',
        'org'
    ]).optional().default('private'),
    objectAliases: z.array(z.string().max(200)).max(20).optional(),
    objectName: z.string().min(1).max(200).nullish(),
    objectType: z.enum([
        'person',
        'project',
        'product',
        'concept',
        'document',
        'event',
        'company',
        'agent'
    ]).nullish(),
    relation: z.enum([
        'works_on',
        'leads',
        'reports_to',
        'owns',
        'expert_in',
        'depends_on',
        'uses',
        'customer_of'
    ]).nullish(),
    role: z.string().max(80).nullish(),
    spaceUuid: z.string().nullish(),
    subjectAliases: z.array(z.string().max(200)).max(20).optional(),
    subjectName: z.string().min(1).max(200).nullish(),
    subjectType: z.enum([
        'person',
        'project',
        'product',
        'concept',
        'document',
        'event',
        'company',
        'agent'
    ]).nullish()
});

/**
 * WrittenPage
 */
export const zWrittenPage = z.object({
    created: z.boolean(),
    path: z.string(),
    title: z.string()
});

/**
 * ProposalAccepted
 */
export const zProposalAccepted = z.object({
    pages: z.array(zWrittenPage),
    proposal: zProposal,
    spaceUuid: z.string()
});

/**
 * DataResponse[ProposalAccepted]
 */
export const zDataResponseProposalAccepted = z.object({
    data: zProposalAccepted,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * DataResponse[Proposal]
 */
export const zDataResponseProposal = z.object({
    data: zProposal,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * Body_upload_import_api_v1_kg_spaces__space_uuid__imports_upload_post
 */
export const zBodyUploadImportApiV1KgSpacesSpaceUuidImportsUploadPost = z.object({
    file: z.string(),
    title: z.string().max(200).nullish()
});

/**
 * KGImportProgress
 */
export const zKgImportProgress = z.object({
    facts: z.int(),
    partsDone: z.int(),
    partsFailed: z.int(),
    partsTotal: z.int(),
    proposals: z.int(),
    truncated: z.boolean()
});

/**
 * KGImportOut
 */
export const zKgImportOut = z.object({
    createdAt: z.iso.datetime(),
    errorCode: z.string().nullable(),
    filename: z.string(),
    finishedAt: z.iso.datetime().nullable(),
    pendingCount: z.int(),
    progress: zKgImportProgress,
    sourceType: z.enum([
        'upload',
        'paste',
        'space_file'
    ]),
    spaceUuid: z.string(),
    status: z.enum([
        'queued',
        'extracting',
        'done',
        'failed',
        'cancelled'
    ]),
    uuid: z.string()
});

/**
 * KGImportCreated
 */
export const zKgImportCreated = z.object({
    alreadyImported: z.boolean(),
    import: zKgImportOut
});

/**
 * DataResponse[KGImportCreated]
 */
export const zDataResponseKgImportCreated = z.object({
    data: zKgImportCreated,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * KGImportText
 *
 * Import pasted text, or a file already in the space's knowledge base (exactly one).
 */
export const zKgImportText = z.object({
    spaceFileUuid: z.string().nullish(),
    text: z.string().min(1).max(200000).nullish(),
    title: z.string().max(200).nullish()
});

/**
 * KGImportList
 */
export const zKgImportList = z.object({
    imports: z.array(zKgImportOut)
});

/**
 * DataResponse[KGImportList]
 */
export const zDataResponseKgImportList = z.object({
    data: zKgImportList,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * DataResponse[KGImportOut]
 */
export const zDataResponseKgImportOut = z.object({
    data: zKgImportOut,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * KGImportAcceptAll
 */
export const zKgImportAcceptAll = z.object({
    newPageVisibility: z.enum([
        'private',
        'members',
        'org'
    ]).optional().default('members'),
    spaceUuid: z.string().nullish()
});

/**
 * KGImportBulkResult
 */
export const zKgImportBulkResult = z.object({
    accepted: z.int(),
    dismissed: z.int().optional().default(0),
    pages: z.array(zWrittenPage).optional(),
    skipped: z.int(),
    spaceUuid: z.string().nullish()
});

/**
 * DataResponse[KGImportBulkResult]
 */
export const zDataResponseKgImportBulkResult = z.object({
    data: zKgImportBulkResult,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * MergePage
 *
 * One side of a possible duplicate, or of a merge.
 */
export const zMergePage = z.object({
    aliases: z.array(z.string()),
    nodeType: z.string(),
    path: z.string(),
    revision: z.int(),
    summary: z.string().nullable(),
    title: z.string(),
    visibility: z.enum([
        'private',
        'members',
        'org'
    ])
});

/**
 * DuplicatePair
 */
export const zDuplicatePair = z.object({
    a: zMergePage,
    b: zMergePage,
    reason: z.enum([
        'same_name',
        'alias',
        'first_name',
        'nickname',
        'first_name_and_surname',
        'initial_and_surname',
        'first_name_and_initial',
        'surname_spelling',
        'first_name_spelling',
        'surname',
        'username',
        'abbreviation',
        'spelling',
        'short_name',
        'shorter_name'
    ]),
    score: z.number()
});

/**
 * DuplicateList
 */
export const zDuplicateList = z.object({
    pairs: z.array(zDuplicatePair)
});

/**
 * DataResponse[DuplicateList]
 */
export const zDataResponseDuplicateList = z.object({
    data: zDuplicateList,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * DismissDuplicate
 */
export const zDismissDuplicate = z.object({
    pathA: z.string().min(1).max(512),
    pathB: z.string().min(1).max(512)
});

/**
 * MergeRequest
 */
export const zMergeRequest = z.object({
    keepPath: z.string().min(1).max(512),
    removePath: z.string().min(1).max(512)
});

/**
 * MergePreview
 */
export const zMergePreview = z.object({
    content: z.string(),
    keep: zMergePage,
    linksFromOtherSpaces: z.int(),
    remove: zMergePage,
    rewrittenPaths: z.array(z.string()),
    visibility: z.enum([
        'private',
        'members',
        'org'
    ])
});

/**
 * DataResponse[MergePreview]
 */
export const zDataResponseMergePreview = z.object({
    data: zMergePreview,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * MergeConfirm
 */
export const zMergeConfirm = z.object({
    keepPath: z.string().min(1).max(512),
    keepRevision: z.int().gte(1),
    removePath: z.string().min(1).max(512),
    removeRevision: z.int().gte(1)
});

/**
 * MergeRecord
 */
export const zMergeRecord = z.object({
    createdAt: z.iso.datetime(),
    keptName: z.string(),
    keptPath: z.string(),
    pagesChanged: z.int(),
    removedName: z.string(),
    removedPath: z.string(),
    undone: z.boolean(),
    uuid: z.string()
});

/**
 * DataResponse[MergeRecord]
 */
export const zDataResponseMergeRecord = z.object({
    data: zMergeRecord,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * MergeList
 */
export const zMergeList = z.object({
    merges: z.array(zMergeRecord)
});

/**
 * DataResponse[MergeList]
 */
export const zDataResponseMergeList = z.object({
    data: zMergeList,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * PaginatedResponse[AgentSummary]
 */
export const zPaginatedResponseAgentSummary = z.object({
    data: z.array(zAgentSummary),
    meta: zPaginationMeta
});

/**
 * AgentToolConfig
 *
 * Configuration for an agent's tool.
 */
export const zAgentToolConfig = z.object({
    config: z.record(z.string(), z.unknown()).optional(),
    isEnabled: z.boolean().optional().default(true),
    toolUuid: z.string()
});

/**
 * AgentCreate
 *
 * Schema for creating an agent.
 */
export const zAgentCreate = z.object({
    color: z.string().max(9).optional().default('#06b6d4'),
    dataCollectionIds: z.array(z.string()).optional(),
    description: z.string().max(5000).nullish(),
    icon: z.string().max(50).optional().default('bot'),
    modelId: z.string().min(1).max(50),
    name: z.string().min(1).max(255),
    settings: z.record(z.string(), z.unknown()).optional(),
    sourceSessionUuid: z.string().max(64).nullish(),
    systemPrompt: z.string().min(1).max(100000),
    teamUuid: z.string(),
    toolConfigs: z.array(zAgentToolConfig).optional()
});

/**
 * DataCollectionSummary
 *
 * Summary data collection schema for lists.
 */
export const zDataCollectionSummary = z.object({
    automatedSyncEnabled: z.boolean().optional().default(false),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    documentCount: z.int(),
    fileCount: z.int(),
    name: z.string(),
    sourceType: z.string(),
    status: z.string(),
    syncOwnerEmail: z.string().nullish(),
    syncOwnerName: z.string().nullish(),
    uuid: z.string()
});

/**
 * ExtensionBadge
 *
 * Lightweight extension identity embedded on Tool/Skill summaries.
 *
 * Lets pickers (e.g. the Agent Builder Capabilities tab) group and badge
 * tools/skills by their parent Extension, and flag disabled extensions,
 * without requiring a separate per-extension fetch.
 */
export const zExtensionBadge = z.object({
    color: z.string().nullish(),
    extensionType: z.string(),
    icon: z.string().nullish(),
    isEnabled: z.boolean(),
    name: z.string(),
    uuid: z.string()
});

/**
 * ToolSummary
 *
 * Summary tool schema for lists.
 */
export const zToolSummary = z.object({
    capabilities: z.array(z.string()).optional(),
    confirmation: z.string().optional().default('none'),
    description: z.string().nullish(),
    escalation: z.string().optional().default('none'),
    extension: zExtensionBadge.nullish(),
    icon: z.string(),
    isEnabled: z.boolean(),
    isSystem: z.boolean(),
    name: z.string(),
    toolType: z.string(),
    uuid: z.string()
});

/**
 * AgentResponse
 *
 * Full agent response schema.
 */
export const zAgentResponse = z.object({
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    color: z.string(),
    createdAt: z.iso.datetime(),
    createdByUuid: z.string(),
    currentUserRole: z.string().nullish(),
    currentVersionNumber: z.int().nullish(),
    dataCollectionIds: z.array(z.string()).readonly(),
    dataCollections: z.array(zDataCollectionSummary).optional(),
    defaultBrand: zBrandRef.nullish(),
    defaultFileBlobPaths: z.array(z.string()).nullish(),
    defaultInputPrompt: z.string().nullish(),
    description: z.string().nullish(),
    icon: z.string(),
    modelId: z.string(),
    name: z.string(),
    ownedByProject: zProjectRef.nullish(),
    settings: z.record(z.string(), z.unknown()).optional(),
    sharedInProjects: z.array(zProjectRef).optional(),
    slashCommands: z.array(zSlashCommandSchema).optional(),
    systemPrompt: z.string(),
    teamUuid: z.string(),
    tools: z.array(zToolSummary).optional(),
    updatedAt: z.iso.datetime(),
    usageCount: z.int(),
    uuid: z.string()
});

/**
 * DataResponse[AgentResponse]
 */
export const zDataResponseAgentResponse = z.object({
    data: zAgentResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AgentUpdate
 *
 * Schema for updating an agent.
 */
export const zAgentUpdate = z.object({
    color: z.string().max(9).nullish(),
    dataCollectionIds: z.array(z.string()).nullish(),
    defaultBrandUuid: z.string().nullish(),
    description: z.string().max(5000).nullish(),
    icon: z.string().max(50).nullish(),
    modelId: z.string().min(1).max(50).nullish(),
    name: z.string().min(1).max(255).nullish(),
    settings: z.record(z.string(), z.unknown()).nullish(),
    systemPrompt: z.string().min(1).max(100000).nullish(),
    teamUuid: z.string().nullish(),
    toolConfigs: z.array(zAgentToolConfig).nullish()
});

/**
 * SkillSummary
 *
 * Summary skill schema for lists.
 */
export const zSkillSummary = z.object({
    category: z.string().nullish(),
    color: z.string(),
    createdAt: z.iso.datetime(),
    currentUserRole: z.string().nullish(),
    description: z.string(),
    extension: zExtensionBadge.nullish(),
    icon: z.string(),
    isSystem: z.boolean(),
    name: z.string(),
    requiredToolUuids: z.array(z.string()).optional(),
    skillType: z.string(),
    tags: z.array(z.string()).optional(),
    teamUuid: z.string().nullish(),
    usageCount: z.int(),
    uuid: z.string(),
    version: z.string()
});

/**
 * AgentSkillResponse
 *
 * Response for a skill attached to an agent.
 */
export const zAgentSkillResponse = z.object({
    config: z.record(z.string(), z.unknown()).optional(),
    isEnabled: z.boolean(),
    priority: z.int(),
    skill: zSkillSummary
});

/**
 * DataResponse[list[AgentSkillResponse]]
 */
export const zDataResponseListAgentSkillResponse = z.object({
    data: z.array(zAgentSkillResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AgentSkillAttach
 *
 * Schema for attaching a skill to an agent.
 */
export const zAgentSkillAttach = z.object({
    config: z.record(z.string(), z.unknown()).optional(),
    isEnabled: z.boolean().optional().default(true),
    priority: z.int().optional().default(0),
    skillUuid: z.string()
});

/**
 * AgentSkillUpdate
 *
 * Schema for updating a skill attachment on an agent.
 */
export const zAgentSkillUpdate = z.object({
    config: z.record(z.string(), z.unknown()).nullish(),
    isEnabled: z.boolean().nullish(),
    priority: z.int().nullish()
});

/**
 * DataResponse[AgentSkillResponse]
 */
export const zDataResponseAgentSkillResponse = z.object({
    data: zAgentSkillResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * PaginatedResponse[ToolSummary]
 */
export const zPaginatedResponseToolSummary = z.object({
    data: z.array(zToolSummary),
    meta: zPaginationMeta
});

/**
 * SpaceGrantResponse
 *
 * A space whose members can use this item, given through the space (MHS-766/774).
 */
export const zSpaceGrantResponse = z.object({
    grantedBy: z.string().nullish(),
    itemUuid: z.string(),
    role: z.string(),
    spaceName: z.string(),
    spaceUuid: z.string()
});

/**
 * AgentTeamMemberResponse
 *
 * A team granted a role on an agent.
 */
export const zAgentTeamMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    teamName: z.string(),
    teamUuid: z.string()
});

/**
 * AgentUserMemberResponse
 *
 * A user granted a role on an agent.
 */
export const zAgentUserMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    user: zUserResponse
});

/**
 * AgentMembersResponse
 *
 * Combined membership state for an agent plus the caller's effective role.
 */
export const zAgentMembersResponse = z.object({
    canManage: z.boolean(),
    currentUserRole: z.string().nullish(),
    spaceGrants: z.array(zSpaceGrantResponse).optional(),
    teamMembers: z.array(zAgentTeamMemberResponse).optional(),
    userMembers: z.array(zAgentUserMemberResponse).optional()
});

/**
 * DataResponse[AgentMembersResponse]
 */
export const zDataResponseAgentMembersResponse = z.object({
    data: zAgentMembersResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SiblingRef
 *
 * A reference to a sibling skill/agent from the same import (MHS-359).
 */
export const zSiblingRef = z.object({
    name: z.string(),
    uuid: z.string()
});

/**
 * AgentDeleteImpactResponse
 *
 * What deleting an agent would affect (the imported sibling skill).
 */
export const zAgentDeleteImpactResponse = z.object({
    importGroupId: z.string().nullish(),
    siblingSkill: zSiblingRef.nullish(),
    siblingSkillAttachedElsewhere: z.boolean().optional().default(false)
});

/**
 * DataResponse[AgentDeleteImpactResponse]
 */
export const zDataResponseAgentDeleteImpactResponse = z.object({
    data: zAgentDeleteImpactResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AddAgentUserMemberRequest
 *
 * Add or update a user's role on an agent.
 */
export const zAddAgentUserMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    userUuid: z.string()
});

/**
 * DataResponse[AgentUserMemberResponse]
 */
export const zDataResponseAgentUserMemberResponse = z.object({
    data: zAgentUserMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UpdateAgentMemberRoleRequest
 *
 * Change an existing member's role.
 */
export const zUpdateAgentMemberRoleRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/)
});

/**
 * AddAgentTeamMemberRequest
 *
 * Add or update a team's role on an agent.
 */
export const zAddAgentTeamMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    teamUuid: z.string()
});

/**
 * DataResponse[AgentTeamMemberResponse]
 */
export const zDataResponseAgentTeamMemberResponse = z.object({
    data: zAgentTeamMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * PaginatedResponse[SkillSummary]
 */
export const zPaginatedResponseSkillSummary = z.object({
    data: z.array(zSkillSummary),
    meta: zPaginationMeta
});

/**
 * SkillCreate
 *
 * Schema for creating a skill.
 */
export const zSkillCreate = z.object({
    category: z.string().max(100).nullish(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().default('#7C16F5'),
    description: z.string().min(1).max(5000),
    icon: z.string().max(50).optional().default('zap'),
    instructions: z.string().min(1).max(200000),
    name: z.string().min(1).max(255),
    requiredToolUuids: z.array(z.string()).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    skillType: z.string().regex(/^(context|dynamic_session|custom_container)$/).optional().default('context'),
    sourceSessionUuid: z.string().max(64).nullish(),
    tags: z.array(z.string()).optional(),
    teamUuid: z.string().nullish(),
    version: z.string().max(50).optional().default('1.0.0')
});

/**
 * SkillToolSummary
 *
 * Summary of a tool linked to a skill.
 */
export const zSkillToolSummary = z.object({
    isRequired: z.boolean(),
    toolName: z.string(),
    toolUuid: z.string()
});

/**
 * SkillResponse
 *
 * Full skill response schema.
 */
export const zSkillResponse = z.object({
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    category: z.string().nullish(),
    color: z.string(),
    createdAt: z.iso.datetime(),
    createdByUuid: z.string().nullish(),
    currentUserRole: z.string().nullish(),
    description: z.string(),
    extension: zExtensionBadge.nullish(),
    icon: z.string(),
    instructions: z.string(),
    isSystem: z.boolean(),
    name: z.string(),
    settings: z.record(z.string(), z.unknown()).optional(),
    skillType: z.string(),
    tags: z.array(z.string()).optional(),
    teamUuid: z.string().nullish(),
    tools: z.array(zSkillToolSummary).optional(),
    updatedAt: z.iso.datetime(),
    usageCount: z.int(),
    uuid: z.string(),
    version: z.string()
});

/**
 * DataResponse[SkillResponse]
 */
export const zDataResponseSkillResponse = z.object({
    data: zSkillResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * DataResponse[list[str]]
 */
export const zDataResponseListStr = z.object({
    data: z.array(z.string()),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SkillUpdate
 *
 * Schema for updating a skill.
 */
export const zSkillUpdate = z.object({
    category: z.string().max(100).nullish(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullish(),
    description: z.string().min(1).max(5000).nullish(),
    icon: z.string().max(50).nullish(),
    instructions: z.string().min(1).max(200000).nullish(),
    name: z.string().min(1).max(255).nullish(),
    requiredToolUuids: z.array(z.string()).nullish(),
    settings: z.record(z.string(), z.unknown()).nullish(),
    skillType: z.string().regex(/^(context|dynamic_session|custom_container)$/).nullish(),
    tags: z.array(z.string()).nullish(),
    teamUuid: z.string().nullish(),
    version: z.string().max(50).nullish()
});

/**
 * SkillDeleteImpactResponse
 *
 * What deleting a skill would affect.
 */
export const zSkillDeleteImpactResponse = z.object({
    attachedAgentCount: z.int().optional().default(0),
    importGroupId: z.string().nullish(),
    siblingAgents: z.array(zSiblingRef).optional()
});

/**
 * DataResponse[SkillDeleteImpactResponse]
 */
export const zDataResponseSkillDeleteImpactResponse = z.object({
    data: zSkillDeleteImpactResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SkillTeamMemberResponse
 *
 * A team granted a role on a skill.
 */
export const zSkillTeamMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    teamName: z.string(),
    teamUuid: z.string()
});

/**
 * SkillUserMemberResponse
 *
 * A user granted a role on a skill.
 */
export const zSkillUserMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    user: zUserResponse
});

/**
 * SkillMembersResponse
 *
 * Membership state for a skill plus the caller's effective role.
 */
export const zSkillMembersResponse = z.object({
    canManage: z.boolean(),
    currentUserRole: z.string().nullish(),
    teamMembers: z.array(zSkillTeamMemberResponse).optional(),
    userMembers: z.array(zSkillUserMemberResponse).optional()
});

/**
 * DataResponse[SkillMembersResponse]
 */
export const zDataResponseSkillMembersResponse = z.object({
    data: zSkillMembersResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AddSkillUserMemberRequest
 *
 * Add or update a user's role on a skill.
 */
export const zAddSkillUserMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    userUuid: z.string()
});

/**
 * DataResponse[SkillUserMemberResponse]
 */
export const zDataResponseSkillUserMemberResponse = z.object({
    data: zSkillUserMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UpdateSkillMemberRoleRequest
 *
 * Change a skill member's role.
 */
export const zUpdateSkillMemberRoleRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/)
});

/**
 * AddSkillTeamMemberRequest
 *
 * Add or update a team's role on a skill.
 */
export const zAddSkillTeamMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    teamUuid: z.string()
});

/**
 * DataResponse[SkillTeamMemberResponse]
 */
export const zDataResponseSkillTeamMemberResponse = z.object({
    data: zSkillTeamMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * Body_analyze_import_api_v1_skills_import_analyze_post
 */
export const zBodyAnalyzeImportApiV1SkillsImportAnalyzePost = z.object({
    file: z.string()
});

/**
 * AnalyzedAgentSchema
 *
 * An agent detected in a bundle (preview, before creation).
 */
export const zAnalyzedAgentSchema = z.object({
    description: z.string().optional().default(''),
    name: z.string(),
    sourceFile: z.string()
});

/**
 * AnalyzedSkillSchema
 *
 * A skill detected in a bundle (preview, before creation).
 */
export const zAnalyzedSkillSchema = z.object({
    agents: z.array(zAnalyzedAgentSchema).optional(),
    description: z.string().optional().default(''),
    disabledTools: z.array(z.string()).optional(),
    matchedTools: z.array(z.string()).optional(),
    missingTools: z.array(z.string()).optional(),
    name: z.string(),
    nameTaken: z.boolean().optional().default(false),
    referenceCount: z.int().optional().default(0),
    requiresExecution: z.boolean().optional().default(false),
    scriptCount: z.int().optional().default(0),
    sourcePath: z.string(),
    usable: z.boolean().optional().default(true),
    warnings: z.array(z.string()).optional()
});

/**
 * BundleAnalysisResponse
 *
 * Everything found in an uploaded bundle, for user review.
 */
export const zBundleAnalysisResponse = z.object({
    skills: z.array(zAnalyzedSkillSchema).optional()
});

/**
 * DataResponse[BundleAnalysisResponse]
 */
export const zDataResponseBundleAnalysisResponse = z.object({
    data: zBundleAnalysisResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * Body_import_skills_api_v1_skills_import_post
 */
export const zBodyImportSkillsApiV1SkillsImportPost = z.object({
    file: z.string(),
    selection: z.string().nullish()
});

/**
 * ImportedAgentReport
 *
 * An agent created by converting a bundle's agents/ config (MHS-359).
 */
export const zImportedAgentReport = z.object({
    agentUuid: z.string().nullish(),
    name: z.string(),
    sourceFolder: z.string()
});

/**
 * ImportedSkillReport
 *
 * Per-skill outcome of a bundle import (MHS-359).
 */
export const zImportedSkillReport = z.object({
    agents: z.array(zImportedAgentReport).optional(),
    disabledTools: z.array(z.string()).optional(),
    error: z.string().nullish(),
    linkedTools: z.array(z.string()).optional(),
    missingTools: z.array(z.string()).optional(),
    name: z.string(),
    skillUuid: z.string().nullish(),
    sourcePath: z.string(),
    status: z.string()
});

/**
 * SkillImportResponse
 *
 * Aggregate result of importing a skill bundle.
 */
export const zSkillImportResponse = z.object({
    createdAgentCount: z.int().optional().default(0),
    createdCount: z.int(),
    missingTools: z.array(z.string()).optional(),
    results: z.array(zImportedSkillReport).optional()
});

/**
 * DataResponse[SkillImportResponse]
 */
export const zDataResponseSkillImportResponse = z.object({
    data: zSkillImportResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * PaginatedResponse[DataCollectionSummary]
 */
export const zPaginatedResponseDataCollectionSummary = z.object({
    data: z.array(zDataCollectionSummary),
    meta: zPaginationMeta
});

/**
 * DataCollectionCreate
 *
 * Schema for creating a data collection.
 */
export const zDataCollectionCreate = z.object({
    config: z.record(z.string(), z.unknown()).optional(),
    content: z.string().max(500000).nullish(),
    description: z.string().max(5000).nullish(),
    name: z.string().min(1).max(255),
    sourceSessionUuid: z.string().max(64).nullish(),
    sourceType: z.string().optional().default('upload'),
    teamUuid: z.string()
});

/**
 * DataCollectionResponse
 *
 * Full data collection response schema.
 */
export const zDataCollectionResponse = z.object({
    automatedSyncEnabled: z.boolean().optional().default(false),
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    config: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.iso.datetime(),
    createdByName: z.string().nullish(),
    createdByUuid: z.string(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    documentCount: z.int(),
    errorMessage: z.string().nullish(),
    fileCount: z.int(),
    files: z.array(zCollectionFileSummary).optional(),
    lastSyncedAt: z.iso.datetime().nullish(),
    name: z.string(),
    sourceType: z.string(),
    status: z.string(),
    syncOwnerEmail: z.string().nullish(),
    syncOwnerName: z.string().nullish(),
    teamUuid: z.string(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * DataResponse[DataCollectionResponse]
 */
export const zDataResponseDataCollectionResponse = z.object({
    data: zDataCollectionResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SyncScheduleConfig
 *
 * Validated cadence for automated background sync.
 *
 * Stored inside ``DataCollection.config['sync_schedule']``. Validates the
 * user-supplied cadence before it is persisted / turned into a cron expression
 * (prevents malformed/abusive schedules — M4).
 */
export const zSyncScheduleConfig = z.object({
    daysOfWeek: z.array(z.int()).nullish(),
    intervalDays: z.int().gte(1).lte(365).nullish(),
    runAtTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional().default('09:00'),
    timezone: z.string().max(64).optional().default('UTC'),
    type: z.string().regex(/^(daily|weekly|custom)$/).optional().default('daily')
});

/**
 * DataCollectionUpdate
 *
 * Schema for updating a data collection.
 */
export const zDataCollectionUpdate = z.object({
    config: z.record(z.string(), z.unknown()).nullish(),
    description: z.string().max(5000).nullish(),
    enableAutomatedSync: z.boolean().nullish(),
    name: z.string().min(1).max(255).nullish(),
    syncSchedule: zSyncScheduleConfig.nullish(),
    teamUuid: z.string().nullish()
});

/**
 * Body_upload_files_api_v1_data_sources__collection_uuid__files_post
 */
export const zBodyUploadFilesApiV1DataSourcesCollectionUuidFilesPost = z.object({
    files: z.array(z.string())
});

/**
 * DataResponse[dict]
 */
export const zDataResponseDict = z.object({
    data: z.record(z.string(), z.unknown()),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * NoteCreate
 *
 * Schema for adding a note to a data collection.
 */
export const zNoteCreate = z.object({
    content: z.string().min(1).max(500000),
    title: z.string().min(1).max(255)
});

/**
 * DataResponse[CollectionFileSummary]
 */
export const zDataResponseCollectionFileSummary = z.object({
    data: zCollectionFileSummary,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * NoteUpdate
 *
 * Schema for updating a note in a data collection.
 */
export const zNoteUpdate = z.object({
    content: z.string().min(1).max(500000).nullish(),
    title: z.string().min(1).max(255).nullish()
});

/**
 * AddWebsiteUrlRequest
 *
 * Schema for adding a website URL to a collection.
 */
export const zAddWebsiteUrlRequest = z.object({
    url: z.string().min(1).max(2048)
});

/**
 * DataSourceTeamMemberResponse
 *
 * A team granted a role on a data source.
 */
export const zDataSourceTeamMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    teamName: z.string(),
    teamUuid: z.string()
});

/**
 * DataSourceUserMemberResponse
 *
 * A user granted a role on a data source.
 */
export const zDataSourceUserMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    user: zUserResponse
});

/**
 * DataSourceMembersResponse
 *
 * Membership state for a data source plus the caller's effective role.
 */
export const zDataSourceMembersResponse = z.object({
    canManage: z.boolean(),
    currentUserRole: z.string().nullish(),
    spaceGrants: z.array(zSpaceGrantResponse).optional(),
    teamMembers: z.array(zDataSourceTeamMemberResponse).optional(),
    userMembers: z.array(zDataSourceUserMemberResponse).optional()
});

/**
 * DataResponse[DataSourceMembersResponse]
 */
export const zDataResponseDataSourceMembersResponse = z.object({
    data: zDataSourceMembersResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AddDataSourceUserMemberRequest
 *
 * Add or update a user's role on a data source.
 */
export const zAddDataSourceUserMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    userUuid: z.string()
});

/**
 * DataResponse[DataSourceUserMemberResponse]
 */
export const zDataResponseDataSourceUserMemberResponse = z.object({
    data: zDataSourceUserMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UpdateDataSourceMemberRoleRequest
 *
 * Change a data source member's role.
 */
export const zUpdateDataSourceMemberRoleRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/)
});

/**
 * AddDataSourceTeamMemberRequest
 *
 * Add or update a team's role on a data source.
 */
export const zAddDataSourceTeamMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    teamUuid: z.string()
});

/**
 * DataResponse[DataSourceTeamMemberResponse]
 */
export const zDataResponseDataSourceTeamMemberResponse = z.object({
    data: zDataSourceTeamMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * PaginatedResponse[TeamSummary]
 */
export const zPaginatedResponseTeamSummary = z.object({
    data: z.array(zTeamSummary),
    meta: zPaginationMeta
});

/**
 * TeamMemberResponse
 *
 * Team member response schema.
 */
export const zTeamMemberResponse = z.object({
    email: z.string(),
    joinedAt: z.iso.datetime(),
    name: z.string(),
    role: z.string(),
    userUuid: z.string()
});

/**
 * TeamResponse
 *
 * Full team response schema.
 */
export const zTeamResponse = z.object({
    createdAt: z.iso.datetime(),
    description: z.string().nullish(),
    isEveryone: z.boolean(),
    members: z.array(zTeamMemberResponse).optional(),
    name: z.string(),
    settings: z.record(z.string(), z.unknown()).optional(),
    tokenLimit: z.int().nullish(),
    tokensUsed: z.int(),
    uuid: z.string()
});

/**
 * DataResponse[TeamResponse]
 */
export const zDataResponseTeamResponse = z.object({
    data: zTeamResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * TeamCreate
 *
 * Schema for creating a team.
 */
export const zTeamCreate = z.object({
    description: z.string().max(350).nullish(),
    name: z.string().min(1).max(48),
    settings: z.record(z.string(), z.unknown()).optional(),
    tokenLimit: z.int().gte(0).nullish()
});

/**
 * TeamUpdate
 *
 * Schema for updating a team.
 */
export const zTeamUpdate = z.object({
    description: z.string().max(350).nullish(),
    name: z.string().min(1).max(48).nullish(),
    settings: z.record(z.string(), z.unknown()).nullish(),
    tokenLimit: z.int().gte(0).nullish()
});

/**
 * TeamMemberAdd
 *
 * Schema for adding a team member.
 */
export const zTeamMemberAdd = z.object({
    email: z.string().max(320),
    role: z.string().regex(/^(team_admin|member|viewer)$/).optional().default('member')
});

/**
 * DataResponse[TeamMemberResponse]
 */
export const zDataResponseTeamMemberResponse = z.object({
    data: zTeamMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * TeamMemberUpdate
 *
 * Schema for updating a team member's role.
 */
export const zTeamMemberUpdate = z.object({
    role: z.string().regex(/^(team_admin|member|viewer)$/)
});

/**
 * BrandSummary
 *
 * Lightweight brand row for gallery/picker views.
 */
export const zBrandSummary = z.object({
    audience: z.string().nullish(),
    createdAt: z.iso.datetime(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    hasLogo: z.boolean().optional().default(false),
    isDefault: z.boolean().optional().default(false),
    name: z.string(),
    tags: z.array(z.string()).nullish(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * PaginatedResponse[BrandSummary]
 */
export const zPaginatedResponseBrandSummary = z.object({
    data: z.array(zBrandSummary),
    meta: zPaginationMeta
});

/**
 * BrandCreate
 *
 * Create a new brand.
 */
export const zBrandCreate = z.object({
    audience: z.string().max(100).nullish(),
    brandKit: z.record(z.string(), z.unknown()).nullish(),
    brandStyle: z.string().max(2000).nullish(),
    description: z.string().max(2000).nullish(),
    name: z.string().min(1).max(255),
    sourceSessionUuid: z.string().max(64).nullish(),
    tags: z.array(z.string()).nullish(),
    teamUuid: z.string().nullish()
});

/**
 * BrandAssetResponse
 *
 * A brand asset (logo or per-format template).
 */
export const zBrandAssetResponse = z.object({
    filename: z.string().nullish(),
    format: z.string().nullish(),
    kind: z.string(),
    mimeType: z.string(),
    uuid: z.string()
});

/**
 * BrandTemplateSlideSize
 *
 * Slide size in EMU.
 */
export const zBrandTemplateSlideSize = z.object({
    height: z.int(),
    width: z.int()
});

/**
 * BrandTemplateFonts
 *
 * The theme's heading (major) and body (minor) fonts.
 */
export const zBrandTemplateFonts = z.object({
    body: z.string().nullish(),
    heading: z.string().nullish()
});

/**
 * BrandTemplateDetails
 *
 * What was read from a template when it was uploaded.
 *
 * PowerPoint fills ``layouts``, ``master_count``, ``slide_count`` and ``slide_size``; Word fills
 * ``styles``, ``paragraph_count`` and ``has_header``; Excel fills ``sheets``, ``cell_styles`` (the
 * template's own named cell styles), ``value_cell_count`` and ``table_count``. All carry the theme. ``source_kind`` says
 * whether a real template (.potx/.dotx) or a normal file was uploaded.
 */
export const zBrandTemplateDetails = z.object({
    cellStyles: z.array(z.string()).optional(),
    hasHeader: z.boolean().nullish(),
    layouts: z.array(z.string()).optional(),
    masterCount: z.int().nullish(),
    paragraphCount: z.int().nullish(),
    sheets: z.array(z.string()).optional(),
    slideCount: z.int().nullish(),
    slideSize: zBrandTemplateSlideSize.nullish(),
    sourceKind: z.enum(['template', 'document']).nullish(),
    styles: z.array(z.string()).optional(),
    tableCount: z.int().nullish(),
    themeColours: z.record(z.string(), z.string()).optional(),
    themeFonts: zBrandTemplateFonts.optional(),
    valueCellCount: z.int().nullish()
});

/**
 * BrandTemplateResponse
 *
 * A brand's template for one format (one per format; uploading again replaces it).
 */
export const zBrandTemplateResponse = z.object({
    createdAt: z.iso.datetime(),
    details: zBrandTemplateDetails.optional(),
    filename: z.string().nullish(),
    format: z.enum([
        'pptx',
        'docx',
        'xlsx'
    ]),
    hasThumbnail: z.boolean().optional().default(false),
    mimeType: z.string(),
    sizeBytes: z.int().nullish(),
    updatedAt: z.iso.datetime(),
    uploadedByName: z.string().nullish(),
    uuid: z.string()
});

/**
 * BrandResponse
 *
 * Full brand response.
 */
export const zBrandResponse = z.object({
    assets: z.array(zBrandAssetResponse).optional(),
    audience: z.string().nullish(),
    brandKit: z.record(z.string(), z.unknown()).nullish(),
    brandStyle: z.string().nullish(),
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    createdAt: z.iso.datetime(),
    createdByName: z.string().nullish(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    isDefault: z.boolean().optional().default(false),
    logoUrl: z.string().nullish(),
    name: z.string(),
    tags: z.array(z.string()).nullish(),
    teamId: z.int().nullish(),
    templates: z.array(zBrandTemplateResponse).optional(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * DataResponse[BrandResponse]
 */
export const zDataResponseBrandResponse = z.object({
    data: zBrandResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * Body_extract_brand_api_v1_brands_extract_post
 */
export const zBodyExtractBrandApiV1BrandsExtractPost = z.object({
    file: z.string()
});

/**
 * BrandExtractionResponse
 *
 * A non-persisted draft brand extracted from a guidelines document.
 *
 * The frontend pre-fills the create/edit form with these values; nothing is
 * saved until the user reviews and submits the normal brand create/update.
 */
export const zBrandExtractionResponse = z.object({
    audience: z.string().nullish(),
    brandKit: z.record(z.string(), z.unknown()).optional(),
    description: z.string().nullish(),
    guidance: z.string().nullish(),
    logoCandidates: z.array(z.string()).optional(),
    name: z.string().nullish()
});

/**
 * DataResponse[BrandExtractionResponse]
 */
export const zDataResponseBrandExtractionResponse = z.object({
    data: zBrandExtractionResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * BrandUpdate
 *
 * Partial update of a brand's metadata/tokens (editor+).
 */
export const zBrandUpdate = z.object({
    audience: z.string().max(100).nullish(),
    brandKit: z.record(z.string(), z.unknown()).nullish(),
    brandStyle: z.string().max(2000).nullish(),
    description: z.string().max(2000).nullish(),
    name: z.string().min(1).max(255).nullish(),
    tags: z.array(z.string()).nullish()
});

/**
 * Body_upload_brand_logo_api_v1_brands__brand_uuid__logo_post
 */
export const zBodyUploadBrandLogoApiV1BrandsBrandUuidLogoPost = z.object({
    file: z.string()
});

/**
 * DataResponse[list[BrandTemplateResponse]]
 */
export const zDataResponseListBrandTemplateResponse = z.object({
    data: z.array(zBrandTemplateResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * Body_upload_brand_template_api_v1_brands__brand_uuid__templates__template_format__put
 */
export const zBodyUploadBrandTemplateApiV1BrandsBrandUuidTemplatesTemplateFormatPut = z.object({
    file: z.string()
});

/**
 * DataResponse[BrandTemplateResponse]
 */
export const zDataResponseBrandTemplateResponse = z.object({
    data: zBrandTemplateResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * BrandTeamMemberResponse
 *
 * A team granted a role on a brand.
 */
export const zBrandTeamMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    teamName: z.string(),
    teamUuid: z.string()
});

/**
 * BrandUserMemberResponse
 *
 * A user granted a role on a brand.
 */
export const zBrandUserMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    user: zUserResponse
});

/**
 * BrandMembersResponse
 *
 * Combined membership state for a brand plus the caller's effective role.
 */
export const zBrandMembersResponse = z.object({
    canManage: z.boolean(),
    currentUserRole: z.string().nullish(),
    teamMembers: z.array(zBrandTeamMemberResponse).optional(),
    userMembers: z.array(zBrandUserMemberResponse).optional()
});

/**
 * DataResponse[BrandMembersResponse]
 */
export const zDataResponseBrandMembersResponse = z.object({
    data: zBrandMembersResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AddBrandUserMemberRequest
 *
 * Add or update a user's role on a brand.
 */
export const zAddBrandUserMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    userUuid: z.string()
});

/**
 * DataResponse[BrandUserMemberResponse]
 */
export const zDataResponseBrandUserMemberResponse = z.object({
    data: zBrandUserMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UpdateBrandMemberRoleRequest
 *
 * Change an existing member's role.
 */
export const zUpdateBrandMemberRoleRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/)
});

/**
 * AddBrandTeamMemberRequest
 *
 * Add or update a team's role on a brand.
 */
export const zAddBrandTeamMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    teamUuid: z.string()
});

/**
 * DataResponse[BrandTeamMemberResponse]
 */
export const zDataResponseBrandTeamMemberResponse = z.object({
    data: zBrandTeamMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * FlowSummary
 *
 * Summary flow schema for lists.
 */
export const zFlowSummary = z.object({
    createdAt: z.iso.datetime(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    executionCount: z.int(),
    hasActiveSchedule: z.boolean().optional().default(false),
    name: z.string(),
    nextRunAt: z.iso.datetime().nullish(),
    stepCount: z.int(),
    uuid: z.string()
});

/**
 * PaginatedResponse[FlowSummary]
 */
export const zPaginatedResponseFlowSummary = z.object({
    data: z.array(zFlowSummary),
    meta: zPaginationMeta
});

/**
 * FlowConnectorConfig
 *
 * Configuration for a flow connector (edge between steps).
 */
export const zFlowConnectorConfig = z.object({
    fromStepOrder: z.int().nullish(),
    label: z.string().max(255).nullish(),
    toStepOrder: z.int()
});

/**
 * FlowStepConfig
 *
 * Configuration for a flow step.
 */
export const zFlowStepConfig = z.object({
    agentUuid: z.string().nullish(),
    config: z.record(z.string(), z.unknown()).optional(),
    expectedOutput: z.string().max(10000).nullish(),
    instructions: z.string().max(50000).nullish(),
    requiresReview: z.boolean().optional().default(false),
    stepType: z.string().regex(/^(agent|prompt|notification|artifact|webhook_out)$/).optional().default('agent')
});

/**
 * FlowCreate
 *
 * Schema for creating a flow.
 */
export const zFlowCreate = z.object({
    connectors: z.array(zFlowConnectorConfig).optional(),
    description: z.string().max(5000).nullish(),
    name: z.string().min(1).max(255),
    purpose: z.string().max(10000).nullish(),
    sourceSessionUuid: z.string().max(64).nullish(),
    steps: z.array(zFlowStepConfig).optional(),
    teamUuid: z.string().nullish()
});

/**
 * FlowConnectorResponse
 *
 * Flow connector response schema.
 */
export const zFlowConnectorResponse = z.object({
    fromStepId: z.int().nullish(),
    id: z.int(),
    label: z.string().nullish(),
    toStepId: z.int()
});

/**
 * FlowInputResponse
 *
 * Flow input response schema.
 */
export const zFlowInputResponse = z.object({
    config: z.record(z.string(), z.unknown()).optional(),
    description: z.string().nullish(),
    inputType: z.string(),
    isRequired: z.boolean(),
    name: z.string(),
    uuid: z.string()
});

/**
 * FlowPreapprovedAction
 *
 * A write action this workflow's runs take without pausing (MHS-972): one tool, to one destination.
 */
export const zFlowPreapprovedAction = z.object({
    addedAt: z.iso.datetime().nullish(),
    id: z.string(),
    label: z.string(),
    tool: z.string()
});

/**
 * FlowStepResponse
 *
 * Flow step response schema.
 */
export const zFlowStepResponse = z.object({
    agentName: z.string().nullish(),
    agentUuid: z.string().nullish(),
    config: z.record(z.string(), z.unknown()).optional(),
    expectedOutput: z.string().nullish(),
    id: z.int(),
    instructions: z.string().nullish(),
    requiresReview: z.boolean(),
    stepOrder: z.int(),
    stepType: z.string().optional().default('agent')
});

/**
 * FlowTriggerSummary
 *
 * Summary trigger schema for lists.
 */
export const zFlowTriggerSummary = z.object({
    isEnabled: z.boolean(),
    lastTriggeredAt: z.iso.datetime().nullish(),
    name: z.string(),
    triggerType: z.string(),
    uuid: z.string()
});

/**
 * FlowResponse
 *
 * Full flow response schema.
 */
export const zFlowResponse = z.object({
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    connectors: z.array(zFlowConnectorResponse).optional(),
    createdAt: z.iso.datetime(),
    createdByUuid: z.string(),
    currentUserRole: z.string().nullish(),
    defaultFileBlobPaths: z.array(z.string()).nullish(),
    defaultInputPrompt: z.string().nullish(),
    description: z.string().nullish(),
    executionCount: z.int(),
    inputs: z.array(zFlowInputResponse).optional(),
    name: z.string(),
    preapprovedActions: z.array(zFlowPreapprovedAction).optional(),
    purpose: z.string().nullish(),
    steps: z.array(zFlowStepResponse).optional(),
    teamUuid: z.string().nullish(),
    triggers: z.array(zFlowTriggerSummary).optional(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * DataResponse[FlowResponse]
 */
export const zDataResponseFlowResponse = z.object({
    data: zFlowResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * FlowUpdate
 *
 * Schema for updating a flow.
 */
export const zFlowUpdate = z.object({
    connectors: z.array(zFlowConnectorConfig).nullish(),
    description: z.string().max(5000).nullish(),
    name: z.string().min(1).max(255).nullish(),
    purpose: z.string().max(10000).nullish(),
    steps: z.array(zFlowStepConfig).nullish(),
    teamUuid: z.string().nullish()
});

/**
 * FlowTeamMemberResponse
 *
 * A team granted a role on a workflow.
 */
export const zFlowTeamMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    teamName: z.string(),
    teamUuid: z.string()
});

/**
 * FlowUserMemberResponse
 *
 * A user granted a role on a workflow.
 */
export const zFlowUserMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    user: zUserResponse
});

/**
 * FlowMembersResponse
 *
 * Membership state for a workflow plus the caller's effective role.
 */
export const zFlowMembersResponse = z.object({
    canManage: z.boolean(),
    currentUserRole: z.string().nullish(),
    teamMembers: z.array(zFlowTeamMemberResponse).optional(),
    userMembers: z.array(zFlowUserMemberResponse).optional()
});

/**
 * DataResponse[FlowMembersResponse]
 */
export const zDataResponseFlowMembersResponse = z.object({
    data: zFlowMembersResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AddFlowUserMemberRequest
 *
 * Add or update a user's role on a workflow.
 */
export const zAddFlowUserMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    userUuid: z.string()
});

/**
 * DataResponse[FlowUserMemberResponse]
 */
export const zDataResponseFlowUserMemberResponse = z.object({
    data: zFlowUserMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UpdateFlowMemberRoleRequest
 *
 * Change a workflow member's role.
 */
export const zUpdateFlowMemberRoleRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/)
});

/**
 * AddFlowTeamMemberRequest
 *
 * Add or update a team's role on a workflow.
 */
export const zAddFlowTeamMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    teamUuid: z.string()
});

/**
 * DataResponse[FlowTeamMemberResponse]
 */
export const zDataResponseFlowTeamMemberResponse = z.object({
    data: zFlowTeamMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * FlowRunSummary
 *
 * Summary flow run schema for lists.
 */
export const zFlowRunSummary = z.object({
    completedAt: z.iso.datetime().nullish(),
    createdAt: z.iso.datetime(),
    errorMessage: z.string().nullish(),
    startedAt: z.iso.datetime().nullish(),
    status: z.string(),
    totalTokens: z.int(),
    triggerType: z.string(),
    uuid: z.string()
});

/**
 * PaginatedResponse[FlowRunSummary]
 */
export const zPaginatedResponseFlowRunSummary = z.object({
    data: z.array(zFlowRunSummary),
    meta: zPaginationMeta
});

/**
 * FlowRunStepAttachment
 *
 * Attachment produced during a flow run step.
 */
export const zFlowRunStepAttachment = z.object({
    downloadUrl: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    size: z.int(),
    uuid: z.string()
});

/**
 * FlowRunStepResponse
 *
 * Flow run step response schema.
 */
export const zFlowRunStepResponse = z.object({
    agentName: z.string().nullish(),
    agentUuid: z.string().nullish(),
    attachments: z.array(zFlowRunStepAttachment).optional(),
    completedAt: z.iso.datetime().nullish(),
    errorMessage: z.string().nullish(),
    id: z.int(),
    inputContent: z.string().nullish(),
    label: z.string().nullish(),
    outputContent: z.string().nullish(),
    outputStructured: z.record(z.string(), z.unknown()).nullish(),
    sideEffects: z.record(z.string(), z.unknown()).nullish(),
    startedAt: z.iso.datetime().nullish(),
    status: z.string(),
    stepNumber: z.int(),
    stepType: z.string().nullish(),
    tokensUsed: z.int()
});

/**
 * FlowRunResponse
 *
 * Full flow run response schema.
 */
export const zFlowRunResponse = z.object({
    completedAt: z.iso.datetime().nullish(),
    createdAt: z.iso.datetime(),
    createdByUuid: z.string(),
    errorMessage: z.string().nullish(),
    flowUuid: z.string(),
    inputPrompt: z.string(),
    startedAt: z.iso.datetime().nullish(),
    status: z.string(),
    steps: z.array(zFlowRunStepResponse).optional(),
    totalTokens: z.int(),
    triggerType: z.string(),
    uuid: z.string()
});

/**
 * DataResponse[FlowRunResponse]
 */
export const zDataResponseFlowRunResponse = z.object({
    data: zFlowRunResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * FlowRunCreate
 *
 * Schema for creating/starting a flow run.
 */
export const zFlowRunCreate = z.object({
    fileBlobPaths: z.array(z.string()).optional(),
    inputPrompt: z.string().max(100000).optional().default(''),
    inputValues: z.record(z.string(), z.string()).optional(),
    triggerUuid: z.string().nullish()
});

/**
 * Body_upload_flow_files_api_v1_flows__flow_uuid__upload_post
 */
export const zBodyUploadFlowFilesApiV1FlowsFlowUuidUploadPost = z.object({
    files: z.array(z.string())
});

/**
 * _UploadedFlowFile
 */
export const zUploadedFlowFile = z.object({
    blob_path: z.string(),
    filename: z.string(),
    mime_type: z.string(),
    size: z.int()
});

/**
 * _FlowFilesUploaded
 *
 * Documents the upload response's wire keys (snake_case, as the old UI reads them).
 */
export const zFlowFilesUploaded = z.object({
    files: z.array(zUploadedFlowFile)
});

/**
 * FlowRunResumeRequest
 *
 * Schema for resuming a paused flow run.
 */
export const zFlowRunResumeRequest = z.object({
    approval: z.boolean().optional().default(true),
    modifiedOutput: z.string().max(100000).nullish()
});

/**
 * FlowRunCancelRequest
 *
 * Schema for cancelling a flow run.
 */
export const zFlowRunCancelRequest = z.object({
    reason: z.string().max(1000).nullish()
});

/**
 * FlowScheduleResponse
 *
 * Flow schedule response schema.
 */
export const zFlowScheduleResponse = z.object({
    daysOfWeek: z.array(z.int()).nullish(),
    intervalDays: z.int().nullish(),
    nextRunAt: z.iso.datetime().nullish(),
    runAtTime: z.string(),
    scheduleType: z.string(),
    startDate: z.iso.date().nullish(),
    timezone: z.string(),
    uuid: z.string()
});

/**
 * WebhookRegistrationResponse
 *
 * Response schema for an inbound webhook registration.
 */
export const zWebhookRegistrationResponse = z.object({
    createdAt: z.iso.datetime(),
    isActive: z.boolean(),
    lastReceivedAt: z.iso.datetime().nullish(),
    receiveCount: z.int().optional().default(0),
    slug: z.string(),
    uuid: z.string(),
    webhookUrl: z.string().nullish()
});

/**
 * FlowTriggerResponse
 *
 * Full trigger response schema.
 */
export const zFlowTriggerResponse = z.object({
    config: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.iso.datetime(),
    description: z.string().nullish(),
    isEnabled: z.boolean(),
    lastTriggeredAt: z.iso.datetime().nullish(),
    name: z.string(),
    schedule: zFlowScheduleResponse.nullish(),
    triggerType: z.string(),
    uuid: z.string(),
    webhook: zWebhookRegistrationResponse.nullish()
});

/**
 * DataResponse[list[FlowTriggerResponse]]
 */
export const zDataResponseListFlowTriggerResponse = z.object({
    data: z.array(zFlowTriggerResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * FlowScheduleCreate
 *
 * Schema for creating a schedule.
 */
export const zFlowScheduleCreate = z.object({
    daysOfWeek: z.array(z.int()).nullish(),
    intervalDays: z.int().gte(1).nullish(),
    runAtTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/).optional().default('09:00'),
    scheduleType: z.string().regex(/^(daily|weekly|custom)$/),
    startDate: z.iso.date().nullish(),
    timezone: z.string().max(100).optional().default('UTC')
});

/**
 * FlowTriggerCreate
 *
 * Schema for creating a trigger.
 */
export const zFlowTriggerCreate = z.object({
    config: z.record(z.string(), z.unknown()).optional(),
    description: z.string().max(5000).nullish(),
    name: z.string().min(1).max(255),
    schedule: zFlowScheduleCreate.nullish(),
    triggerType: z.string().regex(/^(manual|scheduled|webhook)$/)
});

/**
 * FlowTriggerUpdate
 *
 * Schema for updating a trigger's schedule.
 */
export const zFlowTriggerUpdate = z.object({
    config: z.record(z.string(), z.unknown()).nullish(),
    description: z.string().max(5000).nullish(),
    name: z.string().min(1).max(255).nullish(),
    schedule: zFlowScheduleCreate.nullish()
});

/**
 * DataResponse[FlowTriggerResponse]
 */
export const zDataResponseFlowTriggerResponse = z.object({
    data: zFlowTriggerResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * FlowAuthorizationResponse
 *
 * Delegated background-access authorization status for scheduled flows.
 *
 * The ``scheduled_flows`` delegated token is user-scoped (shared across all of
 * the user's scheduled flows), so ``status`` reflects the user's overall
 * scheduled-flow authorization, not a single flow. ``has_scheduled_trigger``
 * tells the UI whether this particular flow has a schedule that would use it.
 */
export const zFlowAuthorizationResponse = z.object({
    cachingEnabled: z.boolean(),
    consent: z.string().optional().default('session'),
    googleScopes: z.array(z.string()).optional().default([]),
    hasScheduledTrigger: z.boolean(),
    status: z.string()
});

/**
 * DataResponse[FlowAuthorizationResponse]
 */
export const zDataResponseFlowAuthorizationResponse = z.object({
    data: zFlowAuthorizationResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * FlowAuthorizationRequest
 *
 * Body of POST /flows/{uuid}/authorization. Google deployments send the consent popup's code.
 */
export const zFlowAuthorizationRequest = z.object({
    googleCode: z.string().max(2048).nullish()
});

/**
 * MicroAppSummary
 *
 * Lightweight micro app row for gallery/list views.
 */
export const zMicroAppSummary = z.object({
    appType: z.string(),
    color: z.string(),
    createdAt: z.iso.datetime(),
    createdByName: z.string().nullish(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    icon: z.string(),
    isShared: z.boolean().optional().default(false),
    name: z.string(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * PaginatedResponse[MicroAppSummary]
 */
export const zPaginatedResponseMicroAppSummary = z.object({
    data: z.array(zMicroAppSummary),
    meta: zPaginationMeta
});

/**
 * MicroAppResponse
 *
 * Full micro app response.
 */
export const zMicroAppResponse = z.object({
    about: z.string().nullish(),
    appType: z.string(),
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    color: z.string(),
    createdAt: z.iso.datetime(),
    createdByName: z.string().nullish(),
    currentUserRole: z.string().nullish(),
    currentVersion: z.int().optional().default(1),
    description: z.string().nullish(),
    icon: z.string(),
    mimeType: z.string(),
    name: z.string(),
    sourceSessionUuid: z.string().nullish(),
    teamId: z.int().nullish(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * DataResponse[MicroAppResponse]
 */
export const zDataResponseMicroAppResponse = z.object({
    data: zMicroAppResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * MicroAppUpdate
 *
 * Partial update of a micro app's metadata.
 */
export const zMicroAppUpdate = z.object({
    color: z.string().max(7).nullish(),
    description: z.string().max(2000).nullish(),
    icon: z.string().max(50).nullish(),
    name: z.string().max(255).nullish()
});

/**
 * MicroAppDataSourceInfo
 *
 * A distinct data source behind a dashboard (for the Info panel).
 */
export const zMicroAppDataSourceInfo = z.object({
    bindingCount: z.int(),
    label: z.string(),
    provider: z.string()
});

/**
 * MicroAppInfo
 *
 * Composed end-user metadata for a micro app's 'Info / About' panel.
 *
 * Generalizes provenance + authorship + identity into one payload so viewers
 * understand what they're looking at, who made it, when, and where its data
 * comes from. Access summary is intentionally deferred to a follow-up.
 */
export const zMicroAppInfo = z.object({
    about: z.string().nullish(),
    appType: z.string(),
    authSummary: z.string().nullish(),
    color: z.string(),
    createdByName: z.string().nullish(),
    dataOwnerName: z.string().nullish(),
    dataSources: z.array(zMicroAppDataSourceInfo).optional().default([]),
    description: z.string().nullish(),
    generatedFromChat: z.boolean().optional().default(false),
    icon: z.string(),
    isLive: z.boolean().optional().default(false),
    lastUpdated: z.iso.datetime().nullish(),
    name: z.string(),
    publishedAt: z.iso.datetime(),
    refreshLabel: z.string().nullish(),
    refreshSeconds: z.int().nullish(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * DataResponse[MicroAppInfo]
 */
export const zDataResponseMicroAppInfo = z.object({
    data: zMicroAppInfo,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * MicroAppSourceModeOption
 *
 * One data source a dashboard binds to, with the modes the author may pick.
 */
export const zMicroAppSourceModeOption = z.object({
    bindingKeys: z.array(z.string()),
    currentMode: z.string(),
    label: z.string(),
    provider: z.string(),
    supportedModes: z.array(z.string())
});

/**
 * MicroAppBindingsPreview
 *
 * A pre-publish read of an attachment's data bindings, grouped per source.
 */
export const zMicroAppBindingsPreview = z.object({
    isLive: z.boolean(),
    sources: z.array(zMicroAppSourceModeOption)
});

/**
 * DataResponse[MicroAppBindingsPreview]
 */
export const zDataResponseMicroAppBindingsPreview = z.object({
    data: zMicroAppBindingsPreview,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * MicroAppPublish
 *
 * Request to publish an existing HTML attachment as a micro app.
 */
export const zMicroAppPublish = z.object({
    asNewApp: z.boolean().optional().default(false),
    attachmentUuid: z.string(),
    authModeOverrides: z.record(z.string(), z.string()).nullish(),
    description: z.string().max(2000).nullish(),
    name: z.string().max(255).nullish(),
    note: z.string().max(2000).nullish()
});

/**
 * MicroAppRepublish
 *
 * Request to replace a published micro app's content from a new attachment.
 */
export const zMicroAppRepublish = z.object({
    attachmentUuid: z.string(),
    authModeOverrides: z.record(z.string(), z.string()).nullish(),
    note: z.string().max(2000).nullish()
});

/**
 * MicroAppTeamMemberResponse
 *
 * A team granted a role on a micro app.
 */
export const zMicroAppTeamMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    teamName: z.string(),
    teamUuid: z.string()
});

/**
 * MicroAppUserMemberResponse
 *
 * A user granted a role on a micro app.
 */
export const zMicroAppUserMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    user: zUserResponse
});

/**
 * MicroAppMembersResponse
 *
 * Combined membership state for a micro app plus the caller's effective role.
 */
export const zMicroAppMembersResponse = z.object({
    canManage: z.boolean(),
    currentUserRole: z.string().nullish(),
    spaceGrants: z.array(zSpaceGrantResponse).optional(),
    teamMembers: z.array(zMicroAppTeamMemberResponse).optional(),
    userMembers: z.array(zMicroAppUserMemberResponse).optional()
});

/**
 * DataResponse[MicroAppMembersResponse]
 */
export const zDataResponseMicroAppMembersResponse = z.object({
    data: zMicroAppMembersResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AddMicroAppUserMemberRequest
 *
 * Add or update a user's role on a micro app.
 */
export const zAddMicroAppUserMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    userUuid: z.string()
});

/**
 * DataResponse[MicroAppUserMemberResponse]
 */
export const zDataResponseMicroAppUserMemberResponse = z.object({
    data: zMicroAppUserMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UpdateMicroAppMemberRoleRequest
 *
 * Change an existing member's role.
 */
export const zUpdateMicroAppMemberRoleRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/)
});

/**
 * AddMicroAppTeamMemberRequest
 *
 * Add or update a team's role on a micro app.
 */
export const zAddMicroAppTeamMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    teamUuid: z.string()
});

/**
 * DataResponse[MicroAppTeamMemberResponse]
 */
export const zDataResponseMicroAppTeamMemberResponse = z.object({
    data: zMicroAppTeamMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UserSearchResult
 *
 * Lightweight user info for search results.
 */
export const zUserSearchResult = z.object({
    avatarUrl: z.string().nullish(),
    email: z.string(),
    name: z.string(),
    uuid: z.string()
});

/**
 * DataResponse[list[UserSearchResult]]
 */
export const zDataResponseListUserSearchResult = z.object({
    data: z.array(zUserSearchResult),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SharePointSite
 *
 * Minimal site representation returned to the frontend.
 */
export const zSharePointSite = z.object({
    description: z.string().nullish(),
    display_name: z.string(),
    id: z.string(),
    is_personal_site: z.boolean().optional().default(false),
    site_type: z.string().optional().default('other'),
    web_url: z.string()
});

/**
 * SharePointDrive
 *
 * Document library / drive.
 */
export const zSharePointDrive = z.object({
    description: z.string().nullish(),
    drive_type: z.string().nullish(),
    id: z.string(),
    item_count: z.int().nullish(),
    name: z.string(),
    web_url: z.string()
});

/**
 * SharePointItem
 *
 * File or folder inside a drive.
 */
export const zSharePointItem = z.object({
    child_count: z.int().nullish(),
    drive_id: z.string().nullish(),
    id: z.string(),
    is_folder: z.boolean().optional().default(false),
    last_modified: z.string().nullish(),
    mime_type: z.string().nullish(),
    name: z.string(),
    size: z.int().nullish(),
    web_url: z.string()
});

/**
 * SharePointBrowseResponse
 *
 * Response for any browse endpoint.
 */
export const zSharePointBrowseResponse = z.object({
    drive: zSharePointDrive.nullish(),
    has_more: z.boolean().optional().default(false),
    items: z.array(zSharePointItem).optional(),
    site: zSharePointSite.nullish(),
    total: z.int().nullish()
});

/**
 * SharePointFolderCountResponse
 *
 * Response for folder item count endpoint.
 */
export const zSharePointFolderCountResponse = z.object({
    file_count: z.int(),
    recursive: z.boolean().optional().default(false),
    total_count: z.int(),
    truncated: z.boolean().optional().default(false)
});

/**
 * PaginatedResponse[UserResponse]
 */
export const zPaginatedResponseUserResponse = z.object({
    data: z.array(zUserResponse),
    meta: zPaginationMeta
});

/**
 * UserCreate
 *
 * Schema for pre-provisioning a user before their first login.
 */
export const zUserCreate = z.object({
    email: z.email(),
    name: z.string().min(1).max(255),
    role: z.string().regex(/^(install_admin|platform_admin|member)$/).optional().default('member'),
    teamUuids: z.array(z.string()).optional()
});

/**
 * AIModelResponse
 *
 * Full AI model response schema.
 */
export const zAiModelResponse = z.object({
    agentCount: z.int().nullish(),
    apiKeyEnv: z.string().nullish(),
    apiProtocol: z.string().optional().default('openai_responses'),
    capabilities: z.array(z.string()).optional(),
    chatCount: z.int().nullish(),
    contextWindow: z.int(),
    costPer1kInput: z.number(),
    costPer1kOutput: z.number(),
    costTier: z.string().nullish(),
    createdAt: z.iso.datetime(),
    deploymentName: z.string(),
    description: z.string().nullish(),
    displayOrder: z.int(),
    endpointUrl: z.string().nullish(),
    hasApiKey: z.boolean().optional().default(false),
    hosting: z.string().optional().default('azure_openai_eastus2'),
    id: z.string(),
    isDeprecated: z.boolean().optional().default(false),
    isEnabled: z.boolean(),
    isReasoning: z.boolean().optional().default(false),
    maxOutputTokens: z.int(),
    modelType: z.string().optional().default('chat'),
    name: z.string(),
    provider: z.string(),
    reasoningConfig: zReasoningConfig.nullish(),
    replacementModelId: z.string().nullish(),
    sunsetDate: z.iso.date().nullish(),
    supportsCodeInterpreter: z.boolean().optional().default(false),
    supportsFunctionCalling: z.boolean().optional().default(true),
    supportsStreaming: z.boolean().optional().default(true),
    supportsVision: z.boolean().optional().default(false),
    updatedAt: z.iso.datetime()
});

/**
 * PaginatedResponse[AIModelResponse]
 */
export const zPaginatedResponseAiModelResponse = z.object({
    data: z.array(zAiModelResponse),
    meta: zPaginationMeta
});

/**
 * DataResponse[AIModelResponse]
 */
export const zDataResponseAiModelResponse = z.object({
    data: zAiModelResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AIModelCreate
 *
 * Schema for creating an AI model (admin only).
 */
export const zAiModelCreate = z.object({
    apiKeyEnv: z.string().max(100).nullish(),
    apiProtocol: z.string().regex(/^(openai_responses|openai_completions|anthropic_messages)$/).optional().default('openai_responses'),
    capabilities: z.array(z.string()).optional(),
    contextWindow: z.int().gte(1),
    costPer1KInput: z.number().gte(0),
    costPer1KOutput: z.number().gte(0),
    costTier: z.string().max(3).regex(/^\$+$/).nullish(),
    deploymentName: z.string().min(1).max(100),
    description: z.string().max(500).nullish(),
    displayOrder: z.int().optional().default(0),
    endpointUrl: z.string().max(500).nullish(),
    hosting: z.string().regex(/^(azure_openai_eastus2|azure_openai_westus|foundry_eastus2|custom)$/).optional().default('azure_openai_eastus2'),
    id: z.string().min(1).max(50),
    isEnabled: z.boolean().optional().default(true),
    isReasoning: z.boolean().optional().default(false),
    maxOutputTokens: z.int().gte(1),
    modelType: z.string().regex(/^(chat|embedding|image|audio|research)$/).optional().default('chat'),
    name: z.string().min(1).max(100),
    provider: z.string().regex(/^(azure_openai|azure_foundry|azure_ai_foundry|anthropic_foundry)$/).nullish(),
    reasoningConfig: zReasoningConfig.nullish(),
    supportsCodeInterpreter: z.boolean().optional().default(false),
    supportsFunctionCalling: z.boolean().optional().default(true),
    supportsStreaming: z.boolean().optional().default(true),
    supportsVision: z.boolean().optional().default(false)
});

/**
 * AIModelSuggestRequest
 *
 * Request schema for AI-assisted model configuration.
 */
export const zAiModelSuggestRequest = z.object({
    modelName: z.string().min(1).max(100)
});

/**
 * AIModelSuggestResponse
 *
 * AI-suggested configuration for a model.
 */
export const zAiModelSuggestResponse = z.object({
    apiProtocol: z.string().optional().default('openai_responses'),
    capabilities: z.array(z.string()).optional(),
    contextWindow: z.int(),
    costPer1kInput: z.number(),
    costPer1kOutput: z.number(),
    deploymentName: z.string(),
    displayOrder: z.int().optional().default(0),
    hosting: z.string().optional().default('azure_openai_eastus2'),
    id: z.string(),
    isEnabled: z.boolean().optional().default(true),
    isReasoning: z.boolean().optional().default(false),
    maxOutputTokens: z.int(),
    modelType: z.string().optional().default('chat'),
    name: z.string(),
    notes: z.string().optional().default(''),
    provider: z.string().optional().default('azure_openai'),
    reasoningConfig: zReasoningConfig.nullish(),
    supportsCodeInterpreter: z.boolean().optional().default(false),
    supportsFunctionCalling: z.boolean().optional().default(true),
    supportsStreaming: z.boolean().optional().default(true),
    supportsVision: z.boolean().optional().default(false)
});

/**
 * DataResponse[AIModelSuggestResponse]
 */
export const zDataResponseAiModelSuggestResponse = z.object({
    data: zAiModelSuggestResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ModelTierDefaultUpdate
 *
 * Update the model assignment for a single tier.
 */
export const zModelTierDefaultUpdate = z.object({
    modelId: z.string().max(50).nullish()
});

/**
 * DataResponse[ModelTierDefaultResponse]
 */
export const zDataResponseModelTierDefaultResponse = z.object({
    data: zModelTierDefaultResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AIModelUpdate
 *
 * Schema for updating an AI model (admin only).
 */
export const zAiModelUpdate = z.object({
    apiKeyEnv: z.string().max(100).nullish(),
    apiKeyValue: z.string().max(500).nullish(),
    apiProtocol: z.string().regex(/^(openai_responses|openai_completions|anthropic_messages)$/).nullish(),
    capabilities: z.array(z.string()).nullish(),
    clearApiKey: z.boolean().nullish(),
    contextWindow: z.int().gte(0).nullish(),
    costPer1KInput: z.number().gte(0).nullish(),
    costPer1KOutput: z.number().gte(0).nullish(),
    costTier: z.string().max(3).regex(/^\$+$/).nullish(),
    deploymentName: z.string().min(1).max(100).nullish(),
    description: z.string().max(500).nullish(),
    displayOrder: z.int().nullish(),
    endpointUrl: z.string().max(500).nullish(),
    hosting: z.string().regex(/^(azure_openai_eastus2|azure_openai_westus|foundry_eastus2|custom)$/).nullish(),
    isDeprecated: z.boolean().nullish(),
    isEnabled: z.boolean().nullish(),
    isReasoning: z.boolean().nullish(),
    maxOutputTokens: z.int().gte(0).nullish(),
    modelType: z.string().regex(/^(chat|embedding|image|audio|research)$/).nullish(),
    name: z.string().min(1).max(100).nullish(),
    provider: z.string().regex(/^(azure_openai|azure_foundry|azure_ai_foundry|anthropic_foundry)$/).nullish(),
    reasoningConfig: zReasoningConfig.nullish(),
    replacementModelId: z.string().min(1).max(50).nullish(),
    sunsetDate: z.iso.date().nullish(),
    supportsCodeInterpreter: z.boolean().nullish(),
    supportsFunctionCalling: z.boolean().nullish(),
    supportsStreaming: z.boolean().nullish(),
    supportsVision: z.boolean().nullish()
});

/**
 * AffectedEntitySummary
 *
 * Summary of an entity affected by a change (e.g. a model retirement).
 */
export const zAffectedEntitySummary = z.object({
    entityType: z.string(),
    name: z.string(),
    uuid: z.string()
});

/**
 * ModelReferencesResponse
 *
 * Live agents and chats that still use a model.
 */
export const zModelReferencesResponse = z.object({
    affectedAgents: z.array(zAffectedEntitySummary).optional(),
    chatCount: z.int().optional().default(0)
});

/**
 * DataResponse[ModelReferencesResponse]
 */
export const zDataResponseModelReferencesResponse = z.object({
    data: zModelReferencesResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ModelReassignRequest
 *
 * Move a model's agents and chats to another model.
 */
export const zModelReassignRequest = z.object({
    replacementModelId: z.string().min(1).max(50)
});

/**
 * UsageByFeature
 *
 * Usage breakdown by feature.
 */
export const zUsageByFeature = z.object({
    estimatedCostUsd: z.number(),
    feature: z.string(),
    requests: z.int(),
    tokensInput: z.int(),
    tokensOutput: z.int()
});

/**
 * UsageByModel
 *
 * Usage breakdown by model.
 */
export const zUsageByModel = z.object({
    estimatedCostUsd: z.number(),
    modelId: z.string(),
    modelName: z.string(),
    requests: z.int(),
    tokensInput: z.int(),
    tokensOutput: z.int()
});

/**
 * UsageByTeam
 *
 * Usage breakdown by team.
 */
export const zUsageByTeam = z.object({
    estimatedCostUsd: z.number(),
    requests: z.int(),
    teamName: z.string(),
    teamUuid: z.string(),
    tokensInput: z.int(),
    tokensOutput: z.int()
});

/**
 * TopAgentEntry
 *
 * An agent in the top-N agents list.
 */
export const zTopAgentEntry = z.object({
    agentId: z.int().nullish(),
    agentName: z.string(),
    uses: z.int()
});

/**
 * TopToolEntry
 *
 * A tool in the top-N tools list.
 */
export const zTopToolEntry = z.object({
    executions: z.int(),
    successRate: z.number(),
    toolName: z.string()
});

/**
 * UsageStats
 *
 * Usage statistics schema.
 */
export const zUsageStats = z.object({
    activeUsers: z.int().optional().default(0),
    estimatedCostUsd: z.number().optional().default(0),
    requests: z.int().optional().default(0),
    tokensInput: z.int().optional().default(0),
    tokensOutput: z.int().optional().default(0)
});

/**
 * UsageResponse
 *
 * Full usage analytics response.
 */
export const zUsageResponse = z.object({
    byFeature: z.array(zUsageByFeature).optional(),
    byModel: z.array(zUsageByModel).optional(),
    byTeam: z.array(zUsageByTeam).optional(),
    topAgents: z.array(zTopAgentEntry).optional(),
    topTools: z.array(zTopToolEntry).optional(),
    totals: zUsageStats
});

/**
 * DataResponse[UsageResponse]
 */
export const zDataResponseUsageResponse = z.object({
    data: zUsageResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ActionSummaryRow
 *
 * How one tool's write actions went (MHS-973): counts only, never who or what.
 */
export const zActionSummaryRow = z.object({
    declined: z.int().optional().default(0),
    executed: z.int().optional().default(0),
    expired: z.int().optional().default(0),
    failed: z.int().optional().default(0),
    integration: z.string().nullish(),
    limited: z.int().optional().default(0),
    pending: z.int().optional().default(0),
    proposed: z.int().optional().default(0),
    superseded: z.int().optional().default(0),
    surface: z.enum(['chat', 'flow']),
    tool: z.string()
});

/**
 * DataResponse[list[ActionSummaryRow]]
 */
export const zDataResponseListActionSummaryRow = z.object({
    data: z.array(zActionSummaryRow),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UserSpendRow
 *
 * Per-user spend row for admin view.
 */
export const zUserSpendRow = z.object({
    costUsd: z.number(),
    requests: z.int(),
    tokensInput: z.int(),
    tokensOutput: z.int(),
    userEmail: z.string(),
    userId: z.int(),
    userName: z.string()
});

/**
 * UserSpendResponse
 *
 * Admin per-user spend response with pagination.
 */
export const zUserSpendResponse = z.object({
    hasMore: z.boolean().optional().default(false),
    totalCount: z.int().optional().default(0),
    users: z.array(zUserSpendRow).optional()
});

/**
 * DataResponse[UserSpendResponse]
 */
export const zDataResponseUserSpendResponse = z.object({
    data: zUserSpendResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * BudgetConfigResponse
 *
 * Schema for returning a budget configuration.
 */
export const zBudgetConfigResponse = z.object({
    createdAt: z.iso.datetime(),
    createdById: z.int().nullish(),
    id: z.int(),
    monthlyBudgetUsd: z.number(),
    scope: z.string(),
    teamId: z.int().nullish(),
    teamName: z.string().nullish(),
    updatedAt: z.iso.datetime(),
    userEmail: z.string().nullish(),
    userId: z.int().nullish(),
    userName: z.string().nullish()
});

/**
 * DataResponse[list[BudgetConfigResponse]]
 */
export const zDataResponseListBudgetConfigResponse = z.object({
    data: z.array(zBudgetConfigResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * BudgetConfigCreate
 *
 * Schema for creating a budget configuration.
 */
export const zBudgetConfigCreate = z.object({
    monthlyBudgetUsd: z.number().gt(0),
    scope: z.enum([
        'platform',
        'user',
        'team'
    ]),
    teamUuid: z.string().nullish(),
    userUuid: z.string().nullish()
});

/**
 * DataResponse[BudgetConfigResponse]
 */
export const zDataResponseBudgetConfigResponse = z.object({
    data: zBudgetConfigResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * BudgetConfigUpdate
 *
 * Schema for updating a budget configuration.
 */
export const zBudgetConfigUpdate = z.object({
    monthlyBudgetUsd: z.number().gt(0)
});

/**
 * ResourceGroupCost
 *
 * Cost for a single Azure resource group.
 */
export const zResourceGroupCost = z.object({
    costUsd: z.number(),
    resourceGroup: z.string()
});

/**
 * ServiceCost
 *
 * Cost for a service within a resource group.
 */
export const zServiceCost = z.object({
    costUsd: z.number(),
    resourceGroup: z.string(),
    serviceName: z.string()
});

/**
 * DailyCost
 *
 * Aggregated daily cost across all Mutual resource groups.
 */
export const zDailyCost = z.object({
    costUsd: z.number(),
    date: z.string()
});

/**
 * PlatformCostsResponse
 *
 * The complete platform cost dashboard payload.
 */
export const zPlatformCostsResponse = z.object({
    byResourceGroup: z.array(zResourceGroupCost).optional().default([]),
    byService: z.array(zServiceCost).optional().default([]),
    configured: z.boolean(),
    dailyTrend: z.array(zDailyCost).optional().default([]),
    message: z.string().nullish(),
    totalCostUsd: z.number().optional().default(0)
});

/**
 * DataResponse[PlatformCostsResponse]
 */
export const zDataResponsePlatformCostsResponse = z.object({
    data: zPlatformCostsResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AccountInfo
 *
 * Azure AI account metadata.
 */
export const zAccountInfo = z.object({
    label: z.string().optional().default(''),
    name: z.string(),
    resourceGroup: z.string(),
    subscriptionId: z.string()
});

/**
 * BlocklistResponse
 *
 * A custom blocklist with item count.
 */
export const zBlocklistResponse = z.object({
    description: z.string().optional().default(''),
    itemCount: z.int().optional().default(0),
    name: z.string().optional().default('')
});

/**
 * DeploymentResponse
 *
 * A model deployment with its governance config.
 */
export const zDeploymentResponse = z.object({
    modelFormat: z.string().optional().default(''),
    modelName: z.string().optional().default(''),
    modelVersion: z.string().optional().default(''),
    name: z.string(),
    provider: z.string().optional().default(''),
    provisioningState: z.string().optional().default(''),
    raiPolicyName: z.string().optional().default(''),
    skuCapacity: z.int().optional().default(0),
    skuName: z.string().optional().default('')
});

/**
 * ContentFilterConfig
 *
 * A single content filter rule within a RAI policy.
 */
export const zContentFilterConfig = z.object({
    blocking: z.boolean().optional().default(true),
    enabled: z.boolean().optional().default(true),
    name: z.string(),
    severityThreshold: z.string().optional().default(''),
    source: z.string().optional().default('')
});

/**
 * PolicyBlocklistRef
 *
 * A blocklist reference within a RAI policy.
 */
export const zPolicyBlocklistRef = z.object({
    blocking: z.boolean().optional().default(true),
    blocklistName: z.string().optional().default(''),
    source: z.string().optional().default('')
});

/**
 * RaiPolicyResponse
 *
 * A content-filter (RAI) policy.
 */
export const zRaiPolicyResponse = z.object({
    basePolicyName: z.string().optional().default(''),
    contentFilters: z.array(zContentFilterConfig).optional().default([]),
    customBlocklists: z.array(zPolicyBlocklistRef).optional().default([]),
    mode: z.string().optional().default(''),
    name: z.string(),
    provider: z.string().optional().default(''),
    type: z.string().optional().default('')
});

/**
 * GovernanceOverviewResponse
 *
 * The complete governance dashboard payload.
 */
export const zGovernanceOverviewResponse = z.object({
    accounts: z.array(zAccountInfo).optional().default([]),
    blocklists: z.array(zBlocklistResponse).optional().default([]),
    configured: z.boolean(),
    deployments: z.array(zDeploymentResponse).optional().default([]),
    message: z.string().nullish(),
    policies: z.array(zRaiPolicyResponse).optional().default([])
});

/**
 * DataResponse[GovernanceOverviewResponse]
 */
export const zDataResponseGovernanceOverviewResponse = z.object({
    data: zGovernanceOverviewResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ContentFilterCategoryCount
 */
export const zContentFilterCategoryCount = z.object({
    blocked: z.int(),
    category: z.string(),
    hits: z.int()
});

/**
 * ContentFilterSeverityCount
 */
export const zContentFilterSeverityCount = z.object({
    hits: z.int(),
    severity: z.string().nullable()
});

/**
 * ContentFilterTotals
 *
 * ``events`` counts flagged LLM calls; ``category_hits`` counts their flagged categories.
 */
export const zContentFilterTotals = z.object({
    blocked: z.int().optional().default(0),
    categoryHits: z.int().optional().default(0),
    completionHits: z.int().optional().default(0),
    events: z.int().optional().default(0),
    promptHits: z.int().optional().default(0)
});

/**
 * ContentFilterTrendPoint
 */
export const zContentFilterTrendPoint = z.object({
    blocked: z.int(),
    day: z.iso.date(),
    hits: z.int()
});

/**
 * ContentFilterAnalyticsResponse
 */
export const zContentFilterAnalyticsResponse = z.object({
    byCategory: z.array(zContentFilterCategoryCount),
    bySeverity: z.array(zContentFilterSeverityCount),
    totals: zContentFilterTotals,
    trend: z.array(zContentFilterTrendPoint)
});

/**
 * DataResponse[ContentFilterAnalyticsResponse]
 */
export const zDataResponseContentFilterAnalyticsResponse = z.object({
    data: zContentFilterAnalyticsResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ContentFilterEventItem
 *
 * One flagged category of one verdict. Never carries who triggered it.
 */
export const zContentFilterEventItem = z.object({
    actualModel: z.string().nullable(),
    category: z.string(),
    correlatable: z.boolean(),
    createdAt: z.iso.datetime(),
    errorCode: z.string().nullable(),
    eventUuid: z.string(),
    feature: z.string().nullable(),
    filtered: z.boolean(),
    finishReason: z.string().nullable(),
    model: z.string(),
    severity: z.string().nullable(),
    source: z.string()
});

/**
 * PaginatedResponse[ContentFilterEventItem]
 */
export const zPaginatedResponseContentFilterEventItem = z.object({
    data: z.array(zContentFilterEventItem),
    meta: zPaginationMeta
});

/**
 * ContentFilterCorrelatedConversation
 */
export const zContentFilterCorrelatedConversation = z.object({
    deleted: z.boolean(),
    kind: z.enum(['chat', 'group_chat']),
    title: z.string().nullable(),
    uuid: z.string()
});

/**
 * ContentFilterCorrelatedUser
 */
export const zContentFilterCorrelatedUser = z.object({
    email: z.string(),
    isActive: z.boolean(),
    name: z.string(),
    uuid: z.string()
});

/**
 * ContentFilterCorrelationResponse
 */
export const zContentFilterCorrelationResponse = z.object({
    categories: z.array(z.string()),
    conversation: zContentFilterCorrelatedConversation.nullable(),
    createdAt: z.iso.datetime(),
    eventUuid: z.string(),
    feature: z.string().nullable(),
    user: zContentFilterCorrelatedUser.nullable()
});

/**
 * DataResponse[ContentFilterCorrelationResponse]
 */
export const zDataResponseContentFilterCorrelationResponse = z.object({
    data: zContentFilterCorrelationResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ContentFilterReviewMessage
 */
export const zContentFilterReviewMessage = z.object({
    attachmentNames: z.array(z.string()),
    content: z.string(),
    createdAt: z.iso.datetime(),
    role: z.string(),
    uuid: z.string()
});

/**
 * ContentFilterReviewResponse
 *
 * A flagged chat, read-only, for an admin reviewing the event (MHS-1070). Audited.
 */
export const zContentFilterReviewResponse = z.object({
    eventUuid: z.string(),
    messages: z.array(zContentFilterReviewMessage),
    owner: zContentFilterCorrelatedUser.nullable(),
    title: z.string().nullable()
});

/**
 * DataResponse[ContentFilterReviewResponse]
 */
export const zDataResponseContentFilterReviewResponse = z.object({
    data: zContentFilterReviewResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * LeadershipEntry
 *
 * A single leadership team member.
 */
export const zLeadershipEntry = z.object({
    name: z.string().max(255),
    title: z.string().max(255)
});

/**
 * CompanyProfileResponse
 *
 * Full company profile response.
 */
export const zCompanyProfileResponse = z.object({
    brand_kit: z.record(z.string(), z.unknown()).nullish(),
    brand_style: z.string().nullish(),
    company_name: z.string(),
    created_at: z.iso.datetime(),
    description: z.string().nullish(),
    employee_count: z.string().nullish(),
    extra: z.record(z.string(), z.unknown()).nullish(),
    founded_year: z.int().nullish(),
    headquarters: z.string().nullish(),
    industry: z.string().nullish(),
    key_terminology: z.string().nullish(),
    knowledge_base_collection_uuid: z.string().nullish(),
    leadership: z.array(zLeadershipEntry).nullish(),
    logo_url: z.string().nullish(),
    mission: z.string().nullish(),
    products_services: z.string().nullish(),
    updated_at: z.iso.datetime(),
    updated_by_name: z.string().nullish(),
    uuid: z.string(),
    values: z.string().nullish(),
    website: z.string().nullish()
});

/**
 * DataResponse[Union[CompanyProfileResponse, NoneType]]
 */
export const zDataResponseUnionCompanyProfileResponseNoneType = z.object({
    data: zCompanyProfileResponse.nullable(),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * CompanyProfileUpdate
 *
 * Schema for creating or updating the company profile.
 *
 * All fields are optional so admins can fill in incrementally.
 */
export const zCompanyProfileUpdate = z.object({
    brand_kit: z.record(z.string(), z.unknown()).nullish(),
    brand_style: z.string().max(2000).nullish(),
    company_name: z.string().max(255).nullish(),
    description: z.string().max(5000).nullish(),
    employee_count: z.string().max(100).nullish(),
    extra: z.record(z.string(), z.unknown()).nullish(),
    founded_year: z.int().gte(1800).lte(2100).nullish(),
    headquarters: z.string().max(255).nullish(),
    industry: z.string().max(255).nullish(),
    key_terminology: z.string().max(5000).nullish(),
    knowledge_base_collection_uuid: z.string().nullish(),
    leadership: z.array(zLeadershipEntry).nullish(),
    mission: z.string().max(2000).nullish(),
    products_services: z.string().max(5000).nullish(),
    values: z.string().max(5000).nullish(),
    website: z.string().max(500).nullish()
});

/**
 * DataResponse[CompanyProfileResponse]
 */
export const zDataResponseCompanyProfileResponse = z.object({
    data: zCompanyProfileResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * CompanyContextPreview
 *
 * Preview of the auto-generated system prompt context.
 */
export const zCompanyContextPreview = z.object({
    context_text: z.string(),
    token_estimate: z.int()
});

/**
 * DataResponse[CompanyContextPreview]
 */
export const zDataResponseCompanyContextPreview = z.object({
    data: zCompanyContextPreview,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * Body_upload_logo_api_v1_admin_company_logo_post
 */
export const zBodyUploadLogoApiV1AdminCompanyLogoPost = z.object({
    file: z.string()
});

/**
 * LicenseResponse
 *
 * License information response (visible to all platform admins).
 */
export const zLicenseResponse = z.object({
    createdAt: z.iso.datetime().nullish(),
    exemptDomains: z.array(z.string()).nullish(),
    expiresAt: z.iso.datetime().nullish(),
    id: z.int(),
    isActive: z.boolean().optional().default(true),
    licenseName: z.string().nullish(),
    notes: z.string().nullish(),
    seatLimit: z.int().nullish(),
    seatsUsed: z.int().optional().default(0),
    startsAt: z.iso.datetime().nullish(),
    status: z.string().optional().default('active'),
    suspendedAt: z.iso.datetime().nullish(),
    suspensionReason: z.string().nullish(),
    updatedAt: z.iso.datetime().nullish(),
    uuid: z.string()
});

/**
 * DataResponse[LicenseResponse]
 */
export const zDataResponseLicenseResponse = z.object({
    data: zLicenseResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * LicenseUpdate
 *
 * Update license configuration (MH admin only).
 */
export const zLicenseUpdate = z.object({
    exemptDomains: z.array(z.string()).nullish(),
    expiresAt: z.iso.datetime().nullish(),
    licenseName: z.string().nullish(),
    notes: z.string().nullish(),
    seatLimit: z.int().nullish(),
    startsAt: z.iso.datetime().nullish()
});

/**
 * LicenseStatusResponse
 *
 * Lightweight license status (for customer admin dashboards).
 *
 * Includes only what customers need to see — no internal notes or
 * configuration details like exempt_domains.
 */
export const zLicenseStatusResponse = z.object({
    expiresAt: z.iso.datetime().nullish(),
    isActive: z.boolean().optional().default(true),
    seatLimit: z.int().nullish(),
    seatsAvailable: z.int().nullish(),
    seatsUsed: z.int().optional().default(0),
    status: z.string()
});

/**
 * DataResponse[LicenseStatusResponse]
 */
export const zDataResponseLicenseStatusResponse = z.object({
    data: zLicenseStatusResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * LicenseSuspend
 *
 * Suspend the platform (MH admin only).
 */
export const zLicenseSuspend = z.object({
    reason: z.string().nullish()
});

/**
 * LicenseResume
 *
 * Resume the platform after suspension (MH admin only).
 */
export const zLicenseResume = z.record(z.string(), z.unknown());

/**
 * ScreenshotEntry
 *
 * Schema for a single screenshot attached to feedback.
 */
export const zScreenshotEntry = z.object({
    blobName: z.string(),
    contentType: z.string(),
    originalFilename: z.string(),
    size: z.int(),
    url: z.string()
});

/**
 * FeedbackResponse
 *
 * Schema for a feedback item returned from the API.
 */
export const zFeedbackResponse = z.object({
    adminNotes: z.string().nullish(),
    category: z.string(),
    createdAt: z.iso.datetime(),
    description: z.string(),
    pageUrl: z.string().nullish(),
    resolvedAt: z.iso.datetime().nullish(),
    screenshots: z.array(zScreenshotEntry).optional().default([]),
    status: z.string(),
    title: z.string(),
    updatedAt: z.iso.datetime(),
    userAgent: z.string().nullish(),
    userEmail: z.string().nullish(),
    userName: z.string().nullish(),
    uuid: z.string()
});

/**
 * PaginatedResponse[FeedbackResponse]
 */
export const zPaginatedResponseFeedbackResponse = z.object({
    data: z.array(zFeedbackResponse),
    meta: zPaginationMeta
});

/**
 * DataResponse[FeedbackResponse]
 */
export const zDataResponseFeedbackResponse = z.object({
    data: zFeedbackResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * FeedbackStatusUpdate
 *
 * Schema for updating feedback status (admin use).
 */
export const zFeedbackStatusUpdate = z.object({
    adminNotes: z.string().max(10000).nullish(),
    status: z.string().regex(/^(open|in_progress|resolved|closed)$/)
});

/**
 * Body_submit_feedback_api_v1_feedback_post
 */
export const zBodySubmitFeedbackApiV1FeedbackPost = z.object({
    category: z.enum(['bug', 'feature']),
    description: z.string().min(1).max(10000),
    page_url: z.string().nullish(),
    screenshots: z.array(z.string()).optional(),
    title: z.string().min(1).max(255),
    user_agent: z.string().nullish()
});

/**
 * SyncTargetSummary
 *
 * A data source driven by a cached authorization.
 */
export const zSyncTargetSummary = z.object({
    lastSyncedAt: z.iso.datetime().nullish(),
    name: z.string(),
    reauthorizationRequired: z.boolean().optional().default(false),
    status: z.string(),
    uuid: z.string()
});

/**
 * AdminTokenAuthorizationResponse
 *
 * Admin view: same as the user view plus the owning user's identity.
 */
export const zAdminTokenAuthorizationResponse = z.object({
    authorizedAt: z.iso.datetime(),
    cacheType: z.string(),
    excludedTools: z.array(z.string()),
    isValid: z.boolean(),
    lastUsedAt: z.iso.datetime(),
    purpose: z.string(),
    scopes: z.array(z.string()),
    syncTargets: z.array(zSyncTargetSummary).optional().default([]),
    userEmail: z.string().nullish(),
    userId: z.int(),
    userName: z.string().nullish(),
    uuid: z.string()
});

/**
 * DataResponse[list[AdminTokenAuthorizationResponse]]
 */
export const zDataResponseListAdminTokenAuthorizationResponse = z.object({
    data: z.array(zAdminTokenAuthorizationResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * OAuthClientResponse
 *
 * A registered OAuth client (secret never included).
 */
export const zOAuthClientResponse = z.object({
    allowedScopes: z.array(z.string()).optional(),
    clientId: z.string(),
    clientName: z.string(),
    clientType: z.string(),
    createdAt: z.iso.datetime().nullish(),
    expiresAt: z.iso.datetime().nullish(),
    grantTypes: z.array(z.string()).optional(),
    hasSecret: z.boolean().optional().default(false),
    isEnabled: z.boolean(),
    isFirstParty: z.boolean(),
    redirectUris: z.array(z.string()).optional(),
    requirePkce: z.boolean(),
    uuid: z.string()
});

/**
 * DataResponse[list[OAuthClientResponse]]
 */
export const zDataResponseListOAuthClientResponse = z.object({
    data: z.array(zOAuthClientResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * OAuthClientCreate
 *
 * Register a new OAuth client.
 */
export const zOAuthClientCreate = z.object({
    allowedScopes: z.array(z.string()).optional(),
    clientName: z.string().min(1).max(255),
    clientType: z.string().regex(/^(public|confidential)$/).optional().default('public'),
    grantTypes: z.array(z.string()).optional(),
    redirectUris: z.array(z.string()).optional(),
    requirePkce: z.boolean().optional().default(true)
});

/**
 * OAuthClientCreated
 *
 * Response when a client is created.
 *
 * For confidential clients the plaintext ``client_secret`` is returned **once**
 * here and never again — the admin must copy it now.
 */
export const zOAuthClientCreated = z.object({
    client: zOAuthClientResponse,
    clientSecret: z.string().nullish()
});

/**
 * DataResponse[OAuthClientCreated]
 */
export const zDataResponseOAuthClientCreated = z.object({
    data: zOAuthClientCreated,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * OAuthClientUpdate
 *
 * Patch a client. Only provided fields change.
 */
export const zOAuthClientUpdate = z.object({
    allowedScopes: z.array(z.string()).nullish(),
    clientName: z.string().min(1).max(255).nullish(),
    grantTypes: z.array(z.string()).nullish(),
    isEnabled: z.boolean().nullish(),
    redirectUris: z.array(z.string()).nullish(),
    requirePkce: z.boolean().nullish()
});

/**
 * DataResponse[OAuthClientResponse]
 */
export const zDataResponseOAuthClientResponse = z.object({
    data: zOAuthClientResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SetupIssue
 *
 * Why an extension isn't ready. The web translates from ``code``.
 */
export const zSetupIssue = z.object({
    code: z.enum(['NOT_AVAILABLE_IN_DEPLOYMENT', 'MISSING_CREDENTIAL']),
    key: z.string().nullish(),
    label: z.string().nullish()
});

/**
 * SetupStatus
 *
 * Computed setup status (docs/design/web/WEB_EXTENSIONS_PLAN.md §5).
 */
export const zSetupStatus = z.object({
    issues: z.array(zSetupIssue).optional(),
    kind: z.enum([
        'built_in',
        'integration',
        'custom'
    ]),
    state: z.enum([
        'ready',
        'needs_setup',
        'unavailable',
        'off'
    ])
});

/**
 * ExtensionSummary
 *
 * Summary for list views.
 */
export const zExtensionSummary = z.object({
    authType: z.string().nullish(),
    color: z.string().nullish(),
    createdAt: z.iso.datetime(),
    description: z.string().nullish(),
    displayOrder: z.int().optional().default(0),
    extensionType: z.string(),
    icon: z.string().nullish(),
    isEnabled: z.boolean(),
    isSystem: z.boolean(),
    name: z.string(),
    readConsentMode: z.string().optional().default('opt_out'),
    setup: zSetupStatus.nullish(),
    skillCount: z.int().optional().default(0),
    slug: z.string(),
    status: z.string(),
    teamAccessCount: z.int().optional().default(0),
    toolCount: z.int().optional().default(0),
    updatedAt: z.iso.datetime(),
    uuid: z.string(),
    writeConsentMode: z.string().optional().default('opt_in')
});

/**
 * DataResponse[list[ExtensionSummary]]
 */
export const zDataResponseListExtensionSummary = z.object({
    data: z.array(zExtensionSummary),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * IntegrationConnectorRef
 *
 * An integration's own connector that holds tools (its MCP server, e.g. HubSpot's), governed on its page.
 */
export const zIntegrationConnectorRef = z.object({
    enabledToolCount: z.int().optional().default(0),
    name: z.string(),
    toolCount: z.int().optional().default(0),
    url: z.string().nullish(),
    uuid: z.string()
});

/**
 * CredentialMetadata
 *
 * Credential info WITHOUT the actual secret value (unless non-secret).
 */
export const zCredentialMetadata = z.object({
    currentValue: z.string().nullish(),
    envVarName: z.string().nullish(),
    hasDbValue: z.boolean().optional().default(false),
    isConfigured: z.boolean(),
    isEnvOverride: z.boolean(),
    isSecret: z.boolean().optional().default(true),
    key: z.string()
});

/**
 * ProfileResponse
 *
 * Extension profile detail response.
 */
export const zProfileResponse = z.object({
    contextInstructions: z.string().optional().default(''),
    createdAt: z.iso.datetime(),
    credentialsOverride: z.record(z.string(), z.unknown()).nullish(),
    description: z.string().nullish(),
    isEnabled: z.boolean().optional().default(true),
    name: z.string(),
    teamName: z.string(),
    teamUuid: z.string(),
    updatedAt: z.iso.datetime(),
    uuid: z.string()
});

/**
 * SkillSummaryForExtension
 *
 * Minimal skill info embedded in an extension detail response.
 */
export const zSkillSummaryForExtension = z.object({
    category: z.string().nullish(),
    color: z.string(),
    description: z.string(),
    icon: z.string(),
    name: z.string(),
    skillType: z.string(),
    tags: z.array(z.string()).optional(),
    uuid: z.string()
});

/**
 * TeamAccessResponse
 *
 * Team info for extension access control.
 */
export const zTeamAccessResponse = z.object({
    teamName: z.string(),
    teamUuid: z.string()
});

/**
 * ToolSummaryForExtension
 *
 * Minimal tool info embedded in an extension detail response.
 */
export const zToolSummaryForExtension = z.object({
    capabilities: z.array(z.string()).optional(),
    description: z.string().nullish(),
    escalation: z.string().optional().default('none'),
    extensionDisplayOrder: z.int().optional().default(0),
    icon: z.string(),
    isEnabled: z.boolean(),
    name: z.string(),
    toolType: z.string(),
    uuid: z.string()
});

/**
 * UserOAuthSetup
 *
 * Admin setup info for a per-user OAuth extension.
 *
 * Surfaces the exact, per-install redirect URI the admin must register in the
 * provider's OAuth app (derived from FRONTEND_BASE_URL, so it differs per
 * deployment domain).
 */
export const zUserOAuthSetup = z.object({
    provider: z.string(),
    providerDocsUrl: z.string().nullish(),
    redirectUri: z.string(),
    scopes: z.array(z.string()).optional()
});

/**
 * ExtensionResponse
 *
 * Full extension detail response.
 */
export const zExtensionResponse = z.object({
    authType: z.string().nullish(),
    color: z.string().nullish(),
    config: z.record(z.string(), z.unknown()).optional(),
    connectors: z.array(zIntegrationConnectorRef).optional(),
    createdAt: z.iso.datetime(),
    credentials: z.array(zCredentialMetadata).optional(),
    credentialsConfig: z.record(z.string(), z.unknown()).optional(),
    description: z.string().nullish(),
    displayOrder: z.int().optional().default(0),
    documentationUrl: z.string().nullish(),
    extensionType: z.string(),
    healthCheckUrl: z.string().nullish(),
    icon: z.string().nullish(),
    isEnabled: z.boolean(),
    isSystem: z.boolean(),
    name: z.string(),
    profiles: z.array(zProfileResponse).optional(),
    readConsentMode: z.string().optional().default('opt_out'),
    setup: zSetupStatus.nullish(),
    skillCount: z.int().optional().default(0),
    skills: z.array(zSkillSummaryForExtension).optional(),
    slug: z.string(),
    status: z.string(),
    teamAccess: z.array(zTeamAccessResponse).optional(),
    teamAccessCount: z.int().optional().default(0),
    toolCount: z.int().optional().default(0),
    tools: z.array(zToolSummaryForExtension).optional(),
    updatedAt: z.iso.datetime(),
    userOauthSetup: zUserOAuthSetup.nullish(),
    uuid: z.string(),
    writeConsentMode: z.string().optional().default('opt_in')
});

/**
 * DataResponse[ExtensionResponse]
 */
export const zDataResponseExtensionResponse = z.object({
    data: zExtensionResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ExtensionToggle
 *
 * Enable / disable an extension.
 */
export const zExtensionToggle = z.object({
    isEnabled: z.boolean()
});

/**
 * ExtensionUpdate
 *
 * Update extension settings (admin only).
 */
export const zExtensionUpdate = z.object({
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullish(),
    config: z.record(z.string(), z.unknown()).nullish(),
    description: z.string().max(5000).nullish(),
    displayOrder: z.int().nullish(),
    icon: z.string().max(50).nullish(),
    name: z.string().min(1).max(255).nullish(),
    readConsentMode: z.enum([
        'opt_in',
        'opt_out',
        'not_allowed'
    ]).nullish(),
    writeConsentMode: z.enum([
        'opt_in',
        'opt_out',
        'not_allowed'
    ]).nullish()
});

/**
 * CredentialUpdate
 *
 * Set or update a single credential value.
 */
export const zCredentialUpdate = z.object({
    key: z.string().min(1).max(255),
    value: z.string().min(1)
});

/**
 * DataResponse[list[CredentialMetadata]]
 */
export const zDataResponseListCredentialMetadata = z.object({
    data: z.array(zCredentialMetadata),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * TeamAccessUpdate
 *
 * Replace the entire team access list for an extension.
 *
 * An empty list means the extension is global (available to all teams).
 */
export const zTeamAccessUpdate = z.object({
    teamUuids: z.array(z.string())
});

/**
 * DataResponse[list[TeamAccessResponse]]
 */
export const zDataResponseListTeamAccessResponse = z.object({
    data: z.array(zTeamAccessResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SharePointDocumentContent
 *
 * Extracted text content (or slide images) from a SharePoint document.
 */
export const zSharePointDocumentContent = z.object({
    content: z.string(),
    filename: z.string(),
    mime_type: z.string(),
    size: z.int(),
    slide_images: z.array(z.string()).nullish(),
    source_url: z.string(),
    truncated: z.boolean().optional().default(false)
});

/**
 * AvailabilityRequest
 */
export const zAvailabilityRequest = z.object({
    attendees: z.array(z.string()).max(19).optional(),
    duration_minutes: z.int().gte(15).lte(480).optional().default(30),
    end: z.iso.datetime(),
    max_suggestions: z.int().gte(1).lte(6).optional().default(3),
    start: z.iso.datetime()
});

/**
 * BusyBlock
 */
export const zBusyBlock = z.object({
    end: z.string(),
    location: z.string().nullish(),
    start: z.string(),
    status: z.string(),
    subject: z.string().nullish()
});

/**
 * WorkingHours
 */
export const zWorkingHours = z.object({
    days: z.array(z.string()),
    end: z.string(),
    start: z.string()
});

/**
 * PersonAvailability
 */
export const zPersonAvailability = z.object({
    busy: z.array(zBusyBlock),
    email: z.string(),
    error: z.string().nullish(),
    hours: zWorkingHours
});

/**
 * SuggestedSlot
 */
export const zSuggestedSlot = z.object({
    busy: z.array(z.string()),
    end: z.string(),
    everyone_free: z.boolean(),
    start: z.string(),
    tentative: z.array(z.string())
});

/**
 * AvailabilityResponse
 */
export const zAvailabilityResponse = z.object({
    people: z.array(zPersonAvailability),
    slots: z.array(zSuggestedSlot),
    time_zone: z.string()
});

/**
 * MicrosoftFeature
 */
export const zMicrosoftFeature = z.object({
    granted: z.boolean(),
    key: z.string(),
    missing: z.array(z.string()),
    offered: z.boolean(),
    permissions: z.array(z.string())
});

/**
 * MicrosoftPermissions
 */
export const zMicrosoftPermissions = z.object({
    checked: z.boolean(),
    features: z.array(zMicrosoftFeature)
});

/**
 * DataResponse[MicrosoftPermissions]
 */
export const zDataResponseMicrosoftPermissions = z.object({
    data: zMicrosoftPermissions,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * GoogleDriveItem
 *
 * File or folder in Google Drive.
 */
export const zGoogleDriveItem = z.object({
    id: z.string(),
    is_folder: z.boolean().optional().default(false),
    last_modified: z.string().nullish(),
    mime_type: z.string().nullish(),
    name: z.string(),
    size: z.int().nullish(),
    web_url: z.string().optional().default('')
});

/**
 * GoogleDriveBrowseResponse
 *
 * Response for browse/list endpoints.
 */
export const zGoogleDriveBrowseResponse = z.object({
    items: z.array(zGoogleDriveItem).optional()
});

/**
 * GoogleDriveDocumentContent
 *
 * Extracted text content from a Google Drive document.
 */
export const zGoogleDriveDocumentContent = z.object({
    content: z.string(),
    filename: z.string(),
    mime_type: z.string(),
    size: z.int(),
    slide_images: z.array(z.string()).nullish(),
    source_url: z.string(),
    truncated: z.boolean().optional().default(false)
});

/**
 * ConnectorSummary
 *
 * A connector in a list.
 */
export const zConnectorSummary = z.object({
    authMode: z.string(),
    color: z.string().nullish(),
    createdAt: z.iso.datetime(),
    createdByName: z.string().nullish(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    disabledByAdmin: z.boolean().optional().default(false),
    disabledReason: z.string().nullish(),
    helpSlug: z.string().nullish(),
    icon: z.string().nullish(),
    integrationName: z.string().nullish(),
    integrationSlug: z.string().nullish(),
    integrationUuid: z.string().nullish(),
    isEnabled: z.boolean(),
    isOrg: z.boolean().optional().default(false),
    isShared: z.boolean().optional().default(false),
    lastError: z.string().nullish(),
    lastRefreshedAt: z.iso.datetime().nullish(),
    name: z.string(),
    setupNotes: z.string().nullish(),
    templateSlug: z.string().nullish(),
    transport: z.string(),
    updatedAt: z.iso.datetime(),
    url: z.string(),
    uuid: z.string()
});

/**
 * PaginatedResponse[ConnectorSummary]
 */
export const zPaginatedResponseConnectorSummary = z.object({
    data: z.array(zConnectorSummary),
    meta: zPaginationMeta
});

/**
 * ConnectorOAuth
 *
 * How people sign in to an ``oauth`` connector (phase 6, MCP authorization spec).
 *
 * The URLs are found from the server's OAuth metadata when left empty. The client
 * secret is the connector's secret (``PUT /secret``), never stored here.
 */
export const zConnectorOAuth = z.object({
    authorizeUrl: z.string().max(2048).nullish(),
    clientId: z.string().max(500).optional().default(''),
    registrationEndpoint: z.string().max(2048).nullish(),
    resource: z.string().max(2048).nullish(),
    scopes: z.array(z.string()).max(100).optional(),
    tokenUrl: z.string().max(2048).nullish()
});

/**
 * ConnectorSetting
 *
 * A non-secret value the connector needs, which each space can set (phase 4, D11).
 *
 * Its value fills ``{{config.<name>}}`` in the URL or body, and/or goes in ``header``.
 */
export const zConnectorSetting = z.object({
    default: z.string().max(1000).nullish(),
    description: z.string().max(500).nullish(),
    header: z.string().nullish(),
    label: z.string().min(1).max(100),
    name: z.string(),
    required: z.boolean().optional().default(false)
});

/**
 * ConnectorEndpoint
 *
 * Where and how to reach the server. Which fields apply depends on the transport.
 */
export const zConnectorEndpoint = z.object({
    authHeader: z.string().optional().default('Authorization'),
    authScheme: z.string().max(50).optional().default('Bearer'),
    bodyTemplate: z.record(z.string(), z.unknown()).nullish(),
    headers: z.record(z.string(), z.string()).nullish(),
    method: z.enum([
        'GET',
        'POST',
        'PUT',
        'PATCH',
        'DELETE'
    ]).nullish(),
    oauth: zConnectorOAuth.nullish(),
    parameters: z.record(z.string(), z.unknown()).nullish(),
    responsePath: z.string().max(255).nullish(),
    settings: z.array(zConnectorSetting).max(20).nullish(),
    timeout: z.number().gte(5).lte(120).optional().default(60),
    toolDescription: z.string().max(2000).nullish(),
    toolName: z.string().nullish(),
    url: z.string().min(8).max(2048)
});

/**
 * ConnectorCreate
 *
 * Add a connector.
 */
export const zConnectorCreate = z.object({
    authMode: z.enum([
        'none',
        'shared_secret',
        'per_user',
        'oauth'
    ]).optional().default('none'),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullish(),
    description: z.string().max(5000).nullish(),
    endpoint: zConnectorEndpoint,
    icon: z.string().max(50).nullish(),
    name: z.string().min(1).max(255),
    transport: z.enum(['mcp', 'http']).optional().default('mcp')
});

/**
 * ConnectorResponse
 *
 * A connector's full settings (never its secrets).
 */
export const zConnectorResponse = z.object({
    authMode: z.string(),
    color: z.string().nullish(),
    createdAt: z.iso.datetime(),
    createdByName: z.string().nullish(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    disabledByAdmin: z.boolean().optional().default(false),
    disabledReason: z.string().nullish(),
    endpoint: z.record(z.string(), z.unknown()).optional(),
    hasMyCredential: z.boolean().optional().default(false),
    hasSecret: z.boolean().optional().default(false),
    helpSlug: z.string().nullish(),
    icon: z.string().nullish(),
    integrationName: z.string().nullish(),
    integrationSlug: z.string().nullish(),
    integrationUuid: z.string().nullish(),
    isEnabled: z.boolean(),
    isOrg: z.boolean().optional().default(false),
    isShared: z.boolean().optional().default(false),
    lastError: z.string().nullish(),
    lastRefreshedAt: z.iso.datetime().nullish(),
    myAccount: z.string().nullish(),
    name: z.string(),
    oauthRedirectUri: z.string().nullish(),
    oauthRegisteredAt: z.iso.datetime().nullish(),
    setupNotes: z.string().nullish(),
    signInReady: z.boolean().optional().default(false),
    templateSlug: z.string().nullish(),
    transport: z.string(),
    updatedAt: z.iso.datetime(),
    url: z.string(),
    uuid: z.string()
});

/**
 * DataResponse[ConnectorResponse]
 */
export const zDataResponseConnectorResponse = z.object({
    data: zConnectorResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ConnectorPolicyResponse
 *
 * The organization's connector policy (governance, D16).
 */
export const zConnectorPolicyResponse = z.object({
    allowedHosts: z.array(z.string()).optional(),
    blockedHosts: z.array(z.string()).optional(),
    canAdd: z.boolean().optional().default(true),
    usersMayAdd: z.boolean(),
    usersMayAutoRegister: z.boolean().optional().default(true)
});

/**
 * DataResponse[ConnectorPolicyResponse]
 */
export const zDataResponseConnectorPolicyResponse = z.object({
    data: zConnectorPolicyResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * MyConnectorConnection
 *
 * A connector that uses each person's own token, and whether I've added mine (Settings > Connections).
 */
export const zMyConnectorConnection = z.object({
    account: z.string().nullish(),
    authMode: z.enum([
        'none',
        'shared_secret',
        'per_user',
        'oauth'
    ]).optional().default('per_user'),
    connected: z.boolean(),
    description: z.string().nullish(),
    disabledByAdmin: z.boolean().optional().default(false),
    integrationSlug: z.string().nullish(),
    name: z.string(),
    url: z.string(),
    uuid: z.string()
});

/**
 * DataResponse[list[MyConnectorConnection]]
 */
export const zDataResponseListMyConnectorConnection = z.object({
    data: z.array(zMyConnectorConnection),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ConnectorUpdate
 *
 * Change a connector's settings (editor+). The transport can't change.
 */
export const zConnectorUpdate = z.object({
    authMode: z.enum([
        'none',
        'shared_secret',
        'per_user',
        'oauth'
    ]).nullish(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullish(),
    description: z.string().max(5000).nullish(),
    endpoint: zConnectorEndpoint.nullish(),
    icon: z.string().max(50).nullish(),
    isEnabled: z.boolean().nullish(),
    name: z.string().min(1).max(255).nullish(),
    setupNotes: z.string().max(5000).nullish()
});

/**
 * ConnectorToolResponse
 *
 * A tool a connector offers, and how it's classified (D10).
 */
export const zConnectorToolResponse = z.object({
    capability: z.string(),
    classifiedBy: z.string(),
    description: z.string().nullish(),
    escalation: z.string(),
    inputSchema: z.record(z.string(), z.unknown()).optional(),
    isEnabled: z.boolean(),
    name: z.string(),
    readOnlyHint: z.boolean().nullish(),
    uuid: z.string()
});

/**
 * DataResponse[list[ConnectorToolResponse]]
 */
export const zDataResponseListConnectorToolResponse = z.object({
    data: z.array(zConnectorToolResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ConnectorToolUpdate
 *
 * Reclassify or turn off a tool (editor+). ``reset`` goes back to what the server says.
 */
export const zConnectorToolUpdate = z.object({
    capability: z.enum([
        'context',
        'action',
        'artifact'
    ]).nullish(),
    escalation: z.enum(['none', 'required']).nullish(),
    isEnabled: z.boolean().nullish(),
    reset: z.boolean().optional().default(false)
});

/**
 * DataResponse[ConnectorToolResponse]
 */
export const zDataResponseConnectorToolResponse = z.object({
    data: zConnectorToolResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ConnectorSecretValue
 *
 * A secret or token to store. Write-only: it's never returned.
 */
export const zConnectorSecretValue = z.object({
    value: z.string().min(1).max(8000)
});

/**
 * ConnectorOAuthStart
 *
 * Start signing in to an ``oauth`` connector.
 */
export const zConnectorOAuthStart = z.object({
    redirectAfter: z.string().max(1024).nullish()
});

/**
 * ConnectorOAuthStartResponse
 */
export const zConnectorOAuthStartResponse = z.object({
    authorizeUrl: z.string()
});

/**
 * DataResponse[ConnectorOAuthStartResponse]
 */
export const zDataResponseConnectorOAuthStartResponse = z.object({
    data: zConnectorOAuthStartResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ConnectorTeamMemberResponse
 *
 * A team granted a role on a connector.
 */
export const zConnectorTeamMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    teamName: z.string(),
    teamUuid: z.string()
});

/**
 * ConnectorUserMemberResponse
 *
 * A user granted a role on a connector.
 */
export const zConnectorUserMemberResponse = z.object({
    joinedAt: z.iso.datetime(),
    role: z.string(),
    user: zUserResponse
});

/**
 * ConnectorMembersResponse
 *
 * Membership state for a connector plus the caller's effective role.
 */
export const zConnectorMembersResponse = z.object({
    canManage: z.boolean(),
    currentUserRole: z.string().nullish(),
    teamMembers: z.array(zConnectorTeamMemberResponse).optional(),
    userMembers: z.array(zConnectorUserMemberResponse).optional()
});

/**
 * DataResponse[ConnectorMembersResponse]
 */
export const zDataResponseConnectorMembersResponse = z.object({
    data: zConnectorMembersResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AddConnectorUserMemberRequest
 *
 * Add or update a user's role on a connector.
 */
export const zAddConnectorUserMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    userUuid: z.string()
});

/**
 * DataResponse[ConnectorUserMemberResponse]
 */
export const zDataResponseConnectorUserMemberResponse = z.object({
    data: zConnectorUserMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * UpdateConnectorMemberRoleRequest
 *
 * Change a connector member's role.
 */
export const zUpdateConnectorMemberRoleRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/)
});

/**
 * AddConnectorTeamMemberRequest
 *
 * Add or update a team's role on a connector.
 */
export const zAddConnectorTeamMemberRequest = z.object({
    role: z.string().regex(/^(admin|editor|viewer)$/).optional().default('viewer'),
    teamUuid: z.string()
});

/**
 * DataResponse[ConnectorTeamMemberResponse]
 */
export const zDataResponseConnectorTeamMemberResponse = z.object({
    data: zConnectorTeamMemberResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AgentConnectorSummary
 *
 * A connector attached to an agent, and whether I can use it (so its tools work for me).
 */
export const zAgentConnectorSummary = z.object({
    canUse: z.boolean(),
    description: z.string().nullish(),
    integrationName: z.string().nullish(),
    name: z.string(),
    transport: z.string(),
    uuid: z.string()
});

/**
 * DataResponse[list[AgentConnectorSummary]]
 */
export const zDataResponseListAgentConnectorSummary = z.object({
    data: z.array(zAgentConnectorSummary),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AgentConnectorsUpdate
 *
 * Replace an agent's connectors.
 */
export const zAgentConnectorsUpdate = z.object({
    connectorUuids: z.array(z.string()).max(50).optional()
});

/**
 * ConnectorTemplateField
 */
export const zConnectorTemplateField = z.object({
    hint: z.string().optional().default(''),
    label: z.string(),
    name: z.string(),
    pattern: z.string().nullish(),
    placeholder: z.string().optional().default('')
});

/**
 * ConnectorTemplateResponse
 *
 * A known server you can connect in one step (MHS-875).
 */
export const zConnectorTemplateResponse = z.object({
    authMode: z.enum([
        'none',
        'shared_secret',
        'per_user',
        'oauth'
    ]).optional().default('shared_secret'),
    clientIdHint: z.string().nullish(),
    clientIdPattern: z.string().nullish(),
    color: z.string(),
    description: z.string(),
    fields: z.array(zConnectorTemplateField).optional(),
    hasStarterSkill: z.boolean(),
    helpSlug: z.string(),
    icon: z.string(),
    integrationName: z.string().nullish(),
    name: z.string(),
    oauthRedirectUri: z.string().nullish(),
    oauthRegistration: z.enum(['manual', 'dynamic']).optional().default('manual'),
    readOnly: z.boolean(),
    secretHint: z.string().optional().default(''),
    secretLabel: z.string().optional().default(''),
    secretPatterns: z.array(z.string()).optional(),
    slug: z.string(),
    url: z.string()
});

/**
 * DataResponse[list[ConnectorTemplateResponse]]
 */
export const zDataResponseListConnectorTemplateResponse = z.object({
    data: z.array(zConnectorTemplateResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ConnectorFromTemplate
 *
 * Add a connector from a template.
 */
export const zConnectorFromTemplate = z.object({
    clientId: z.string().max(500).nullish(),
    fields: z.record(z.string(), z.string()).optional(),
    name: z.string().max(255).nullish(),
    readOnly: z.boolean().optional().default(true),
    secret: z.string().max(8000).optional().default(''),
    starterSkill: z.boolean().optional().default(true),
    template: z.string()
});

/**
 * ConnectorFromTemplateResponse
 */
export const zConnectorFromTemplateResponse = z.object({
    connector: zConnectorResponse,
    skillUuid: z.string().nullish(),
    toolsTurnedOff: z.int().optional().default(0)
});

/**
 * DataResponse[ConnectorFromTemplateResponse]
 */
export const zDataResponseConnectorFromTemplateResponse = z.object({
    data: zConnectorFromTemplateResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ConnectorIntegrationOption
 *
 * An integration anyone can add their own connector for (shared-key integrations, phase 5).
 */
export const zConnectorIntegrationOption = z.object({
    color: z.string().nullish(),
    description: z.string().nullish(),
    icon: z.string().nullish(),
    name: z.string(),
    secretLabel: z.string(),
    settings: z.array(zConnectorSetting).optional(),
    slug: z.string()
});

/**
 * DataResponse[list[ConnectorIntegrationOption]]
 */
export const zDataResponseListConnectorIntegrationOption = z.object({
    data: z.array(zConnectorIntegrationOption),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ConnectorInstanceCreate
 *
 * Add my own connector for an integration, with my own key.
 */
export const zConnectorInstanceCreate = z.object({
    description: z.string().max(2000).nullish(),
    integration: z.string(),
    name: z.string().min(1).max(255),
    secret: z.string().min(1).max(8000)
});

/**
 * SpaceConnectorConnector
 *
 * The connector a space uses (no secrets).
 */
export const zSpaceConnectorConnector = z.object({
    authMode: z.string(),
    description: z.string().nullish(),
    disabledByAdmin: z.boolean().optional().default(false),
    isEnabled: z.boolean(),
    isOrg: z.boolean().optional().default(false),
    name: z.string(),
    transport: z.string(),
    url: z.string(),
    uuid: z.string()
});

/**
 * SpaceConnectorSkillRef
 *
 * A flavor skill attached to a connector in a space.
 */
export const zSpaceConnectorSkillRef = z.object({
    description: z.string().nullish(),
    name: z.string(),
    uuid: z.string()
});

/**
 * SpaceConnectorResponse
 *
 * A connector in a space: its settings, this space's values, and its flavor skills.
 */
export const zSpaceConnectorResponse = z.object({
    addedByName: z.string().nullish(),
    config: z.record(z.string(), z.string()).optional(),
    connector: zSpaceConnectorConnector,
    membersWithoutToken: z.int().optional().default(0),
    settings: z.array(zConnectorSetting).optional(),
    skills: z.array(zSpaceConnectorSkillRef).optional(),
    uuid: z.string()
});

/**
 * SpaceConnectorsResponse
 *
 * A space's connectors, and whether I can change them.
 */
export const zSpaceConnectorsResponse = z.object({
    canEdit: z.boolean(),
    connectors: z.array(zSpaceConnectorResponse).optional()
});

/**
 * DataResponse[SpaceConnectorsResponse]
 */
export const zDataResponseSpaceConnectorsResponse = z.object({
    data: zSpaceConnectorsResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SpaceConnectorCreate
 *
 * Add a connector to a space.
 */
export const zSpaceConnectorCreate = z.object({
    config: z.record(z.string(), z.string().nullable()).optional(),
    connectorUuid: z.string()
});

/**
 * DataResponse[SpaceConnectorResponse]
 */
export const zDataResponseSpaceConnectorResponse = z.object({
    data: zSpaceConnectorResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * SpaceConnectorUpdate
 *
 * Set this space's values for the connector's settings.
 */
export const zSpaceConnectorUpdate = z.object({
    config: z.record(z.string(), z.string().nullable()).optional()
});

/**
 * SpaceConnectorSkillsUpdate
 *
 * Replace the flavor skills (in order).
 */
export const zSpaceConnectorSkillsUpdate = z.object({
    skillUuids: z.array(z.string()).max(20).optional()
});

/**
 * AdminConnectorSummary
 *
 * A connector in the admin list.
 */
export const zAdminConnectorSummary = z.object({
    authMode: z.string(),
    color: z.string().nullish(),
    createdAt: z.iso.datetime(),
    createdByName: z.string().nullish(),
    currentUserRole: z.string().nullish(),
    description: z.string().nullish(),
    disabledByAdmin: z.boolean().optional().default(false),
    disabledReason: z.string().nullish(),
    helpSlug: z.string().nullish(),
    hostAllowed: z.boolean().optional().default(true),
    icon: z.string().nullish(),
    integrationName: z.string().nullish(),
    integrationSlug: z.string().nullish(),
    integrationUuid: z.string().nullish(),
    isEnabled: z.boolean(),
    isOrg: z.boolean().optional().default(false),
    isShared: z.boolean().optional().default(false),
    lastError: z.string().nullish(),
    lastRefreshedAt: z.iso.datetime().nullish(),
    name: z.string(),
    setupNotes: z.string().nullish(),
    templateSlug: z.string().nullish(),
    toolCount: z.int().optional().default(0),
    transport: z.string(),
    updatedAt: z.iso.datetime(),
    url: z.string(),
    uuid: z.string()
});

/**
 * DataResponse[list[AdminConnectorSummary]]
 */
export const zDataResponseListAdminConnectorSummary = z.object({
    data: z.array(zAdminConnectorSummary),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ConnectorPolicyUpdate
 *
 * Change the policy (platform admin).
 */
export const zConnectorPolicyUpdate = z.object({
    allowedHosts: z.array(z.string()).max(200).nullish(),
    blockedHosts: z.array(z.string()).max(200).nullish(),
    usersMayAdd: z.boolean().nullish(),
    usersMayAutoRegister: z.boolean().nullish()
});

/**
 * WriteActionSource
 *
 * A source of write actions the policy sets (MHS-973): a connector, or a builtin tool.
 */
export const zWriteActionSource = z.object({
    isSet: z.boolean().optional().default(false),
    key: z.string(),
    kind: z.enum(['connector', 'tool']),
    label: z.string(),
    level: z.enum([
        'ask',
        'chat_only',
        'off'
    ])
});

/**
 * WriteActionPolicyResponse
 *
 * The organization's write-action policy (MHS-973).
 */
export const zWriteActionPolicyResponse = z.object({
    allowPreapproval: z.boolean(),
    allowRemember: z.boolean(),
    defaultLevel: z.enum([
        'ask',
        'chat_only',
        'off'
    ]),
    enabled: z.boolean(),
    sources: z.array(zWriteActionSource).optional()
});

/**
 * DataResponse[WriteActionPolicyResponse]
 */
export const zDataResponseWriteActionPolicyResponse = z.object({
    data: zWriteActionPolicyResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * WriteActionPolicyUpdate
 *
 * Change the write-action policy (platform admin). ``levels`` lists the sources to change.
 */
export const zWriteActionPolicyUpdate = z.object({
    allowPreapproval: z.boolean().nullish(),
    allowRemember: z.boolean().nullish(),
    defaultLevel: z.enum([
        'ask',
        'chat_only',
        'off'
    ]).nullish(),
    enabled: z.boolean().nullish(),
    levels: z.record(z.string(), z.enum([
        'ask',
        'chat_only',
        'off'
    ])).nullish()
});

/**
 * ConnectorDisable
 *
 * Turn a connector off for everyone (platform admin).
 */
export const zConnectorDisable = z.object({
    reason: z.string().max(500).nullish()
});

/**
 * ConnectorAuditEventResponse
 *
 * One thing that happened to a connector.
 */
export const zConnectorAuditEventResponse = z.object({
    action: z.string(),
    actorName: z.string().nullish(),
    at: z.iso.datetime(),
    connectorName: z.string(),
    connectorUuid: z.string().nullish(),
    detail: z.record(z.string(), z.unknown()).optional(),
    id: z.int()
});

/**
 * DataResponse[list[ConnectorAuditEventResponse]]
 */
export const zDataResponseListConnectorAuditEventResponse = z.object({
    data: z.array(zConnectorAuditEventResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * AccessPermission
 *
 * What a tool does with an extension: read data, or act (write) as the user.
 */
export const zAccessPermission = z.enum(['read', 'write']);

/**
 * ConsentPreferenceUpdate
 */
export const zConsentPreferenceUpdate = z.object({
    enabled: z.boolean(),
    permission: zAccessPermission
});

/**
 * AccessPermissionConsent
 */
export const zAccessPermissionConsent = z.object({
    consentMode: z.string(),
    effectiveEnabled: z.boolean(),
    hasExplicitPreference: z.boolean(),
    present: z.boolean()
});

/**
 * ExtensionConsent
 */
export const zExtensionConsent = z.object({
    color: z.string().nullish(),
    description: z.string().nullish(),
    extensionType: z.string(),
    icon: z.string().nullish(),
    name: z.string(),
    read: zAccessPermissionConsent,
    slug: z.string(),
    write: zAccessPermissionConsent
});

/**
 * DataResponse[ExtensionConsent]
 */
export const zDataResponseExtensionConsent = z.object({
    data: zExtensionConsent,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * NotificationResponse
 *
 * Full notification response schema.
 */
export const zNotificationResponse = z.object({
    actor: zNotificationActor.nullable(),
    context: z.array(z.string()).nullable(),
    createdAt: z.iso.datetime(),
    excerpt: z.string().nullable(),
    message: z.string(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    readAt: z.iso.datetime().nullish(),
    subject: zNotificationSubject.nullable(),
    title: z.string(),
    type: z.string(),
    uuid: z.string()
});

/**
 * PaginatedResponse[NotificationResponse]
 */
export const zPaginatedResponseNotificationResponse = z.object({
    data: z.array(zNotificationResponse),
    meta: zPaginationMeta
});

/**
 * UnreadCountResponse
 *
 * Unread notification count.
 */
export const zUnreadCountResponse = z.object({
    count: z.int()
});

/**
 * DataResponse[UnreadCountResponse]
 */
export const zDataResponseUnreadCountResponse = z.object({
    data: zUnreadCountResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * NotificationPreferenceResponse
 *
 * Single notification-type preference.
 */
export const zNotificationPreferenceResponse = z.object({
    desktopEnabled: z.boolean(),
    inAppEnabled: z.boolean(),
    notificationType: z.string()
});

/**
 * DataResponse[list[NotificationPreferenceResponse]]
 */
export const zDataResponseListNotificationPreferenceResponse = z.object({
    data: z.array(zNotificationPreferenceResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * NotificationType
 *
 * Types of notifications.
 */
export const zNotificationType = z.enum([
    'flow_completed',
    'flow_failed',
    'flow_paused',
    'action_approval_requested',
    'chat_shared',
    'brainstorm_shared',
    'data_sync_completed',
    'data_sync_failed',
    'scheduled_flow_triggered',
    'task_shared',
    'deep_research_shared',
    'task_completed',
    'task_failed'
]);

/**
 * NotificationPreferenceUpdate
 *
 * Update body for a single notification-type preference.
 */
export const zNotificationPreferenceUpdate = z.object({
    desktopEnabled: z.boolean().nullish(),
    inAppEnabled: z.boolean().nullish(),
    notificationType: zNotificationType
});

/**
 * NotificationPreferenceBulkUpdate
 *
 * Bulk update of notification preferences.
 */
export const zNotificationPreferenceBulkUpdate = z.object({
    preferences: z.array(zNotificationPreferenceUpdate)
});

/**
 * ActionRequestResponse
 *
 * A write action waiting for (or past) its approval, with where it came from.
 */
export const zActionRequestResponse = z.object({
    arguments: z.record(z.string(), z.unknown()),
    createdAt: z.iso.datetime(),
    decidedAt: z.iso.datetime().nullish(),
    details: z.record(z.string(), z.unknown()).nullish(),
    expiresAt: z.iso.datetime().nullish(),
    flowName: z.string().nullish(),
    flowRunUuid: z.string().nullish(),
    flowUuid: z.string().nullish(),
    status: z.enum([
        'pending',
        'approved',
        'declined',
        'superseded',
        'expired',
        'executed',
        'failed'
    ]),
    surface: z.enum(['chat', 'flow']),
    toolName: z.string(),
    uuid: z.string()
});

/**
 * DataResponse[list[ActionRequestResponse]]
 */
export const zDataResponseListActionRequestResponse = z.object({
    data: z.array(zActionRequestResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * ActionDecisionRequest
 *
 * Approve the action as shown (``run``, with the text the person edited) or decline it.
 */
export const zActionDecisionRequest = z.object({
    decision: z.enum(['run', 'decline']),
    overrides: z.record(z.string(), z.unknown()).nullish(),
    remember: z.boolean().optional().default(false)
});

/**
 * ApiKeyResponse
 */
export const zApiKeyResponse = z.object({
    created_at: z.iso.datetime(),
    expires_at: z.iso.datetime().nullable(),
    id: z.string(),
    is_revoked: z.boolean(),
    key_prefix: z.string(),
    last_used_at: z.iso.datetime().nullable(),
    name: z.string(),
    scopes: z.array(z.string())
});

/**
 * CreateApiKeyRequest
 */
export const zCreateApiKeyRequest = z.object({
    expires_at: z.iso.datetime().nullish(),
    name: z.string().min(1).max(100),
    scopes: z.array(z.string()).optional().default(['completions'])
});

/**
 * ApiKeyCreatedResponse
 *
 * Returned only on creation — includes the full plaintext key.
 */
export const zApiKeyCreatedResponse = z.object({
    created_at: z.iso.datetime(),
    expires_at: z.iso.datetime().nullable(),
    id: z.string(),
    is_revoked: z.boolean(),
    key: z.string(),
    key_prefix: z.string(),
    last_used_at: z.iso.datetime().nullable(),
    name: z.string(),
    scopes: z.array(z.string())
});

/**
 * WorkerResponse
 */
export const zWorkerResponse = z.object({
    available: z.boolean(),
    capabilities: z.record(z.string(), z.unknown()),
    current_job_id: z.string().nullable(),
    id: z.string(),
    last_seen_at: z.iso.datetime().nullable(),
    name: z.string(),
    status: z.string(),
    team_id: z.int().nullable(),
    worker_id: z.string()
});

/**
 * JobStatusResponse
 *
 * Job view for the web monitoring panel (owner-authenticated).
 */
export const zJobStatusResponse = z.object({
    claimed_at: z.iso.datetime().nullable(),
    created_at: z.iso.datetime().nullable(),
    elapsed_seconds: z.number().nullable(),
    error_message: z.string().nullable(),
    finished_at: z.iso.datetime().nullable(),
    id: z.string(),
    prompt_preview: z.string(),
    session_id: z.int().nullable(),
    started_at: z.iso.datetime().nullable(),
    status: z.string(),
    timeout_seconds: z.int(),
    worker_id: z.string().nullable()
});

/**
 * AvailableIntegration
 */
export const zAvailableIntegration = z.object({
    accountLabel: z.string().nullish(),
    color: z.string().nullish(),
    connected: z.boolean(),
    credentialType: z.string(),
    description: z.string().nullish(),
    expiresAt: z.iso.datetime().nullish(),
    icon: z.string().nullish(),
    name: z.string(),
    slug: z.string()
});

/**
 * AuthorizeResponse
 */
export const zAuthorizeResponse = z.object({
    authorizeUrl: z.string()
});

/**
 * DataResponse[list[ExtensionConsent]]
 */
export const zDataResponseListExtensionConsent = z.object({
    data: z.array(zExtensionConsent),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * TokenAuthorizationResponse
 *
 * A user's cached delegated authorization for a purpose.
 *
 * Never exposes the encrypted cache / token material — only metadata needed for
 * the dashboard (which purpose, when authorized, last used, validity, and the
 * data sources it drives).
 */
export const zTokenAuthorizationResponse = z.object({
    authorizedAt: z.iso.datetime(),
    cacheType: z.string(),
    excludedTools: z.array(z.string()),
    isValid: z.boolean(),
    lastUsedAt: z.iso.datetime(),
    purpose: z.string(),
    scopes: z.array(z.string()),
    syncTargets: z.array(zSyncTargetSummary).optional().default([]),
    uuid: z.string()
});

/**
 * DataResponse[list[TokenAuthorizationResponse]]
 */
export const zDataResponseListTokenAuthorizationResponse = z.object({
    data: z.array(zTokenAuthorizationResponse),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * DataResponse[bool]
 */
export const zDataResponseBool = z.object({
    data: z.boolean(),
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * MySpendByFeature
 *
 * User spend breakdown by feature.
 */
export const zMySpendByFeature = z.object({
    costUsd: z.number(),
    feature: z.string(),
    requests: z.int()
});

/**
 * MySpendByModel
 *
 * User spend breakdown by model.
 */
export const zMySpendByModel = z.object({
    costUsd: z.number(),
    modelId: z.string(),
    requests: z.int(),
    tokensInput: z.int(),
    tokensOutput: z.int()
});

/**
 * MonthlySpend
 *
 * Spend for a single month.
 */
export const zMonthlySpend = z.object({
    costUsd: z.number(),
    month: z.string(),
    requests: z.int()
});

/**
 * MySpendResponse
 *
 * User's own spend summary.
 */
export const zMySpendResponse = z.object({
    byFeature: z.array(zMySpendByFeature).optional(),
    byModel: z.array(zMySpendByModel).optional(),
    currentMonthCostUsd: z.number(),
    currentMonthRequests: z.int(),
    currentMonthTokensInput: z.int(),
    currentMonthTokensOutput: z.int(),
    monthlyTrend: z.array(zMonthlySpend).optional()
});

/**
 * DataResponse[MySpendResponse]
 */
export const zDataResponseMySpendResponse = z.object({
    data: zMySpendResponse,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * MessageStartData
 */
export const zMessageStartDataWritable = z.record(z.string(), z.unknown());

/**
 * NotificationPushedData
 *
 * A new notification (camelCase keys, as the REST list returns them).
 */
export const zNotificationPushedDataWritable = z.object({
    createdAt: z.iso.datetime(),
    message: z.string(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    readAt: z.iso.datetime().nullish().default(null),
    showDesktop: z.boolean().nullish().default(null),
    title: z.string(),
    type: z.string(),
    uuid: z.string()
});

/**
 * NotificationPushedEvent
 */
export const zNotificationPushedEventWritable = z.object({
    data: zNotificationPushedDataWritable,
    event: z.literal('notification')
});

/**
 * One SSE frame from the user event stream: `event:` and parsed `data:`.
 */
export const zUserStreamEventWritable = z.discriminatedUnion('event', [
    zNotificationPushedEventWritable,
    zUnreadCountEvent,
    zOperationStatusChangedEvent,
    zChatTitleUpdatedEvent,
    zHeartbeatEvent
]);

/**
 * OperationResumedData
 */
export const zOperationResumedDataWritable = z.record(z.string(), z.unknown());

/**
 * AgentResponse
 *
 * Full agent response schema.
 */
export const zAgentResponseWritable = z.object({
    canEdit: z.boolean().optional().default(false),
    canManage: z.boolean().optional().default(false),
    color: z.string(),
    createdAt: z.iso.datetime(),
    createdByUuid: z.string(),
    currentUserRole: z.string().nullish(),
    currentVersionNumber: z.int().nullish(),
    dataCollections: z.array(zDataCollectionSummary).optional(),
    defaultBrand: zBrandRef.nullish(),
    defaultFileBlobPaths: z.array(z.string()).nullish(),
    defaultInputPrompt: z.string().nullish(),
    description: z.string().nullish(),
    icon: z.string(),
    modelId: z.string(),
    name: z.string(),
    ownedByProject: zProjectRef.nullish(),
    settings: z.record(z.string(), z.unknown()).optional(),
    sharedInProjects: z.array(zProjectRef).optional(),
    slashCommands: z.array(zSlashCommandSchema).optional(),
    systemPrompt: z.string(),
    teamUuid: z.string(),
    tools: z.array(zToolSummary).optional(),
    updatedAt: z.iso.datetime(),
    usageCount: z.int(),
    uuid: z.string()
});

/**
 * DataResponse[AgentResponse]
 */
export const zDataResponseAgentResponseWritable = z.object({
    data: zAgentResponseWritable,
    meta: z.record(z.string(), z.unknown()).nullish(),
    operationId: z.string().nullish()
});

/**
 * LicenseResume
 *
 * Resume the platform after suspension (MH admin only).
 */
export const zLicenseResumeWritable = z.record(z.string(), z.unknown());

/**
 * NotificationResponse
 *
 * Full notification response schema.
 */
export const zNotificationResponseWritable = z.object({
    createdAt: z.iso.datetime(),
    message: z.string(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    readAt: z.iso.datetime().nullish(),
    title: z.string(),
    type: z.string(),
    uuid: z.string()
});

/**
 * PaginatedResponse[NotificationResponse]
 */
export const zPaginatedResponseNotificationResponseWritable = z.object({
    data: z.array(zNotificationResponseWritable),
    meta: zPaginationMeta
});

export const zListActionRequestsQuery = z.object({
    status: z.enum(['pending', 'all']).optional().default('pending')
});

/**
 * Successful Response
 */
export const zListActionRequestsResponse = zDataResponseListActionRequestResponse;

export const zDecideActionRequestBody = zActionDecisionRequest;

export const zDecideActionRequestPath = z.object({
    request_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDecideActionRequestResponse = zSrcSchemasBaseMessageResponse;

export const zAdminActionSummaryQuery = z.object({
    start_date: z.iso.datetime().nullish(),
    end_date: z.iso.datetime().nullish()
});

/**
 * Successful Response
 */
export const zAdminActionSummaryResponse = zDataResponseListActionSummaryRow;

/**
 * Successful Response
 */
export const zListBudgetConfigsResponse = zDataResponseListBudgetConfigResponse;

export const zCreateBudgetConfigBody = zBudgetConfigCreate;

/**
 * Successful Response
 */
export const zCreateBudgetConfigResponse = zDataResponseBudgetConfigResponse;

export const zDeleteBudgetConfigPath = z.object({
    budget_id: z.int()
});

/**
 * Successful Response
 */
export const zDeleteBudgetConfigResponse = zSrcSchemasBaseMessageResponse;

export const zUpdateBudgetConfigBody = zBudgetConfigUpdate;

export const zUpdateBudgetConfigPath = z.object({
    budget_id: z.int()
});

/**
 * Successful Response
 */
export const zUpdateBudgetConfigResponse = zDataResponseBudgetConfigResponse;

/**
 * Successful Response
 */
export const zGetCompanyProfileResponse = zDataResponseUnionCompanyProfileResponseNoneType;

export const zUpsertCompanyProfileBody = zCompanyProfileUpdate;

/**
 * Successful Response
 */
export const zUpsertCompanyProfileResponse = zDataResponseCompanyProfileResponse;

/**
 * Successful Response
 */
export const zPreviewCompanyContextResponse = zDataResponseCompanyContextPreview;

export const zUploadLogoBody = zBodyUploadLogoApiV1AdminCompanyLogoPost;

/**
 * Successful Response
 */
export const zUploadLogoResponse = zDataResponseDict;

/**
 * Successful Response
 */
export const zAdminListConnectorsResponse = zDataResponseListAdminConnectorSummary;

export const zAdminConnectorAuditQuery = z.object({
    connector_uuid: z.string().nullish(),
    beforeId: z.int().nullish(),
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Successful Response
 */
export const zAdminConnectorAuditResponse = zDataResponseListConnectorAuditEventResponse;

/**
 * Successful Response
 */
export const zAdminGetConnectorPolicyResponse = zDataResponseConnectorPolicyResponse;

export const zAdminUpdateConnectorPolicyBody = zConnectorPolicyUpdate;

/**
 * Successful Response
 */
export const zAdminUpdateConnectorPolicyResponse = zDataResponseConnectorPolicyResponse;

/**
 * Successful Response
 */
export const zAdminGetWriteActionPolicyResponse = zDataResponseWriteActionPolicyResponse;

export const zAdminUpdateWriteActionPolicyBody = zWriteActionPolicyUpdate;

/**
 * Successful Response
 */
export const zAdminUpdateWriteActionPolicyResponse = zDataResponseWriteActionPolicyResponse;

export const zAdminDisableConnectorBody = zConnectorDisable;

export const zAdminDisableConnectorPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAdminDisableConnectorResponse = z.void();

export const zAdminEnableConnectorPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAdminEnableConnectorResponse = z.void();

export const zGetContentFilterAnalyticsQuery = z.object({
    start_date: z.iso.datetime().nullish(),
    end_date: z.iso.datetime().nullish()
});

/**
 * Successful Response
 */
export const zGetContentFilterAnalyticsResponse = zDataResponseContentFilterAnalyticsResponse;

export const zListContentFilterEventsQuery = z.object({
    start_date: z.iso.datetime().nullish(),
    end_date: z.iso.datetime().nullish(),
    source: z.enum(['prompt', 'completion']).nullish(),
    category: z.string().max(50).nullish(),
    severity: z.enum([
        'safe',
        'low',
        'medium',
        'high'
    ]).nullish(),
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zListContentFilterEventsResponse = zPaginatedResponseContentFilterEventItem;

export const zReviewContentFilterConversationPath = z.object({
    event_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zReviewContentFilterConversationResponse = zDataResponseContentFilterReviewResponse;

export const zCorrelateContentFilterEventPath = z.object({
    event_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCorrelateContentFilterEventResponse = zDataResponseContentFilterCorrelationResponse;

/**
 * Successful Response
 */
export const zGetGovernanceOverviewResponse = zDataResponseGovernanceOverviewResponse;

/**
 * Successful Response
 */
export const zGetLicenseDetailResponse = zDataResponseLicenseResponse;

export const zUpdateLicenseBody = zLicenseUpdate;

/**
 * Successful Response
 */
export const zUpdateLicenseResponse = zDataResponseLicenseResponse;

export const zResumePlatformBody = zLicenseResumeWritable;

/**
 * Successful Response
 */
export const zResumePlatformResponse = zDataResponseLicenseResponse;

/**
 * Successful Response
 */
export const zGetLicenseStatusResponse = zDataResponseLicenseStatusResponse;

export const zSuspendPlatformBody = zLicenseSuspend;

/**
 * Successful Response
 */
export const zSuspendPlatformResponse = zDataResponseLicenseResponse;

/**
 * Successful Response
 */
export const zAdminMicrosoftPermissionsResponse = zDataResponseMicrosoftPermissions;

export const zAdminListModelsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zAdminListModelsResponse = zPaginatedResponseAiModelResponse;

export const zAdminCreateModelBody = zAiModelCreate;

/**
 * Successful Response
 */
export const zAdminCreateModelResponse = zDataResponseAiModelResponse;

export const zAdminSuggestModelBody = zAiModelSuggestRequest;

/**
 * Successful Response
 */
export const zAdminSuggestModelResponse = zDataResponseAiModelSuggestResponse;

/**
 * Successful Response
 */
export const zAdminListTierDefaultsResponse = zDataResponseModelTierDefaultsResponse;

export const zAdminUpdateTierDefaultBody = zModelTierDefaultUpdate;

export const zAdminUpdateTierDefaultPath = z.object({
    tier: z.string()
});

/**
 * Successful Response
 */
export const zAdminUpdateTierDefaultResponse = zDataResponseModelTierDefaultResponse;

export const zAdminDeleteModelPath = z.object({
    model_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminDeleteModelResponse = zSrcSchemasBaseMessageResponse;

export const zAdminGetModelPath = z.object({
    model_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminGetModelResponse = zDataResponseAiModelResponse;

export const zAdminUpdateModelBody = zAiModelUpdate;

export const zAdminUpdateModelPath = z.object({
    model_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminUpdateModelResponse = zDataResponseAiModelResponse;

export const zAdminReassignModelBody = zModelReassignRequest;

export const zAdminReassignModelPath = z.object({
    model_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminReassignModelResponse = zSrcSchemasBaseMessageResponse;

export const zAdminListModelReferencesPath = z.object({
    model_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminListModelReferencesResponse = zDataResponseModelReferencesResponse;

export const zGetPlatformCostsQuery = z.object({
    days: z.int().gte(7).lte(90).optional().default(30)
});

/**
 * Successful Response
 */
export const zGetPlatformCostsResponse = zDataResponsePlatformCostsResponse;

/**
 * Successful Response
 */
export const zPlatformListOauthClientsResponse = zDataResponseListOAuthClientResponse;

export const zPlatformCreateOauthClientBody = zOAuthClientCreate;

/**
 * Successful Response
 */
export const zPlatformCreateOauthClientResponse = zDataResponseOAuthClientCreated;

export const zPlatformDeleteOauthClientPath = z.object({
    client_uuid: z.string()
});

/**
 * Successful Response
 */
export const zPlatformDeleteOauthClientResponse = z.void();

export const zPlatformUpdateOauthClientBody = zOAuthClientUpdate;

export const zPlatformUpdateOauthClientPath = z.object({
    client_uuid: z.string()
});

/**
 * Successful Response
 */
export const zPlatformUpdateOauthClientResponse = zDataResponseOAuthClientResponse;

export const zAdminGetUsageQuery = z.object({
    start_date: z.iso.datetime().nullish(),
    end_date: z.iso.datetime().nullish()
});

/**
 * Successful Response
 */
export const zAdminGetUsageResponse = zDataResponseUsageResponse;

export const zAdminListUsersQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    search: z.string().nullish(),
    include_inactive: z.boolean().optional().default(true)
});

/**
 * Successful Response
 */
export const zAdminListUsersResponse = zPaginatedResponseUserResponse;

export const zAdminCreateUserBody = zUserCreate;

/**
 * Successful Response
 */
export const zAdminCreateUserResponse = zDataResponseUserResponse;

export const zAdminDeleteUserPath = z.object({
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAdminDeleteUserResponse = zSrcSchemasBaseMessageResponse;

export const zAdminUpdateUserPath = z.object({
    user_uuid: z.string()
});

export const zAdminUpdateUserQuery = z.object({
    role: z.string().regex(/^(install_admin|platform_admin|member)$/).nullish(),
    is_active: z.boolean().nullish(),
    deactivation_reason: z.string().max(500).nullish()
});

/**
 * Successful Response
 */
export const zAdminUpdateUserResponse = zDataResponseUserResponse;

/**
 * Successful Response
 */
export const zClearAllAgentMemoryResponse = z.void();

/**
 * Successful Response
 */
export const zListAgentMemoriesResponse = zDataResponseAgentMemoryList;

export const zClearAgentMemoryPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zClearAgentMemoryResponse = z.void();

export const zDeleteAgentMemoryPath = z.object({
    entry_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteAgentMemoryResponse = z.void();

export const zListAgentsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    team_uuid: z.string().nullish(),
    search: z.string().nullish(),
    project_uuid: z.string().nullish(),
    exclude_project_agents: z.boolean().optional().default(false)
});

/**
 * Successful Response
 */
export const zListAgentsResponse = zPaginatedResponseAgentSummary;

export const zCreateAgentBody = zAgentCreate;

/**
 * Successful Response
 */
export const zCreateAgentResponse = zDataResponseAgentResponse;

/**
 * Successful Response
 */
export const zListAgentPinsResponse = zDataResponseListAgentSummary;

export const zAddAgentPinBody = zAgentPinAdd;

/**
 * Successful Response
 */
export const zAddAgentPinResponse = z.void();

export const zRemoveAgentPinPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveAgentPinResponse = z.void();

export const zDeleteAgentPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteAgentResponse = z.void();

export const zGetAgentPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetAgentResponse = zDataResponseAgentResponse;

export const zUpdateAgentBody = zAgentUpdate;

export const zUpdateAgentPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateAgentResponse = zDataResponseAgentResponse;

export const zListAgentConnectorsPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListAgentConnectorsResponse = zDataResponseListAgentConnectorSummary;

export const zSetAgentConnectorsBody = zAgentConnectorsUpdate;

export const zSetAgentConnectorsPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSetAgentConnectorsResponse = zDataResponseListAgentConnectorSummary;

export const zAgentDeleteImpactPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAgentDeleteImpactResponse2 = zDataResponseAgentDeleteImpactResponse;

export const zGetAgentMembersPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetAgentMembersResponse = zDataResponseAgentMembersResponse;

export const zAddAgentTeamMemberBody = zAddAgentTeamMemberRequest;

export const zAddAgentTeamMemberPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddAgentTeamMemberResponse = zDataResponseAgentTeamMemberResponse;

export const zRemoveAgentTeamMemberPath = z.object({
    agent_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveAgentTeamMemberResponse = z.void();

export const zUpdateAgentTeamMemberBody = zUpdateAgentMemberRoleRequest;

export const zUpdateAgentTeamMemberPath = z.object({
    agent_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateAgentTeamMemberResponse = zDataResponseAgentTeamMemberResponse;

export const zAddAgentUserMemberBody = zAddAgentUserMemberRequest;

export const zAddAgentUserMemberPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddAgentUserMemberResponse = zDataResponseAgentUserMemberResponse;

export const zRemoveAgentUserMemberPath = z.object({
    agent_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveAgentUserMemberResponse = z.void();

export const zUpdateAgentUserMemberBody = zUpdateAgentMemberRoleRequest;

export const zUpdateAgentUserMemberPath = z.object({
    agent_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateAgentUserMemberResponse = zDataResponseAgentUserMemberResponse;

export const zListAgentSkillsPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListAgentSkillsResponse = zDataResponseListAgentSkillResponse;

/**
 * Data
 */
export const zAttachSkillsToAgentBody = z.array(zAgentSkillAttach);

export const zAttachSkillsToAgentPath = z.object({
    agent_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAttachSkillsToAgentResponse = zDataResponseListAgentSkillResponse;

export const zDetachSkillFromAgentPath = z.object({
    agent_uuid: z.string(),
    skill_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDetachSkillFromAgentResponse = z.void();

export const zUpdateAgentSkillBody = zAgentSkillUpdate;

export const zUpdateAgentSkillPath = z.object({
    agent_uuid: z.string(),
    skill_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateAgentSkillResponse = zDataResponseAgentSkillResponse;

export const zStreamAgentBody = zAgentInvokeRequest;

export const zStreamAgentPath = z.object({
    agent_uuid: z.string()
});

/**
 * Operation started; stream it from /operations/{operation_id}/stream.
 */
export const zStreamAgentResponse = zOperationStarted;

/**
 * Response List Api Keys Api V1 Api Keys Get
 *
 * Successful Response
 */
export const zListApiKeysResponse = z.array(zApiKeyResponse);

export const zCreateApiKeyBody = zCreateApiKeyRequest;

/**
 * Successful Response
 */
export const zCreateApiKeyResponse = zApiKeyCreatedResponse;

export const zRevokeApiKeyPath = z.object({
    key_id: z.string()
});

/**
 * Successful Response
 */
export const zRevokeApiKeyResponse = z.void();

/**
 * Successful Response
 */
export const zGetCurrentUserInfoResponse = zDataResponseCurrentUserResponse;

export const zUpdateUserSettingsBody = zUserSettingsUpdate;

/**
 * Successful Response
 */
export const zUpdateUserSettingsResponse = zDataResponseUserResponse;

export const zSearchUsersQuery = z.object({
    q: z.string().min(1).max(100)
});

/**
 * Successful Response
 */
export const zSearchUsersResponse = zDataResponseListUserSearchResult;

/**
 * Successful Response
 */
export const zListMyAuthorizationsResponse = zDataResponseListTokenAuthorizationResponse;

/**
 * Successful Response
 */
export const zListAllAuthorizationsResponse = zDataResponseListAdminTokenAuthorizationResponse;

export const zRevokeMyAuthorizationPath = z.object({
    purpose: z.string()
});

/**
 * Successful Response
 */
export const zRevokeMyAuthorizationResponse = zDataResponseBool;

export const zListBrandsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zListBrandsResponse = zPaginatedResponseBrandSummary;

export const zCreateBrandBody = zBrandCreate;

/**
 * Successful Response
 */
export const zCreateBrandResponse = zDataResponseBrandResponse;

export const zExtractBrandBody = zBodyExtractBrandApiV1BrandsExtractPost;

/**
 * Successful Response
 */
export const zExtractBrandResponse = zDataResponseBrandExtractionResponse;

export const zDeleteBrandPath = z.object({
    brand_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteBrandResponse = zSrcSchemasBaseMessageResponse;

export const zGetBrandPath = z.object({
    brand_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetBrandResponse = zDataResponseBrandResponse;

export const zUpdateBrandBody = zBrandUpdate;

export const zUpdateBrandPath = z.object({
    brand_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateBrandResponse = zDataResponseBrandResponse;

export const zUploadBrandLogoBody = zBodyUploadBrandLogoApiV1BrandsBrandUuidLogoPost;

export const zUploadBrandLogoPath = z.object({
    brand_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUploadBrandLogoResponse = zDataResponseBrandResponse;

export const zGetBrandMembersPath = z.object({
    brand_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetBrandMembersResponse = zDataResponseBrandMembersResponse;

export const zAddBrandTeamMemberBody = zAddBrandTeamMemberRequest;

export const zAddBrandTeamMemberPath = z.object({
    brand_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddBrandTeamMemberResponse = zDataResponseBrandTeamMemberResponse;

export const zRemoveBrandTeamMemberPath = z.object({
    brand_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveBrandTeamMemberResponse = z.void();

export const zUpdateBrandTeamMemberBody = zUpdateBrandMemberRoleRequest;

export const zUpdateBrandTeamMemberPath = z.object({
    brand_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateBrandTeamMemberResponse = zDataResponseBrandTeamMemberResponse;

export const zAddBrandUserMemberBody = zAddBrandUserMemberRequest;

export const zAddBrandUserMemberPath = z.object({
    brand_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddBrandUserMemberResponse = zDataResponseBrandUserMemberResponse;

export const zRemoveBrandUserMemberPath = z.object({
    brand_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveBrandUserMemberResponse = z.void();

export const zUpdateBrandUserMemberBody = zUpdateBrandMemberRoleRequest;

export const zUpdateBrandUserMemberPath = z.object({
    brand_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateBrandUserMemberResponse = zDataResponseBrandUserMemberResponse;

export const zListBrandTemplatesPath = z.object({
    brand_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListBrandTemplatesResponse = zDataResponseListBrandTemplateResponse;

export const zDeleteBrandTemplatePath = z.object({
    brand_uuid: z.string(),
    template_format: z.enum([
        'pptx',
        'docx',
        'xlsx'
    ])
});

/**
 * Successful Response
 */
export const zDeleteBrandTemplateResponse = z.void();

export const zUploadBrandTemplateBody = zBodyUploadBrandTemplateApiV1BrandsBrandUuidTemplatesTemplateFormatPut;

export const zUploadBrandTemplatePath = z.object({
    brand_uuid: z.string(),
    template_format: z.enum([
        'pptx',
        'docx',
        'xlsx'
    ])
});

/**
 * Successful Response
 */
export const zUploadBrandTemplateResponse = zDataResponseBrandTemplateResponse;

export const zDownloadBrandTemplatePath = z.object({
    brand_uuid: z.string(),
    template_format: z.enum([
        'pptx',
        'docx',
        'xlsx'
    ])
});

export const zGetBrandTemplateThumbnailPath = z.object({
    brand_uuid: z.string(),
    template_format: z.enum([
        'pptx',
        'docx',
        'xlsx'
    ])
});

export const zGetAvailabilityBody = zAvailabilityRequest;

/**
 * Successful Response
 */
export const zGetAvailabilityResponse = zAvailabilityResponse;

export const zListMessageAttachmentsPath = z.object({
    message_uuid: z.string().regex(/^[0-9a-fA-F-]{36}$/)
});

export const zListMessageAttachmentsQuery = z.object({
    cursor: z.string().max(64).nullish(),
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Successful Response
 */
export const zListMessageAttachmentsResponse = zAttachmentPage;

/**
 * AttachmentStatusRequest
 *
 * Exactly one selector: explicit ids, a batch, or a message (the latter two paged by cursor).
 */
export const zAttachmentStatusBody = z.object({
    batchId: z.string().regex(/^[0-9a-fA-F-]{36}$/).nullish(),
    cursor: z.string().max(64).nullish(),
    fileIds: z.array(z.string().regex(/^[0-9a-fA-F-]{36}$/)).max(200).nullish(),
    limit: z.int().gte(1).lte(200).nullish(),
    messageId: z.string().regex(/^[0-9a-fA-F-]{36}$/).nullish()
});

/**
 * Successful Response
 */
export const zAttachmentStatusResponse2 = zDataResponseAttachmentStatusResponse;

/**
 * Successful Response
 */
export const zGetAttachmentLimitsResponse = zAttachmentLimitsResponse;

export const zChatListSessionsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    starred_only: z.boolean().optional().default(false),
    agent_uuid: z.string().nullish(),
    tags: z.string().nullish(),
    project_uuid: z.string().nullish(),
    exclude_project_sessions: z.boolean().optional().default(false),
    search: z.string().max(200).nullish(),
    space_uuid: z.string().nullish(),
    source: z.enum(['user', 'build']).optional().default('user')
});

/**
 * Successful Response
 */
export const zChatListSessionsResponse = zPaginatedResponseChatSessionSummary;

export const zChatCreateSessionBody = zChatSessionCreate;

/**
 * Successful Response
 */
export const zChatCreateSessionResponse = zDataResponseChatSessionResponse;

export const zEditWithAiBody = zEditWithAiRequest;

/**
 * Successful Response
 */
export const zEditWithAiResponse = zDataResponseChatSessionResponse;

export const zChatDeleteSessionPath = z.object({
    session_uuid: z.string()
});

/**
 * Successful Response
 */
export const zChatDeleteSessionResponse = z.void();

export const zChatGetSessionPath = z.object({
    session_uuid: z.string()
});

export const zChatGetSessionQuery = z.object({
    attachmentsLimit: z.int().gte(1).lte(200).nullish()
});

/**
 * Successful Response
 */
export const zChatGetSessionResponse = zDataResponseChatSessionResponse;

export const zChatUpdateSessionBody = zChatSessionUpdate;

export const zChatUpdateSessionPath = z.object({
    session_uuid: z.string()
});

/**
 * Successful Response
 */
export const zChatUpdateSessionResponse = zDataResponseChatSessionResponse;

export const zChatUploadAttachmentsBody = zBodyUploadAttachmentsApiV1ChatSessionsSessionUuidAttachmentsPost;

export const zChatUploadAttachmentsPath = z.object({
    session_uuid: z.string()
});

/**
 * Successful Response
 */
export const zChatUploadAttachmentsResponse = zDataResponseListAttachmentResponse;

export const zListSessionDocumentsPath = z.object({
    session_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListSessionDocumentsResponse = zDataResponseListDocumentLineageResponse;

export const zMarkDraftAppliedBody = zDraftApplied;

export const zMarkDraftAppliedPath = z.object({
    session_uuid: z.string(),
    draft_id: z.string()
});

/**
 * Successful Response
 */
export const zMarkDraftAppliedResponse = z.void();

export const zForkSessionPath = z.object({
    session_uuid: z.string()
});

/**
 * Successful Response
 */
export const zForkSessionResponse = zDataResponseChatSessionResponse;

export const zListMessagesPath = z.object({
    session_uuid: z.string()
});

export const zListMessagesQuery = z.object({
    attachmentsLimit: z.int().gte(1).lte(200).nullish()
});

/**
 * Successful Response
 */
export const zListMessagesResponse = zDataResponseListMessageResponse;

export const zChatStreamMessageBody = zMessageCreate;

export const zChatStreamMessagePath = z.object({
    session_uuid: z.string()
});

/**
 * Operation started; stream it from /operations/{operation_id}/stream.
 */
export const zChatStreamMessageResponse = zOperationStarted;

export const zChatGetSessionSharingPath = z.object({
    session_uuid: z.string()
});

/**
 * Successful Response
 */
export const zChatGetSessionSharingResponse = zDataResponseSessionSharingResponse;

export const zChatCreateShareLinkBody = zCreateShareLinkRequest;

export const zChatCreateShareLinkPath = z.object({
    session_uuid: z.string()
});

/**
 * Successful Response
 */
export const zChatCreateShareLinkResponse = zDataResponseShareLinkResponse;

export const zChatDeactivateShareLinkPath = z.object({
    session_uuid: z.string(),
    link_uuid: z.string()
});

/**
 * Successful Response
 */
export const zChatDeactivateShareLinkResponse = z.void();

export const zSetSessionSpaceShareBody = zSpaceShareUpdate;

export const zSetSessionSpaceSharePath = z.object({
    session_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSetSessionSpaceShareResponse = zDataResponseSessionSharingResponse;

export const zChatShareWithUserBody = zShareUserRequest;

export const zChatShareWithUserPath = z.object({
    session_uuid: z.string()
});

/**
 * Successful Response
 */
export const zChatShareWithUserResponse = zDataResponseShareResponse;

export const zChatRemoveSharePath = z.object({
    session_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zChatRemoveShareResponse = z.void();

/**
 * Successful Response
 */
export const zChatListSharedWithMeResponse = zDataResponseListSharedSessionSummary;

export const zChatAccessShareLinkPath = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zChatAccessShareLinkResponse = zDataResponseChatSessionResponse;

export const zListConnectorsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(50),
    search: z.string().max(200).nullish(),
    include_org: z.boolean().optional().default(false)
});

/**
 * Successful Response
 */
export const zListConnectorsResponse = zPaginatedResponseConnectorSummary;

export const zCreateConnectorBody = zConnectorCreate;

/**
 * Successful Response
 */
export const zCreateConnectorResponse = zDataResponseConnectorResponse;

export const zCreateConnectorFromTemplateBody = zConnectorFromTemplate;

/**
 * Successful Response
 */
export const zCreateConnectorFromTemplateResponse = zDataResponseConnectorFromTemplateResponse;

export const zCreateConnectorInstanceBody = zConnectorInstanceCreate;

/**
 * Successful Response
 */
export const zCreateConnectorInstanceResponse = zDataResponseConnectorResponse;

/**
 * Successful Response
 */
export const zListConnectorIntegrationsResponse = zDataResponseListConnectorIntegrationOption;

/**
 * Successful Response
 */
export const zListMyConnectorConnectionsResponse = zDataResponseListMyConnectorConnection;

/**
 * Successful Response
 */
export const zGetConnectorPolicyResponse = zDataResponseConnectorPolicyResponse;

/**
 * Successful Response
 */
export const zListConnectorTemplatesResponse = zDataResponseListConnectorTemplateResponse;

export const zDeleteConnectorPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteConnectorResponse = z.void();

export const zGetConnectorDetailPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetConnectorDetailResponse = zDataResponseConnectorResponse;

export const zUpdateConnectorBody = zConnectorUpdate;

export const zUpdateConnectorPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateConnectorResponse = zDataResponseConnectorResponse;

export const zGetConnectorMembersPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetConnectorMembersResponse = zDataResponseConnectorMembersResponse;

export const zAddConnectorTeamMemberBody = zAddConnectorTeamMemberRequest;

export const zAddConnectorTeamMemberPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddConnectorTeamMemberResponse = zDataResponseConnectorTeamMemberResponse;

export const zRemoveConnectorTeamMemberPath = z.object({
    connector_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveConnectorTeamMemberResponse = z.void();

export const zUpdateConnectorTeamMemberBody = zUpdateConnectorMemberRoleRequest;

export const zUpdateConnectorTeamMemberPath = z.object({
    connector_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateConnectorTeamMemberResponse = zDataResponseConnectorTeamMemberResponse;

export const zAddConnectorUserMemberBody = zAddConnectorUserMemberRequest;

export const zAddConnectorUserMemberPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddConnectorUserMemberResponse = zDataResponseConnectorUserMemberResponse;

export const zRemoveConnectorUserMemberPath = z.object({
    connector_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveConnectorUserMemberResponse = z.void();

export const zUpdateConnectorUserMemberBody = zUpdateConnectorMemberRoleRequest;

export const zUpdateConnectorUserMemberPath = z.object({
    connector_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateConnectorUserMemberResponse = zDataResponseConnectorUserMemberResponse;

export const zClearMyConnectorCredentialPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zClearMyConnectorCredentialResponse = z.void();

export const zSetMyConnectorCredentialBody = zConnectorSecretValue;

export const zSetMyConnectorCredentialPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSetMyConnectorCredentialResponse = z.void();

export const zStartConnectorOauthBody = zConnectorOAuthStart;

export const zStartConnectorOauthPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zStartConnectorOauthResponse = zDataResponseConnectorOAuthStartResponse;

export const zRefreshConnectorToolsPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRefreshConnectorToolsResponse = zDataResponseListConnectorToolResponse;

export const zClearConnectorSecretPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zClearConnectorSecretResponse = z.void();

export const zSetConnectorSecretBody = zConnectorSecretValue;

export const zSetConnectorSecretPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSetConnectorSecretResponse = z.void();

export const zListConnectorToolsPath = z.object({
    connector_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListConnectorToolsResponse = zDataResponseListConnectorToolResponse;

export const zUpdateConnectorToolBody = zConnectorToolUpdate;

export const zUpdateConnectorToolPath = z.object({
    connector_uuid: z.string(),
    tool_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateConnectorToolResponse = zDataResponseConnectorToolResponse;

export const zListDataSourcesQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    team_uuid: z.string().nullish(),
    status: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListDataSourcesResponse = zPaginatedResponseDataCollectionSummary;

export const zCreateDataSourceBody = zDataCollectionCreate;

/**
 * Successful Response
 */
export const zCreateDataSourceResponse = zDataResponseDataCollectionResponse;

export const zDeleteDataSourcePath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteDataSourceResponse = zSrcSchemasBaseMessageResponse;

export const zGetDataSourcePath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetDataSourceResponse = zDataResponseDataCollectionResponse;

export const zUpdateDataSourceBody = zDataCollectionUpdate;

export const zUpdateDataSourcePath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateDataSourceResponse = zDataResponseDataCollectionResponse;

export const zUploadFilesBody = zBodyUploadFilesApiV1DataSourcesCollectionUuidFilesPost;

export const zUploadFilesPath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUploadFilesResponse = zDataResponseListCollectionFileSummary;

export const zDeleteFilePath = z.object({
    collection_uuid: z.string(),
    file_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteFileResponse = zSrcSchemasBaseMessageResponse;

export const zGetFileDownloadUrlPath = z.object({
    collection_uuid: z.string(),
    file_uuid: z.string()
});

export const zGetFileDownloadUrlQuery = z.object({
    expiry_hours: z.int().gte(1).lte(4).optional().default(1)
});

/**
 * Successful Response
 */
export const zGetFileDownloadUrlResponse = zDataResponseDict;

export const zGetFileMarkdownPath = z.object({
    collection_uuid: z.string(),
    file_uuid: z.string()
});

export const zGetDataSourceMembersPath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetDataSourceMembersResponse = zDataResponseDataSourceMembersResponse;

export const zAddDataSourceTeamMemberBody = zAddDataSourceTeamMemberRequest;

export const zAddDataSourceTeamMemberPath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddDataSourceTeamMemberResponse = zDataResponseDataSourceTeamMemberResponse;

export const zRemoveDataSourceTeamMemberPath = z.object({
    collection_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveDataSourceTeamMemberResponse = z.void();

export const zUpdateDataSourceTeamMemberBody = zUpdateDataSourceMemberRoleRequest;

export const zUpdateDataSourceTeamMemberPath = z.object({
    collection_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateDataSourceTeamMemberResponse = zDataResponseDataSourceTeamMemberResponse;

export const zAddDataSourceUserMemberBody = zAddDataSourceUserMemberRequest;

export const zAddDataSourceUserMemberPath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddDataSourceUserMemberResponse = zDataResponseDataSourceUserMemberResponse;

export const zRemoveDataSourceUserMemberPath = z.object({
    collection_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveDataSourceUserMemberResponse = z.void();

export const zUpdateDataSourceUserMemberBody = zUpdateDataSourceMemberRoleRequest;

export const zUpdateDataSourceUserMemberPath = z.object({
    collection_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateDataSourceUserMemberResponse = zDataResponseDataSourceUserMemberResponse;

export const zAddNoteBody = zNoteCreate;

export const zAddNotePath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddNoteResponse = zDataResponseCollectionFileSummary;

export const zUpdateNoteBody = zNoteUpdate;

export const zUpdateNotePath = z.object({
    collection_uuid: z.string(),
    file_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateNoteResponse = zDataResponseCollectionFileSummary;

export const zSyncDataSourcePath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSyncDataSourceResponse = zSrcSchemasBaseMessageResponse;

export const zAddWebsiteUrlBody = zAddWebsiteUrlRequest;

export const zAddWebsiteUrlPath = z.object({
    collection_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddWebsiteUrlResponse = zDataResponseCollectionFileSummary;

/**
 * Response Get Export Capabilities Api V1 Export Capabilities Get
 *
 * Successful Response
 */
export const zGetExportCapabilitiesResponse = z.record(z.string(), z.unknown());

/**
 * Successful Response
 */
export const zListExtensionsResponse = zDataResponseListExtensionSummary;

export const zGetExtensionPath = z.object({
    ext_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetExtensionResponse = zDataResponseExtensionResponse;

export const zUpdateExtensionBody = zExtensionUpdate;

export const zUpdateExtensionPath = z.object({
    ext_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateExtensionResponse = zDataResponseExtensionResponse;

export const zUpdateCredentialBody = zCredentialUpdate;

export const zUpdateCredentialPath = z.object({
    ext_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateCredentialResponse = zDataResponseListCredentialMetadata;

export const zClearCredentialPath = z.object({
    ext_uuid: z.string(),
    credential_key: z.string()
});

/**
 * Successful Response
 */
export const zClearCredentialResponse = zDataResponseListCredentialMetadata;

export const zUpdateTeamAccessBody = zTeamAccessUpdate;

export const zUpdateTeamAccessPath = z.object({
    ext_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateTeamAccessResponse = zDataResponseListTeamAccessResponse;

export const zToggleExtensionBody = zExtensionToggle;

export const zToggleExtensionPath = z.object({
    ext_uuid: z.string()
});

/**
 * Successful Response
 */
export const zToggleExtensionResponse = zDataResponseExtensionResponse;

export const zListAllFeedbackQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(50),
    category: z.string().nullish(),
    status: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListAllFeedbackResponse = zPaginatedResponseFeedbackResponse;

export const zSubmitFeedbackBody = zBodySubmitFeedbackApiV1FeedbackPost;

/**
 * Successful Response
 */
export const zSubmitFeedbackResponse = zDataResponseFeedbackResponse;

export const zGetFeedbackPath = z.object({
    feedback_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetFeedbackResponse = zDataResponseFeedbackResponse;

export const zUpdateFeedbackStatusBody = zFeedbackStatusUpdate;

export const zUpdateFeedbackStatusPath = z.object({
    feedback_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateFeedbackStatusResponse = zDataResponseFeedbackResponse;

export const zDownloadScreenshotPath = z.object({
    feedback_uuid: z.string(),
    blob_name: z.string()
});

export const zListFlowsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    team_uuid: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListFlowsResponse = zPaginatedResponseFlowSummary;

export const zCreateFlowBody = zFlowCreate;

/**
 * Successful Response
 */
export const zCreateFlowResponse = zDataResponseFlowResponse;

export const zDeleteFlowPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteFlowResponse = z.void();

export const zGetFlowPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetFlowResponse = zDataResponseFlowResponse;

export const zUpdateFlowBody = zFlowUpdate;

export const zUpdateFlowPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateFlowResponse = zDataResponseFlowResponse;

export const zGetFlowAuthorizationPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetFlowAuthorizationResponse = zDataResponseFlowAuthorizationResponse;

/**
 * Body
 */
export const zAuthorizeFlowBody = zFlowAuthorizationRequest.nullable();

export const zAuthorizeFlowPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAuthorizeFlowResponse = zDataResponseFlowAuthorizationResponse;

export const zGetFlowMembersPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetFlowMembersResponse = zDataResponseFlowMembersResponse;

export const zAddFlowTeamMemberBody = zAddFlowTeamMemberRequest;

export const zAddFlowTeamMemberPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddFlowTeamMemberResponse = zDataResponseFlowTeamMemberResponse;

export const zRemoveFlowTeamMemberPath = z.object({
    flow_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveFlowTeamMemberResponse = z.void();

export const zUpdateFlowTeamMemberBody = zUpdateFlowMemberRoleRequest;

export const zUpdateFlowTeamMemberPath = z.object({
    flow_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateFlowTeamMemberResponse = zDataResponseFlowTeamMemberResponse;

export const zAddFlowUserMemberBody = zAddFlowUserMemberRequest;

export const zAddFlowUserMemberPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddFlowUserMemberResponse = zDataResponseFlowUserMemberResponse;

export const zRemoveFlowUserMemberPath = z.object({
    flow_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveFlowUserMemberResponse = z.void();

export const zUpdateFlowUserMemberBody = zUpdateFlowMemberRoleRequest;

export const zUpdateFlowUserMemberPath = z.object({
    flow_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateFlowUserMemberResponse = zDataResponseFlowUserMemberResponse;

export const zRemoveFlowPreapprovalPath = z.object({
    flow_uuid: z.string(),
    preapproval_id: z.string()
});

/**
 * Successful Response
 */
export const zRemoveFlowPreapprovalResponse = z.void();

export const zListFlowRunsPath = z.object({
    flow_uuid: z.string()
});

export const zListFlowRunsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    status: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListFlowRunsResponse = zPaginatedResponseFlowRunSummary;

export const zStreamFlowRunBody = zFlowRunCreate;

export const zStreamFlowRunPath = z.object({
    flow_uuid: z.string()
});

/**
 * Operation started; stream it from /operations/{operation_id}/stream.
 */
export const zStreamFlowRunResponse = zOperationStarted;

export const zGetFlowRunPath = z.object({
    flow_uuid: z.string(),
    run_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetFlowRunResponse = zDataResponseFlowRunResponse;

export const zCancelFlowRunBody = zFlowRunCancelRequest;

export const zCancelFlowRunPath = z.object({
    flow_uuid: z.string(),
    run_uuid: z.string()
});

/**
 * Successful Response
 */
export const zCancelFlowRunResponse = zDataResponseFlowRunResponse;

export const zResumeFlowRunBody = zFlowRunResumeRequest;

export const zResumeFlowRunPath = z.object({
    flow_uuid: z.string(),
    run_uuid: z.string()
});

/**
 * Successful Response
 */
export const zResumeFlowRunResponse = zDataResponseFlowRunResponse;

export const zListFlowTriggersPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListFlowTriggersResponse = zDataResponseListFlowTriggerResponse;

export const zCreateFlowTriggerBody = zFlowTriggerCreate;

export const zCreateFlowTriggerPath = z.object({
    flow_uuid: z.string()
});

export const zDeleteFlowTriggerPath = z.object({
    flow_uuid: z.string(),
    trigger_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteFlowTriggerResponse = z.void();

export const zUpdateFlowTriggerBody = zFlowTriggerUpdate;

export const zUpdateFlowTriggerPath = z.object({
    flow_uuid: z.string(),
    trigger_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateFlowTriggerResponse = zDataResponseFlowTriggerResponse;

export const zUploadFlowFilesBody = zBodyUploadFlowFilesApiV1FlowsFlowUuidUploadPost;

export const zUploadFlowFilesPath = z.object({
    flow_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUploadFlowFilesResponse = zFlowFilesUploaded;

export const zGoogleDriveDownloadDocumentPath = z.object({
    file_id: z.string()
});

/**
 * Successful Response
 */
export const zGoogleDriveDownloadDocumentResponse = zGoogleDriveDocumentContent;

export const zListFilesQuery = z.object({
    folder_id: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListFilesResponse = zGoogleDriveBrowseResponse;

export const zSearchFilesQuery = z.object({
    q: z.string().min(1)
});

/**
 * Response Search Files Api V1 Google Drive Search Get
 *
 * Successful Response
 */
export const zSearchFilesResponse = z.array(zGoogleDriveItem);

export const zListMyJobsQuery = z.object({
    limit: z.int().optional().default(50)
});

/**
 * Response List My Jobs Api V1 Harness Jobs Get
 *
 * Successful Response
 */
export const zListMyJobsResponse = z.record(z.string(), z.array(zJobStatusResponse));

/**
 * Response List Workers Api V1 Harness Workers Get
 *
 * Successful Response
 */
export const zListWorkersResponse = z.record(z.string(), z.array(zWorkerResponse));

/**
 * Successful Response
 */
export const zHealthCheckResponse = zHealthResponse;

/**
 * Response List Available Api V1 Integrations Available Get
 *
 * Successful Response
 */
export const zListAvailableResponse = z.array(zAvailableIntegration);

/**
 * Successful Response
 */
export const zListExtensionConsentResponse = zDataResponseListExtensionConsent;

export const zClearExtensionPreferencePath = z.object({
    slug: z.string()
});

export const zClearExtensionPreferenceQuery = z.object({
    permission: zAccessPermission
});

/**
 * Successful Response
 */
export const zClearExtensionPreferenceResponse = z.void();

export const zSetExtensionPreferenceBody = zConsentPreferenceUpdate;

export const zSetExtensionPreferencePath = z.object({
    slug: z.string()
});

/**
 * Successful Response
 */
export const zSetExtensionPreferenceResponse = zDataResponseExtensionConsent;

export const zAuthorizePath = z.object({
    slug: z.string()
});

export const zAuthorizeQuery = z.object({
    redirect_after: z.string().nullish()
});

/**
 * Successful Response
 */
export const zAuthorizeResponse2 = zAuthorizeResponse;

export const zDisconnectPath = z.object({
    slug: z.string()
});

/**
 * Successful Response
 */
export const zDisconnectResponse = z.void();

export const zListImportsQuery = z.object({
    space_uuid: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListImportsResponse = zDataResponseKgImportList;

export const zDeleteImportPath = z.object({
    import_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteImportResponse = z.void();

export const zGetImportPath = z.object({
    import_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetImportResponse = zDataResponseKgImportOut;

export const zAcceptAllBody = zKgImportAcceptAll;

export const zAcceptAllPath = z.object({
    import_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAcceptAllResponse = zDataResponseKgImportBulkResult;

export const zDismissAllPath = z.object({
    import_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDismissAllResponse = zDataResponseKgImportBulkResult;

/**
 * Successful Response
 */
export const zListInboxResponse = zDataResponseProposalList;

export const zAcceptProposalBody = zProposalAccept;

export const zAcceptProposalPath = z.object({
    proposal_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAcceptProposalResponse = zDataResponseProposalAccepted;

export const zRejectProposalPath = z.object({
    proposal_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRejectProposalResponse = zDataResponseProposal;

export const zListDuplicatesPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListDuplicatesResponse = zDataResponseDuplicateList;

export const zDismissDuplicateBody = zDismissDuplicate;

export const zDismissDuplicatePath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDismissDuplicateResponse = z.void();

export const zCreateImportBody = zKgImportText;

export const zCreateImportPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zCreateImportResponse = zDataResponseKgImportCreated;

export const zUploadImportBody = zBodyUploadImportApiV1KgSpacesSpaceUuidImportsUploadPost;

export const zUploadImportPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUploadImportResponse = zDataResponseKgImportCreated;

export const zListMergesPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListMergesResponse = zDataResponseMergeList;

export const zMergePagesBody = zMergeConfirm;

export const zMergePagesPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zMergePagesResponse = zDataResponseMergeRecord;

export const zPreviewMergeBody = zMergeRequest;

export const zPreviewMergePath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zPreviewMergeResponse = zDataResponseMergePreview;

export const zUndoMergePath = z.object({
    space_uuid: z.string(),
    merge_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUndoMergeResponse = zDataResponseMergeRecord;

export const zListWikiFilesPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListWikiFilesResponse = zDataResponseWikiFileList;

export const zDeleteWikiFilePath = z.object({
    space_uuid: z.string()
});

export const zDeleteWikiFileQuery = z.object({
    path: z.string().min(1).max(512),
    baseRevision: z.int().gte(1)
});

/**
 * Successful Response
 */
export const zDeleteWikiFileResponse = z.void();

export const zGetWikiFilePath = z.object({
    space_uuid: z.string()
});

export const zGetWikiFileQuery = z.object({
    path: z.string().min(1).max(512)
});

/**
 * Successful Response
 */
export const zGetWikiFileResponse = zDataResponseWikiFileResponse;

export const zSaveWikiFileBody = zWikiFileSave;

export const zSaveWikiFilePath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSaveWikiFileResponse = zDataResponseWikiFileResponse;

export const zListMicroAppsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zListMicroAppsResponse = zPaginatedResponseMicroAppSummary;

export const zGetAttachmentBindingsPath = z.object({
    attachment_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetAttachmentBindingsResponse = zDataResponseMicroAppBindingsPreview;

export const zPublishMicroAppBody = zMicroAppPublish;

/**
 * Successful Response
 */
export const zPublishMicroAppResponse = zDataResponseMicroAppResponse;

export const zDeleteMicroAppPath = z.object({
    app_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteMicroAppResponse = zSrcSchemasBaseMessageResponse;

export const zGetMicroAppPath = z.object({
    app_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetMicroAppResponse = zDataResponseMicroAppResponse;

export const zUpdateMicroAppBody = zMicroAppUpdate;

export const zUpdateMicroAppPath = z.object({
    app_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateMicroAppResponse = zDataResponseMicroAppResponse;

export const zGetMicroAppContentPath = z.object({
    app_uuid: z.string()
});

export const zGetMicroAppInfoPath = z.object({
    app_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetMicroAppInfoResponse = zDataResponseMicroAppInfo;

export const zGetMicroAppMembersPath = z.object({
    app_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetMicroAppMembersResponse = zDataResponseMicroAppMembersResponse;

export const zAddMicroAppTeamMemberBody = zAddMicroAppTeamMemberRequest;

export const zAddMicroAppTeamMemberPath = z.object({
    app_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddMicroAppTeamMemberResponse = zDataResponseMicroAppTeamMemberResponse;

export const zRemoveMicroAppTeamMemberPath = z.object({
    app_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveMicroAppTeamMemberResponse = z.void();

export const zUpdateMicroAppTeamMemberBody = zUpdateMicroAppMemberRoleRequest;

export const zUpdateMicroAppTeamMemberPath = z.object({
    app_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateMicroAppTeamMemberResponse = zDataResponseMicroAppTeamMemberResponse;

export const zAddMicroAppUserMemberBody = zAddMicroAppUserMemberRequest;

export const zAddMicroAppUserMemberPath = z.object({
    app_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddMicroAppUserMemberResponse = zDataResponseMicroAppUserMemberResponse;

export const zRemoveMicroAppUserMemberPath = z.object({
    app_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveMicroAppUserMemberResponse = z.void();

export const zUpdateMicroAppUserMemberBody = zUpdateMicroAppMemberRoleRequest;

export const zUpdateMicroAppUserMemberPath = z.object({
    app_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateMicroAppUserMemberResponse = zDataResponseMicroAppUserMemberResponse;

export const zRepublishMicroAppBody = zMicroAppRepublish;

export const zRepublishMicroAppPath = z.object({
    app_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRepublishMicroAppResponse = zDataResponseMicroAppResponse;

export const zModelsListModelsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    include_disabled: z.boolean().optional().default(false)
});

/**
 * Successful Response
 */
export const zModelsListModelsResponse = zPaginatedResponseAiModelSummary;

/**
 * Successful Response
 */
export const zListUserFacingTierDefaultsResponse = zDataResponseModelTierDefaultsResponse;

export const zListNotificationsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    unreadOnly: z.boolean().optional().default(false),
    type: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListNotificationsResponse = zPaginatedResponseNotificationResponse;

/**
 * Successful Response
 */
export const zMarkAllReadResponse = zSrcSchemasBaseMessageResponse;

/**
 * Successful Response
 */
export const zGetPreferencesResponse = zDataResponseListNotificationPreferenceResponse;

export const zUpdatePreferencesBody = zNotificationPreferenceBulkUpdate;

/**
 * Successful Response
 */
export const zUpdatePreferencesResponse = zDataResponseListNotificationPreferenceResponse;

/**
 * Successful Response
 */
export const zGetUnreadCountResponse = zDataResponseUnreadCountResponse;

export const zMarkNotificationReadPath = z.object({
    notification_uuid: z.string()
});

/**
 * Successful Response
 */
export const zMarkNotificationReadResponse = zSrcSchemasBaseMessageResponse;

/**
 * Successful Response
 */
export const zListActiveOperationsResponse = zActiveOperationsResponse;

export const zCancelOperationPath = z.object({
    op_id: z.string()
});

/**
 * Response Cancel Operation Api V1 Operations  Op Id  Cancel Post
 *
 * Successful Response
 */
export const zCancelOperationResponse = z.record(z.string(), z.unknown());

export const zSharepointDownloadDocumentQuery = z.object({
    url: z.string()
});

/**
 * Successful Response
 */
export const zSharepointDownloadDocumentResponse = zSharePointDocumentContent;

export const zListDriveItemsPath = z.object({
    drive_id: z.string()
});

export const zListDriveItemsQuery = z.object({
    folder_id: z.string().nullish(),
    path: z.string().nullish(),
    limit: z.int().gte(1).lte(200).nullish()
});

/**
 * Successful Response
 */
export const zListDriveItemsResponse = zSharePointBrowseResponse;

export const zSharepointCountFolderItemsPath = z.object({
    drive_id: z.string(),
    item_id: z.string()
});

export const zSharepointCountFolderItemsQuery = z.object({
    recursive: z.boolean().optional().default(false)
});

/**
 * Successful Response
 */
export const zSharepointCountFolderItemsResponse = zSharePointFolderCountResponse;

export const zSearchDriveItemsPath = z.object({
    drive_id: z.string()
});

export const zSearchDriveItemsQuery = z.object({
    q: z.string().min(1)
});

/**
 * Response Search Drive Items Api V1 Sharepoint Drives  Drive Id  Search Get
 *
 * Successful Response
 */
export const zSearchDriveItemsResponse = z.array(zSharePointItem);

/**
 * Successful Response
 */
export const zGetMyOnedriveResponse = zSharePointDrive;

export const zResolveSharepointUrlQuery = z.object({
    url: z.string()
});

/**
 * Successful Response
 */
export const zResolveSharepointUrlResponse = zSharePointItem;

export const zSearchSitesQuery = z.object({
    q: z.string().optional().default('*')
});

/**
 * Response Search Sites Api V1 Sharepoint Sites Get
 *
 * Successful Response
 */
export const zSearchSitesResponse = z.array(zSharePointSite);

export const zListSiteDrivesPath = z.object({
    site_id: z.string()
});

/**
 * Response List Site Drives Api V1 Sharepoint Sites  Site Id  Drives Get
 *
 * Successful Response
 */
export const zListSiteDrivesResponse = z.array(zSharePointDrive);

export const zListSkillsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    category: z.string().nullish(),
    skill_type: z.string().nullish(),
    team_uuid: z.string().nullish(),
    search: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListSkillsResponse = zPaginatedResponseSkillSummary;

export const zCreateSkillBody = zSkillCreate;

/**
 * Successful Response
 */
export const zCreateSkillResponse = zDataResponseSkillResponse;

/**
 * Successful Response
 */
export const zListSkillCategoriesResponse = zDataResponseListStr;

export const zImportSkillsBody = zBodyImportSkillsApiV1SkillsImportPost;

/**
 * Successful Response
 */
export const zImportSkillsResponse = zDataResponseSkillImportResponse;

export const zAnalyzeImportBody = zBodyAnalyzeImportApiV1SkillsImportAnalyzePost;

/**
 * Successful Response
 */
export const zAnalyzeImportResponse = zDataResponseBundleAnalysisResponse;

export const zDeleteSkillPath = z.object({
    skill_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteSkillResponse = zSrcSchemasBaseMessageResponse;

export const zGetSkillPath = z.object({
    skill_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetSkillResponse = zDataResponseSkillResponse;

export const zUpdateSkillBody = zSkillUpdate;

export const zUpdateSkillPath = z.object({
    skill_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateSkillResponse = zDataResponseSkillResponse;

export const zSkillDeleteImpactPath = z.object({
    skill_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSkillDeleteImpactResponse2 = zDataResponseSkillDeleteImpactResponse;

export const zGetSkillMembersPath = z.object({
    skill_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetSkillMembersResponse = zDataResponseSkillMembersResponse;

export const zAddSkillTeamMemberBody = zAddSkillTeamMemberRequest;

export const zAddSkillTeamMemberPath = z.object({
    skill_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddSkillTeamMemberResponse = zDataResponseSkillTeamMemberResponse;

export const zRemoveSkillTeamMemberPath = z.object({
    skill_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveSkillTeamMemberResponse = z.void();

export const zUpdateSkillTeamMemberBody = zUpdateSkillMemberRoleRequest;

export const zUpdateSkillTeamMemberPath = z.object({
    skill_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateSkillTeamMemberResponse = zDataResponseSkillTeamMemberResponse;

export const zAddSkillUserMemberBody = zAddSkillUserMemberRequest;

export const zAddSkillUserMemberPath = z.object({
    skill_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddSkillUserMemberResponse = zDataResponseSkillUserMemberResponse;

export const zRemoveSkillUserMemberPath = z.object({
    skill_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveSkillUserMemberResponse = z.void();

export const zUpdateSkillUserMemberBody = zUpdateSkillMemberRoleRequest;

export const zUpdateSkillUserMemberPath = z.object({
    skill_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateSkillUserMemberResponse = zDataResponseSkillUserMemberResponse;

export const zListSpacesQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(50),
    status: z.string().regex(/^(active|archived)$/).nullish(),
    search: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListSpacesResponse = zPaginatedResponseSpaceSummary;

export const zCreateSpaceBody = zSpaceCreate;

/**
 * Successful Response
 */
export const zCreateSpaceResponse = zDataResponseSpaceResponse;

export const zListRecentActivitiesQuery = z.object({
    limit: z.int().gte(1).lte(50).optional().default(10)
});

/**
 * Successful Response
 */
export const zListRecentActivitiesResponse = zDataResponseListSpaceActivityResponse;

/**
 * Successful Response
 */
export const zGetPersonalSpaceResponse = zDataResponseSpaceResponse;

/**
 * Successful Response
 */
export const zListPinsResponse = zDataResponseListSpaceSummary;

export const zAddPinBody = zSpacePinAdd;

/**
 * Successful Response
 */
export const zAddPinResponse = zSrcSchemasBaseMessageResponse;

export const zReorderPinsBody = zSpacePinReorder;

/**
 * Successful Response
 */
export const zReorderPinsResponse = zSrcSchemasBaseMessageResponse;

export const zRemovePinPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemovePinResponse = z.void();

/**
 * Successful Response
 */
export const zGetSpaceSidebarResponse = zDataResponseSidebarResponse;

export const zCreateSidebarSectionBody = zSidebarSectionCreate;

/**
 * Successful Response
 */
export const zCreateSidebarSectionResponse = zDataResponseSidebarResponse;

export const zReorderSidebarSectionsBody = zSidebarSectionReorder;

/**
 * Successful Response
 */
export const zReorderSidebarSectionsResponse = zDataResponseSidebarResponse;

export const zDeleteSidebarSectionPath = z.object({
    section_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteSidebarSectionResponse = zDataResponseSidebarResponse;

export const zUpdateSidebarSectionBody = zSidebarSectionUpdate;

export const zUpdateSidebarSectionPath = z.object({
    section_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateSidebarSectionResponse = zDataResponseSidebarResponse;

export const zMoveSidebarSpaceBody = zSidebarSpaceMove;

export const zMoveSidebarSpacePath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zMoveSidebarSpaceResponse = zDataResponseSidebarResponse;

export const zDeleteSpacePath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteSpaceResponse = z.void();

export const zGetSpacePath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetSpaceResponse = zDataResponseSpaceResponse;

export const zUpdateSpaceBody = zSpaceUpdate;

export const zUpdateSpacePath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateSpaceResponse = zDataResponseSpaceResponse;

export const zListSpaceActivitiesPath = z.object({
    space_uuid: z.string()
});

export const zListSpaceActivitiesQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zListSpaceActivitiesResponse = zPaginatedResponseSpaceActivityResponse;

export const zListSpaceConnectorsPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListSpaceConnectorsResponse = zDataResponseSpaceConnectorsResponse;

export const zAddSpaceConnectorBody = zSpaceConnectorCreate;

export const zAddSpaceConnectorPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddSpaceConnectorResponse = zDataResponseSpaceConnectorResponse;

export const zRemoveSpaceConnectorPath = z.object({
    space_uuid: z.string(),
    binding_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveSpaceConnectorResponse = z.void();

export const zUpdateSpaceConnectorBody = zSpaceConnectorUpdate;

export const zUpdateSpaceConnectorPath = z.object({
    space_uuid: z.string(),
    binding_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateSpaceConnectorResponse = zDataResponseSpaceConnectorResponse;

export const zSetSpaceConnectorSkillsBody = zSpaceConnectorSkillsUpdate;

export const zSetSpaceConnectorSkillsPath = z.object({
    space_uuid: z.string(),
    binding_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSetSpaceConnectorSkillsResponse = zDataResponseSpaceConnectorResponse;

export const zListSpaceDocumentsPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListSpaceDocumentsResponse = zDataResponseListSpaceDocumentResponse;

export const zShareSpaceDocumentBody = zSpaceDocumentShare;

export const zShareSpaceDocumentPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zShareSpaceDocumentResponse = zDataResponseSpaceDocumentResponse;

export const zRemoveSpaceDocumentPath = z.object({
    space_uuid: z.string(),
    document_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveSpaceDocumentResponse = z.void();

export const zUploadSpaceFilesBody = zBodyUploadSpaceFilesApiV1SpacesSpaceUuidFilesPost;

export const zUploadSpaceFilesPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUploadSpaceFilesResponse = zDataResponseListCollectionFileSummary;

export const zDeleteSpaceFilePath = z.object({
    space_uuid: z.string(),
    file_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteSpaceFileResponse = zSrcSchemasBaseMessageResponse;

export const zGetSpaceHubPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetSpaceHubResponse = zDataResponseSpaceHubResponse;

export const zListSpaceKnowledgePath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListSpaceKnowledgeResponse = zDataResponseListCollectionFileSummary;

export const zGetSpaceMembersPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetSpaceMembersResponse = zDataResponseSpaceMembersResponse;

export const zAddSpaceTeamMemberBody = zAddSpaceTeamMemberRequest;

export const zAddSpaceTeamMemberPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddSpaceTeamMemberResponse = zDataResponseSpaceTeamMemberResponse;

export const zRemoveSpaceTeamMemberPath = z.object({
    space_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveSpaceTeamMemberResponse = z.void();

export const zUpdateSpaceTeamMemberBody = zUpdateSpaceMemberRoleRequest;

export const zUpdateSpaceTeamMemberPath = z.object({
    space_uuid: z.string(),
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateSpaceTeamMemberResponse = zDataResponseSpaceTeamMemberResponse;

export const zAddSpaceUserMemberBody = zAddSpaceUserMemberRequest;

export const zAddSpaceUserMemberPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddSpaceUserMemberResponse = zDataResponseSpaceUserMemberResponse;

export const zRemoveSpaceUserMemberPath = z.object({
    space_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveSpaceUserMemberResponse = z.void();

export const zUpdateSpaceUserMemberBody = zUpdateSpaceMemberRoleRequest;

export const zUpdateSpaceUserMemberPath = z.object({
    space_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateSpaceUserMemberResponse = zDataResponseSpaceUserMemberResponse;

export const zListQuickActionsPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListQuickActionsResponse = zDataResponseListSpaceQuickActionSummary;

export const zSpacesCreateQuickActionBody = zSpaceQuickActionCreate;

export const zSpacesCreateQuickActionPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSpacesCreateQuickActionResponse = zDataResponseSpaceQuickActionSummary;

export const zSpacesDeleteQuickActionPath = z.object({
    space_uuid: z.string(),
    action_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSpacesDeleteQuickActionResponse = z.void();

export const zSpacesUpdateQuickActionBody = zSpaceQuickActionUpdate;

export const zSpacesUpdateQuickActionPath = z.object({
    space_uuid: z.string(),
    action_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSpacesUpdateQuickActionResponse = zDataResponseSpaceQuickActionSummary;

export const zListSpaceResourcesPath = z.object({
    space_uuid: z.string()
});

export const zListSpaceResourcesQuery = z.object({
    pinned: z.boolean().nullish(),
    type: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListSpaceResourcesResponse = zDataResponseListSpaceResourceResponse;

export const zAddSpaceResourceBody = zSpaceResourceAdd;

export const zAddSpaceResourcePath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddSpaceResourceResponse = zDataResponseSpaceResourceResponse;

export const zReorderSpaceResourcesBody = zSpaceResourceReorder;

export const zReorderSpaceResourcesPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zReorderSpaceResourcesResponse = zSrcSchemasBaseMessageResponse;

export const zRemoveSpaceResourcePath = z.object({
    space_uuid: z.string(),
    resource_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveSpaceResourceResponse = z.void();

export const zUpdateSpaceResourceBody = zSpaceResourceUpdate;

export const zUpdateSpaceResourcePath = z.object({
    space_uuid: z.string(),
    resource_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateSpaceResourceResponse = zDataResponseSpaceResourceResponse;

export const zRemoveSpaceAccessPath = z.object({
    space_uuid: z.string(),
    resource_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveSpaceAccessResponse = zDataResponseSpaceResourceResponse;

export const zGiveSpaceAccessPath = z.object({
    space_uuid: z.string(),
    resource_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGiveSpaceAccessResponse = zDataResponseSpaceResourceResponse;

export const zSpaceAccessImpactPath = z.object({
    space_uuid: z.string(),
    resource_uuid: z.string()
});

/**
 * Successful Response
 */
export const zSpaceAccessImpactResponse2 = zDataResponseSpaceAccessImpactResponse;

export const zListSpaceSharedChatsPath = z.object({
    space_uuid: z.string()
});

/**
 * Successful Response
 */
export const zListSpaceSharedChatsResponse = zDataResponseListSpaceChatSummary;

export const zListTeamsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zListTeamsResponse = zPaginatedResponseTeamSummary;

export const zCreateTeamBody = zTeamCreate;

/**
 * Successful Response
 */
export const zCreateTeamResponse = zDataResponseTeamResponse;

export const zDeleteTeamPath = z.object({
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zDeleteTeamResponse = zSrcSchemasBaseMessageResponse;

export const zGetTeamPath = z.object({
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zGetTeamResponse = zDataResponseTeamResponse;

export const zUpdateTeamBody = zTeamUpdate;

export const zUpdateTeamPath = z.object({
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateTeamResponse = zDataResponseTeamResponse;

export const zAddTeamMemberBody = zTeamMemberAdd;

export const zAddTeamMemberPath = z.object({
    team_uuid: z.string()
});

/**
 * Successful Response
 */
export const zAddTeamMemberResponse = zDataResponseTeamMemberResponse;

export const zRemoveTeamMemberPath = z.object({
    team_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRemoveTeamMemberResponse = zSrcSchemasBaseMessageResponse;

export const zUpdateTeamMemberBody = zTeamMemberUpdate;

export const zUpdateTeamMemberPath = z.object({
    team_uuid: z.string(),
    user_uuid: z.string()
});

/**
 * Successful Response
 */
export const zUpdateTeamMemberResponse = zDataResponseTeamMemberResponse;

export const zListToolsQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    tool_type: z.string().nullish(),
    team_uuid: z.string().nullish(),
    include_platform: z.boolean().optional().default(true)
});

/**
 * Successful Response
 */
export const zListToolsResponse = zPaginatedResponseToolSummary;

export const zGetUsageByUserQuery = z.object({
    start_date: z.iso.datetime().nullish(),
    end_date: z.iso.datetime().nullish(),
    search: z.string().nullish(),
    limit: z.int().gte(1).lte(100).optional().default(10),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetUsageByUserResponse = zDataResponseUserSpendResponse;

export const zGetMySpendQuery = z.object({
    months: z.int().gte(1).lte(12).optional().default(6)
});

/**
 * Successful Response
 */
export const zGetMySpendResponse = zDataResponseMySpendResponse;

export const zRegenerateWebhookSecretPath = z.object({
    webhook_uuid: z.string()
});

/**
 * Successful Response
 */
export const zRegenerateWebhookSecretResponse = zDataResponseDict;
